// =============================================================================
// AdminDashboard.js
// =============================================================================
// Main admin interface for the FOOM/SCALA competition management system.
// All panels are self-contained components defined at module scope so that
// state changes in one panel do not remount others.
//
// TABLE OF CONTENTS
// -----------------
//   ~10    Toggle             — reusable toggle switch
//   ~27    SettingsPanel      — competition settings (dates, email templates, etc.)
//   ~310   PlacementCategoriesSection — placement category CRUD
//   ~507   CategoriesPanel    — song category CRUD
//   ~790   CriteriaPanel      — scoring criteria CRUD
//   ~1009  InstrumentsPanel   — instrument CRUD
//   ~1191  SortTh             — sortable table-header utility
//   ~1206  JudgesPanel        — judge management and assignment
//   ~2143  OverviewPanel      — dashboard stats, StatCard, countdown helpers
//   ~2811  CompetitionPanel   — competition lifecycle (archive, new, export, reset)
//   ~2813  ConfirmModal       — generic confirmation dialog
//   ~2835  EmailsPanel        — bulk email management
//   ~3508  ManageHelpText     — tooltip/help text CRUD
//   ~3734  UsersPanel         — user list and deletion
//   ~6223  AdminEmailPanel    — admin/observer accounts, sender emails, CC list
//   ~3978  Heat sub-components — ResultDropdown, HeatSongRow, HeatPdfButtons,
//                               HeatNotifyRow, GrandFinalCard, RegularHeatCard
//   ~1224  VenuesPanel        — reusable venue list (Categories & Criteria tab)
//   ~4382  LiveHeatsPanel helpers — HeatLogisticsEdit + reorder/notify utils
//   ~4470  LiveHeatsPanel     — heat scheduling, song assignment, results
//   ~5533  Audio URL helper   — resolveAudioUrl
//   (AudioControls — play + download — is components/AudioControls.js)
//   ~5573  ResultsPanel       — judge evaluation results viewer
//   ~5739  AdminDashboard     — root component, tab routing, layout
// =============================================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { downloadCelebratedPdf } from '../../utils/celebratedPdf';
import { loadCompetition } from '../../components/CompetitionBadge';
import AdminHeader from '../../components/AdminHeader';
import AudioControls from '../../components/AudioControls';
import Tooltip from '../../components/Tooltip';
import AdminSongEntryModal from './AdminSongEntryModal';
import { downloadLyricsPdf, downloadTechPdf, downloadRunningSheetPdf, downloadVotingSlipPdf, downloadTallyFormPdf, getRunningSheetPdfBase64, getTechPdfBase64 } from '../../utils/heatPdf';
import './admin.css';

// ─── Toggle switch ────────────────────────────────────────────────────────────
function Toggle({ id, checked, onChange, disabled }) {
  return (
    <label className="a-toggle" htmlFor={id}>
      <input
        type="checkbox"
        id={id}
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
      />
      <span className="a-toggle__slider" />
    </label>
  );
}

// Converts a stored UTC ISO string to the local "YYYY-MM-DDTHH:mm" format a
// <input type="datetime-local"> expects for its value prop. Do NOT use
// .toISOString() for this — it always returns UTC, but datetime-local
// interprets its value as local time, so that mismatch silently shifts both
// the displayed date and time (Adelaide is UTC+9:30, so this isn't just an
// hour off — the date itself can roll to the previous or next day depending
// on what time was originally picked).
function toLocalDatetimeValue(isoString) {
  if (!isoString) return '';
  const d = new Date(isoString);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ─── SettingsPanel ────────────────────────────────────────────────────────────
function SettingsPanel() {
  const [settings, setSettings] = useState(null);
  const [form, setForm]         = useState({});
  const [dirty, setDirty]       = useState(false);
  const [saving, setSaving]     = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [error, setError]       = useState('');


  useEffect(() => {
    api.get('/api/admin/settings')
      .then((res) => {
        setSettings(res.data.settings);
        setForm(res.data.settings);
      })
      .catch(() => setError('Failed to load settings.'));
  }, []);

  function set(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
    setSaveStatus('');
  }

  async function handleSave() {
    setSaving(true);
    setSaveStatus('');
    setError('');
    try {
      const res = await api.put('/api/admin/settings', form);
      setSettings(res.data.settings);
      setForm(res.data.settings);
      setDirty(false);
      setSaveStatus('Saved!');
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  }

  if (!settings) {
    return error
      ? <div className="c-alert">{error}</div>
      : <div className="c-loading"><div className="c-spinner" /></div>;
  }

  return (
    <div className="a-section">
      <div className="a-section__header">
        <div>
          <div className="a-section__title">Competition Settings</div>
          <div className="a-section__desc">Core configuration for the competition.</div>
        </div>
      </div>

      {error && <div className="c-alert">{error}</div>}

      <div className="a-settings-grid">
        <div className="c-form-group">
          <label className="c-label" htmlFor="s-name">Competition Name</label>
          <input
            id="s-name"
            className="c-input"
            value={form.competition_name || ''}
            onChange={(e) => set('competition_name', e.target.value)}
          />
        </div>

        <div className="c-form-group">
          <label className="c-label" htmlFor="s-year">Competition Year</label>
          <input
            id="s-year"
            className="c-input"
            type="number"
            value={form.competition_year || ''}
            onChange={(e) => set('competition_year', Number(e.target.value))}
          />
        </div>

        <div className="c-form-group">
          <label className="c-label" htmlFor="s-maxsongs">Max Songs per Contestant<Tooltip tooltipKey="admin_max_songs" /></label>
          <input
            id="s-maxsongs"
            className="c-input"
            type="number"
            min="1"
            value={form.max_songs_per_contestant || ''}
            onChange={(e) => set('max_songs_per_contestant', Number(e.target.value))}
          />
        </div>

        <div className="c-form-group">
          <label className="c-label" htmlFor="s-maxperformers">Max Performers per Song</label>
          <input
            id="s-maxperformers"
            className="c-input"
            type="number"
            min="1"
            value={form.max_performers ?? ''}
            onChange={(e) => set('max_performers', Number(e.target.value))}
          />
        </div>

        <div className="c-form-group">
          <label className="c-label" htmlFor="s-maxscore">Max Score per Criterion</label>
          <input
            id="s-maxscore"
            className="c-input"
            type="number"
            min="1"
            max="10"
            value={form.max_score_per_criterion || ''}
            onChange={(e) => set('max_score_per_criterion', Number(e.target.value))}
          />
        </div>

        <div className="c-form-group">
          <label className="c-label" htmlFor="s-jps">Judges per Song<Tooltip tooltipKey="admin_judges_per_song" /></label>
          <input
            id="s-jps"
            className="c-input"
            type="number"
            min="1"
            value={form.judges_per_song || ''}
            onChange={(e) => set('judges_per_song', Number(e.target.value))}
          />
        </div>

        <div className="c-form-group">
          <label className="c-label" htmlFor="s-memberfee">
            Member Entry Fee (cents)
            <span className="c-label-optional"> — e.g. 1500 = R15.00</span>
            <Tooltip tooltipKey="admin_member_fee" />
          </label>
          <input
            id="s-memberfee"
            className="c-input"
            type="number"
            min="0"
            value={form.member_fee_cents ?? ''}
            onChange={(e) => set('member_fee_cents', Number(e.target.value))}
          />
        </div>

        <div className="c-form-group">
          <label className="c-label" htmlFor="s-nonmemberfee">
            Non-member Entry Fee (cents)
            <span className="c-label-optional"> — e.g. 2500 = R25.00</span>
            <Tooltip tooltipKey="admin_nonmember_fee" />
          </label>
          <input
            id="s-nonmemberfee"
            className="c-input"
            type="number"
            min="0"
            value={form.non_member_fee_cents ?? ''}
            onChange={(e) => set('non_member_fee_cents', Number(e.target.value))}
          />
        </div>
      </div>

      <div className="a-toggle-row" style={{ marginBottom: '0.75rem' }}>
        <div>
          <div className="a-toggle-row__label">Registration Open</div>
          <div className="a-toggle-row__desc">Allow contestants to register and submit songs.</div>
        </div>
        <Toggle
          id="s-reg"
          checked={form.registration_open}
          onChange={(v) => set('registration_open', v)}
        />
      </div>

      <div className="a-toggle-row" style={{ marginBottom: '1.25rem' }}>
        <div>
          <div className="a-toggle-row__label">Scoring Open</div>
          <div className="a-toggle-row__desc">Allow judges to submit evaluations.</div>
        </div>
        <Toggle
          id="s-scoring"
          checked={form.scoring_open}
          onChange={(v) => set('scoring_open', v)}
        />
      </div>

      <div className="c-form-group">
        <label className="c-label" htmlFor="s-instructions">Judge Comments Instructions<Tooltip tooltipKey="admin_judge_comments_instructions" /></label>
        <textarea
          id="s-instructions"
          className="c-textarea"
          rows={4}
          value={form.judge_comments_instructions || ''}
          onChange={(e) => set('judge_comments_instructions', e.target.value)}
        />
      </div>

      <div className="a-toggle-row" style={{ marginBottom: '0.75rem' }}>
        <div>
          <div className="a-toggle-row__label">Show SCALA Membership Section</div>
          <div className="a-toggle-row__desc">
            Display the Member / Non-member question on the contestant registration page.
            Disable for competitions where membership is not relevant.
          </div>
        </div>
        <Toggle
          id="s-show-membership"
          checked={form.show_membership_section ?? true}
          onChange={(v) => set('show_membership_section', v)}
        />
      </div>

      <div className="c-form-group">
        <label className="c-label" htmlFor="s-membership-text">
          Membership Info Text
          <span className="c-label-optional"> — shown above the Member/Non-member radio buttons</span>
        </label>
        <textarea
          id="s-membership-text"
          className="c-textarea"
          rows={3}
          value={form.membership_info_text ?? ''}
          onChange={(e) => set('membership_info_text', e.target.value)}
          disabled={form.show_membership_section === false}
        />
      </div>

      <div className="a-form-grid" style={{ marginBottom: '1.25rem' }}>
        <div className="c-form-group">
          <label className="c-label" htmlFor="s-sub-deadline">
            Submission Deadline
            <span className="c-label-optional"> — optional</span>
            <Tooltip tooltipKey="admin_submission_deadline" />
          </label>
          <input
            id="s-sub-deadline"
            className="c-input"
            type="datetime-local"
            value={toLocalDatetimeValue(form.submission_deadline)}
            onChange={(e) =>
              set('submission_deadline', e.target.value ? new Date(e.target.value).toISOString() : null)
            }
          />
        </div>

        <div className="c-form-group">
          <label className="c-label" htmlFor="s-judge-deadline">
            Judging Deadline
            <span className="c-label-optional"> — optional</span>
            <Tooltip tooltipKey="admin_judging_deadline" />
          </label>
          <input
            id="s-judge-deadline"
            className="c-input"
            type="datetime-local"
            value={toLocalDatetimeValue(form.judging_deadline)}
            onChange={(e) =>
              set('judging_deadline', e.target.value ? new Date(e.target.value).toISOString() : null)
            }
          />
        </div>
      </div>

      <div className="a-form-actions">
        <button
          className="c-btn"
          onClick={handleSave}
          disabled={!dirty || saving}
        >
          {saving ? 'Saving…' : 'Save Settings'}
        </button>
        {saveStatus && <span className="a-save-status">{saveStatus}</span>}
      </div>

    </div>
  );
}

// ─── PlacementCategoriesSection ───────────────────────────────────────────────
function PlacementCategoriesSection() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [mode, setMode]             = useState(null); // null | 'adding' | { type:'editing', item }
  const [form, setForm]             = useState({});
  const [formError, setFormError]   = useState('');
  const [saving, setSaving]         = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/api/admin/placement-categories')
      .then((res) => setCategories(res.data.categories))
      .catch(() => setError('Failed to load placement categories.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  function startAdd() {
    setMode('adding');
    setForm({ name: '', description: '', display_order: 0, is_active: true });
    setFormError('');
  }

  function startEdit(cat) {
    setMode({ type: 'editing', item: cat });
    setForm({ name: cat.name, description: cat.description || '', display_order: cat.display_order, is_active: cat.is_active });
    setFormError('');
  }

  function cancelForm() { setMode(null); setForm({}); setFormError(''); }

  async function handleSubmit() {
    if (!form.name?.trim()) { setFormError('Name is required.'); return; }
    setSaving(true); setFormError('');
    try {
      if (mode === 'adding') {
        await api.post('/api/admin/placement-categories', form);
      } else {
        await api.put(`/api/admin/placement-categories/${mode.item.id}`, form);
      }
      cancelForm();
      load();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(cat) {
    try {
      const res = await api.put(`/api/admin/placement-categories/${cat.id}`, { is_active: !cat.is_active });
      setCategories((prev) => prev.map((c) => c.id === cat.id ? res.data.category : c));
    } catch {
      setError('Failed to update placement category.');
    }
  }

  async function handleDelete(cat) {
    if (cat.usage_count > 0) {
      alert(`Cannot delete: ${cat.usage_count} evaluation(s) use this category. Deactivate it instead.`);
      return;
    }
    if (!window.confirm(`Delete placement category "${cat.name}"?`)) return;
    try {
      await api.delete(`/api/admin/placement-categories/${cat.id}`);
      setCategories((prev) => prev.filter((c) => c.id !== cat.id));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete.');
    }
  }

  return (
    <div className="a-section" style={{ marginTop: '1.5rem' }}>
      <div className="a-section__header">
        <div>
          <div className="a-section__title">Placement Categories</div>
          <div className="a-section__desc">Designations awarded to songs by judges (e.g. Winner, Runner-up, Highly Commended).</div>
        </div>
        {mode === null && (
          <button className="c-btn" onClick={startAdd}>+ Add Placement Category</button>
        )}
      </div>

      {error && <div className="c-alert">{error}</div>}

      {mode !== null && (
        <div className="c-form-card">
          <div className="c-form-card__title">
            {mode === 'adding' ? 'New Placement Category' : `Edit — ${mode.item.name}`}
          </div>
          {formError && <div className="c-alert">{formError}</div>}
          <div className="c-form-grid">
            <div className="c-form-group">
              <label className="c-label">Name *</label>
              <input
                className="c-input"
                value={form.name || ''}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              />
            </div>
            <div className="c-form-group">
              <label className="c-label">Display Order</label>
              <input
                className="c-input"
                type="number"
                value={form.display_order ?? 0}
                onChange={(e) => setForm((p) => ({ ...p, display_order: Number(e.target.value) }))}
              />
            </div>
            <div className="c-form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="c-label">Description</label>
              <input
                className="c-input"
                value={form.description || ''}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              />
            </div>
            <div className="a-toggle-row" style={{ gridColumn: '1 / -1' }}>
              <span className="c-label" style={{ margin: 0 }}>Active</span>
              <Toggle
                id="pc-active"
                checked={form.is_active}
                onChange={(v) => setForm((p) => ({ ...p, is_active: v }))}
              />
            </div>
          </div>
          <div className="a-form-actions">
            <button className="c-btn" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button className="c-btn c-btn--outline" onClick={cancelForm}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="c-loading"><div className="c-spinner" /></div>
      ) : categories.length === 0 ? (
        <div className="c-empty">
          <div className="c-empty__icon">🏆</div>
          <h3 className="c-empty__title">No placement categories yet</h3>
          <p className="c-empty__text">Add categories like Winner, Runner-up, Highly Commended.</p>
        </div>
      ) : (
        <div className="a-table-wrap">
          <table className="a-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Order</th>
                <th>Uses</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((cat) => (
                <tr key={cat.id}>
                  <td><strong>{cat.name}</strong></td>
                  <td>{cat.description || '—'}</td>
                  <td>{cat.display_order}</td>
                  <td>{cat.usage_count}</td>
                  <td>
                    <span className={`badge ${cat.is_active ? 'badge--active' : 'badge--inactive'}`}>
                      {cat.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="a-row-actions">
                      <button className="c-btn c-btn--sm c-btn--outline" onClick={() => startEdit(cat)}>Edit</button>
                      <button className="c-btn c-btn--sm c-btn--outline" onClick={() => toggleActive(cat)}>
                        {cat.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        className="c-btn c-btn--sm c-btn--danger"
                        onClick={() => handleDelete(cat)}
                        title={cat.usage_count > 0 ? `${cat.usage_count} evaluation(s) use this — deactivate instead` : ''}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── CategoriesPanel ──────────────────────────────────────────────────────────
function CategoriesPanel() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  // mode: null | 'adding' | { type:'editing', item }
  const [mode, setMode]             = useState(null);
  const [form, setForm]             = useState({});
  const [formError, setFormError]   = useState('');
  const [saving, setSaving]         = useState(false);
  const [catOpen, setCatOpen]       = usePersistentCollapse('catcriteria:categories');

  const load = useCallback(() => {
    setLoading(true);
    api.get('/api/admin/categories')
      .then((res) => setCategories(res.data.categories))
      .catch(() => setError('Failed to load categories.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  function startAdd() {
    setMode('adding');
    setForm({
      name: '', description: '', display_order: 0, is_active: true, is_live: false,
      requires_music_file: false, requires_lyrics_text: false,
      lyrics_instructions: '', lyrics_placeholder: '',
      max_entries_per_contestant: '',
    });
    setFormError('');
  }

  function startEdit(cat) {
    setMode({ type: 'editing', item: cat });
    setForm({
      name: cat.name,
      description: cat.description || '',
      display_order: cat.display_order,
      is_active: cat.is_active,
      is_live: cat.is_live ?? false,
      requires_music_file:  cat.requires_music_file  ?? false,
      requires_lyrics_text: cat.requires_lyrics_text ?? false,
      lyrics_instructions:  cat.lyrics_instructions  || '',
      lyrics_placeholder:   cat.lyrics_placeholder   || '',
      // '' means no limit; the API turns it back into NULL.
      max_entries_per_contestant: cat.max_entries_per_contestant ?? '',
    });
    setFormError('');
  }

  function cancelForm() { setMode(null); setForm({}); setFormError(''); }

  async function handleSubmit() {
    if (!form.name?.trim()) { setFormError('Name is required.'); return; }
    setSaving(true);
    setFormError('');
    try {
      if (mode === 'adding') {
        await api.post('/api/admin/categories', form);
      } else {
        await api.put(`/api/admin/categories/${mode.item.id}`, form);
      }
      cancelForm();
      load();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(cat) {
    try {
      const res = await api.put(`/api/admin/categories/${cat.id}`, { is_active: !cat.is_active });
      setCategories((prev) => prev.map((c) => c.id === cat.id ? res.data.category : c));
    } catch {
      setError('Failed to update category.');
    }
  }

  async function handleDelete(cat) {
    if (cat.song_count > 0) {
      alert(`Cannot delete: ${cat.song_count} song(s) use this category. Deactivate it instead.`);
      return;
    }
    if (!window.confirm(`Delete category "${cat.name}"?`)) return;
    try {
      await api.delete(`/api/admin/categories/${cat.id}`);
      setCategories((prev) => prev.filter((c) => c.id !== cat.id));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete.');
    }
  }

  return (
    <div className="a-section">
      <div className="a-section__header" style={{ cursor: 'pointer' }} onClick={() => setCatOpen((v) => !v)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <span className="a-chevron" style={{ transform: catOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
          <div>
            <div className="a-section__title">Song Categories</div>
            <div className="a-section__desc">Manage competition entry categories.</div>
          </div>
        </div>
        {mode === null && (
          <button className="c-btn" onClick={(e) => { e.stopPropagation(); setCatOpen(true); startAdd(); }}>+ Add Category</button>
        )}
      </div>

      {catOpen && (<>
      {error && <div className="c-alert">{error}</div>}

      {mode !== null && (
        <div className="c-form-card">
          <div className="c-form-card__title">
            {mode === 'adding' ? 'New Category' : `Edit — ${mode.item.name}`}
          </div>
          {formError && <div className="c-alert">{formError}</div>}

          <div className="c-form-grid">
            <div className="c-form-group">
              <label className="c-label">Name *</label>
              <input
                className="c-input"
                value={form.name || ''}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              />
            </div>
            <div className="c-form-group">
              <label className="c-label">Display Order</label>
              <input
                className="c-input"
                type="number"
                value={form.display_order ?? 0}
                onChange={(e) => setForm((p) => ({ ...p, display_order: Number(e.target.value) }))}
              />
            </div>
            <div className="c-form-group">
              <label className="c-label">Maximum Entries</label>
              <input
                className="c-input"
                type="number"
                min="1"
                placeholder="No limit"
                value={form.max_entries_per_contestant ?? ''}
                onChange={(e) => setForm((p) => ({ ...p, max_entries_per_contestant: e.target.value }))}
              />
              <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '0.25rem' }}>
                Most songs one contestant may enter in this category. Leave blank for no limit.
                Entries already above the limit are kept — contestants simply can't add more.
              </div>
            </div>
            <div className="c-form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="c-label">Description</label>
              <input
                className="c-input"
                value={form.description || ''}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              />
            </div>
            <div className="a-toggle-row" style={{ gridColumn: '1 / -1' }}>
              <span className="c-label" style={{ margin: 0 }}>Active</span>
              <Toggle
                id="cat-active"
                checked={form.is_active}
                onChange={(v) => setForm((p) => ({ ...p, is_active: v }))}
              />
            </div>
            <div className="a-toggle-row" style={{ gridColumn: '1 / -1' }}>
              <div>
                <span className="c-label" style={{ margin: 0 }}>Is Live Category</span>
                <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '0.15rem' }}>
                  Show the Performance Requirements (tech rider) section on the submission form.
                </div>
              </div>
              <Toggle
                id="cat-live"
                checked={form.is_live}
                onChange={(v) => setForm((p) => ({ ...p, is_live: v }))}
              />
            </div>
            <div className="a-toggle-row" style={{ gridColumn: '1 / -1' }}>
              <div>
                <span className="c-label" style={{ margin: 0 }}>Requires Music File Upload<Tooltip tooltipKey="admin_category_requires_music" /></span>
                <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '0.15rem' }}>
                  Show an MP3/WAV upload field on the submission form.
                </div>
              </div>
              <Toggle
                id="cat-music"
                checked={form.requires_music_file}
                onChange={(v) => setForm((p) => ({ ...p, requires_music_file: v }))}
              />
            </div>
            <div className="a-toggle-row" style={{ gridColumn: '1 / -1' }}>
              <div>
                <span className="c-label" style={{ margin: 0 }}>Requires Lyrics (text entry)<Tooltip tooltipKey="admin_category_requires_lyrics" /></span>
                <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '0.15rem' }}>
                  Show a lyrics text box on the submission form.
                </div>
              </div>
              <Toggle
                id="cat-lyrics"
                checked={form.requires_lyrics_text}
                onChange={(v) => setForm((p) => ({ ...p, requires_lyrics_text: v }))}
              />
            </div>
            {form.requires_lyrics_text && (
              <>
                <div className="c-form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="c-label">Lyrics Instructions</label>
                  <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '0.35rem' }}>
                    Shown above the lyrics text box to the contestant.
                  </div>
                  <textarea
                    className="c-textarea"
                    rows={3}
                    value={form.lyrics_instructions || ''}
                    onChange={(e) => setForm((p) => ({ ...p, lyrics_instructions: e.target.value }))}
                    placeholder="Enter your song lyrics below. Please do not include any chord symbols or other annotation - lyrics only."
                  />
                </div>
                <div className="c-form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="c-label">Lyrics Placeholder Text</label>
                  <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '0.35rem' }}>
                    Placeholder text shown inside the empty lyrics text box.
                  </div>
                  <input
                    className="c-input"
                    value={form.lyrics_placeholder || ''}
                    onChange={(e) => setForm((p) => ({ ...p, lyrics_placeholder: e.target.value }))}
                    placeholder="Your lyrics here. Remember, words only."
                  />
                </div>
              </>
            )}
          </div>

          <div className="a-form-actions">
            <button className="c-btn" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button className="c-btn c-btn--outline" onClick={cancelForm}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="c-loading"><div className="c-spinner" /></div>
      ) : categories.length === 0 ? (
        <div className="c-empty">
          <div className="c-empty__icon">📂</div>
          <h3 className="c-empty__title">No categories yet</h3>
          <p className="c-empty__text">Add your first category to get started.</p>
        </div>
      ) : (
        <div className="a-table-wrap">
          <table className="a-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Order</th>
                <th>Songs</th>
                <th>Maximum Entries</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((cat) => (
                <tr key={cat.id}>
                  <td><strong>{cat.name}</strong></td>
                  <td>{cat.description || '—'}</td>
                  <td>{cat.display_order}</td>
                  <td>{cat.song_count}</td>
                  <td>
                    {cat.max_entries_per_contestant ?? (
                      <span style={{ color: '#9ca3af' }}>No limit</span>
                    )}
                  </td>
                  <td>
                    <span className={`badge ${cat.is_active ? 'badge--active' : 'badge--inactive'}`}>
                      {cat.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="a-row-actions">
                      <button className="c-btn c-btn--sm c-btn--outline" onClick={() => startEdit(cat)}>
                        Edit
                      </button>
                      <button className="c-btn c-btn--sm c-btn--outline" onClick={() => toggleActive(cat)}>
                        {cat.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button className="c-btn c-btn--sm c-btn--danger" onClick={() => handleDelete(cat)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <PlacementCategoriesSection />
      </>)}
    </div>
  );
}

// ─── CriteriaPanel ────────────────────────────────────────────────────────────
function CriteriaPanel() {
  const [criteria, setCriteria] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [mode, setMode]         = useState(null);
  const [form, setForm]         = useState({});
  const [formError, setFormError] = useState('');
  const [saving, setSaving]     = useState(false);
  const [criteriaOpen, setCriteriaOpen] = usePersistentCollapse('catcriteria:criteria');

  const load = useCallback(() => {
    setLoading(true);
    api.get('/api/admin/criteria')
      .then((res) => setCriteria(res.data.criteria))
      .catch(() => setError('Failed to load criteria.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  function startAdd() {
    setMode('adding');
    setForm({ name: '', description: '', weight: 1, display_order: 0, is_active: true });
    setFormError('');
  }

  function startEdit(c) {
    setMode({ type: 'editing', item: c });
    setForm({
      name: c.name,
      description: c.description || '',
      weight: c.weight,
      display_order: c.display_order,
      is_active: c.is_active,
    });
    setFormError('');
  }

  function cancelForm() { setMode(null); setForm({}); setFormError(''); }

  async function handleSubmit() {
    if (!form.name?.trim()) { setFormError('Name is required.'); return; }
    const w = Number(form.weight);
    if (isNaN(w) || w <= 0) { setFormError('Weight must be a positive number.'); return; }
    setSaving(true);
    setFormError('');
    try {
      if (mode === 'adding') {
        await api.post('/api/admin/criteria', { ...form, weight: w });
      } else {
        await api.put(`/api/admin/criteria/${mode.item.id}`, { ...form, weight: w });
      }
      cancelForm();
      load();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(c) {
    try {
      const res = await api.put(`/api/admin/criteria/${c.id}`, { is_active: !c.is_active });
      setCriteria((prev) => prev.map((x) => x.id === c.id ? res.data.criterion : x));
    } catch {
      setError('Failed to update criterion.');
    }
  }

  async function handleDelete(c) {
    if (c.score_count > 0) {
      alert(`Cannot delete: ${c.score_count} score(s) use this criterion. Deactivate it instead.`);
      return;
    }
    if (!window.confirm(`Delete criterion "${c.name}"?`)) return;
    try {
      await api.delete(`/api/admin/criteria/${c.id}`);
      setCriteria((prev) => prev.filter((x) => x.id !== c.id));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete.');
    }
  }

  return (
    <div className="a-section">
      <div className="a-section__header" style={{ cursor: 'pointer' }} onClick={() => setCriteriaOpen((v) => !v)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <span className="a-chevron" style={{ transform: criteriaOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
          <div>
            <div className="a-section__title">Scoring Criteria</div>
            <div className="a-section__desc">Define what judges score and the weight of each criterion.</div>
          </div>
        </div>
        {mode === null && (
          <button className="c-btn" onClick={(e) => { e.stopPropagation(); setCriteriaOpen(true); startAdd(); }}>+ Add Criterion</button>
        )}
      </div>

      {criteriaOpen && (<>
      {error && <div className="c-alert">{error}</div>}

      {mode !== null && (
        <div className="c-form-card">
          <div className="c-form-card__title">
            {mode === 'adding' ? 'New Criterion' : `Edit — ${mode.item.name}`}
          </div>
          {formError && <div className="c-alert">{formError}</div>}

          <div className="c-form-grid">
            <div className="c-form-group">
              <label className="c-label">Name *</label>
              <input
                className="c-input"
                value={form.name || ''}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              />
            </div>
            <div className="c-form-group">
              <label className="c-label">Weight<Tooltip tooltipKey="admin_criteria_weight" /></label>
              <input
                className="c-input"
                type="number"
                min="0.1"
                step="0.1"
                value={form.weight ?? 1}
                onChange={(e) => setForm((p) => ({ ...p, weight: e.target.value }))}
              />
            </div>
            <div className="c-form-group">
              <label className="c-label">Display Order</label>
              <input
                className="c-input"
                type="number"
                value={form.display_order ?? 0}
                onChange={(e) => setForm((p) => ({ ...p, display_order: Number(e.target.value) }))}
              />
            </div>
            <div className="c-form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="c-label">Description</label>
              <input
                className="c-input"
                value={form.description || ''}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              />
            </div>
            <div className="a-toggle-row" style={{ gridColumn: '1 / -1' }}>
              <span className="c-label" style={{ margin: 0 }}>Active</span>
              <Toggle
                id="crit-active"
                checked={form.is_active}
                onChange={(v) => setForm((p) => ({ ...p, is_active: v }))}
              />
            </div>
          </div>

          <div className="a-form-actions">
            <button className="c-btn" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button className="c-btn c-btn--outline" onClick={cancelForm}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="c-loading"><div className="c-spinner" /></div>
      ) : criteria.length === 0 ? (
        <div className="c-empty">
          <div className="c-empty__icon">📊</div>
          <h3 className="c-empty__title">No criteria yet</h3>
          <p className="c-empty__text">Add your first scoring criterion to get started.</p>
        </div>
      ) : (
        <div className="a-table-wrap">
          <table className="a-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Weight</th>
                <th>Order</th>
                <th>Used in</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {criteria.map((c) => (
                <tr key={c.id}>
                  <td><strong>{c.name}</strong></td>
                  <td>{c.description || '—'}</td>
                  <td>{c.weight}</td>
                  <td>{c.display_order}</td>
                  <td>{c.score_count} score{c.score_count !== 1 ? 's' : ''}</td>
                  <td>
                    <span className={`badge ${c.is_active ? 'badge--active' : 'badge--inactive'}`}>
                      {c.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="a-row-actions">
                      <button className="c-btn c-btn--sm c-btn--outline" onClick={() => startEdit(c)}>
                        Edit
                      </button>
                      <button className="c-btn c-btn--sm c-btn--outline" onClick={() => toggleActive(c)}>
                        {c.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button className="c-btn c-btn--sm c-btn--danger" onClick={() => handleDelete(c)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </>)}
    </div>
  );
}

// ─── InstrumentsPanel ────────────────────────────────────────────────────────
function InstrumentsPanel() {
  const [instruments, setInstruments] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [mode, setMode]               = useState(null);
  const [form, setForm]               = useState({});
  const [formError, setFormError]     = useState('');
  const [saving, setSaving]           = useState(false);
  const [instrumentsOpen, setInstrumentsOpen] = usePersistentCollapse('catcriteria:instruments');

  const load = useCallback(() => {
    setLoading(true);
    api.get('/api/admin/instruments/all')
      .then((res) => setInstruments(res.data.instruments))
      .catch(() => setError('Failed to load instruments.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  function startAdd() {
    setMode('adding');
    setForm({ name: '', sort_order: 0, is_active: true });
    setFormError('');
  }

  function startEdit(inst) {
    setMode({ type: 'editing', item: inst });
    setForm({ name: inst.name, sort_order: inst.sort_order, is_active: inst.is_active });
    setFormError('');
  }

  function cancelForm() { setMode(null); setForm({}); setFormError(''); }

  async function handleSubmit() {
    if (!form.name?.trim()) { setFormError('Name is required.'); return; }
    setSaving(true);
    setFormError('');
    try {
      if (mode === 'adding') {
        await api.post('/api/admin/instruments', form);
      } else {
        await api.put(`/api/admin/instruments/${mode.item.id}`, form);
      }
      cancelForm();
      load();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(inst) {
    try {
      const res = await api.put(`/api/admin/instruments/${inst.id}`, { is_active: !inst.is_active });
      setInstruments((prev) => prev.map((x) => x.id === inst.id ? res.data.instrument : x));
    } catch {
      setError('Failed to update instrument.');
    }
  }

  async function handleDelete(inst) {
    if (!window.confirm(`Delete instrument "${inst.name}"?`)) return;
    try {
      await api.delete(`/api/admin/instruments/${inst.id}`);
      setInstruments((prev) => prev.filter((x) => x.id !== inst.id));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete.');
    }
  }

  return (
    <div className="a-section">
      <div className="a-section__header" style={{ cursor: 'pointer' }} onClick={() => setInstrumentsOpen((v) => !v)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <span className="a-chevron" style={{ transform: instrumentsOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
          <div>
            <div className="a-section__title">Instruments</div>
            <div className="a-section__desc">Manage the instrument list shown on the FOOM Live submission form.</div>
          </div>
        </div>
        {mode === null && (
          <button className="c-btn" onClick={(e) => { e.stopPropagation(); setInstrumentsOpen(true); startAdd(); }}>+ Add Instrument</button>
        )}
      </div>

      {instrumentsOpen && (<>
      {error && <div className="c-alert">{error}</div>}

      {mode !== null && (
        <div className="c-form-card">
          <div className="c-form-card__title">
            {mode === 'adding' ? 'New Instrument' : `Edit — ${mode.item.name}`}
          </div>
          {formError && <div className="c-alert">{formError}</div>}

          <div className="c-form-grid">
            <div className="c-form-group">
              <label className="c-label">Name *</label>
              <input
                className="c-input"
                value={form.name || ''}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              />
            </div>
            <div className="c-form-group">
              <label className="c-label">Sort Order</label>
              <input
                className="c-input"
                type="number"
                value={form.sort_order ?? 0}
                onChange={(e) => setForm((p) => ({ ...p, sort_order: Number(e.target.value) }))}
              />
            </div>
            <div className="a-toggle-row" style={{ gridColumn: '1 / -1' }}>
              <span className="c-label" style={{ margin: 0 }}>Active</span>
              <Toggle
                id="inst-active"
                checked={form.is_active}
                onChange={(v) => setForm((p) => ({ ...p, is_active: v }))}
              />
            </div>
          </div>

          <div className="a-form-actions">
            <button className="c-btn" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button className="c-btn c-btn--outline" onClick={cancelForm}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="c-loading"><div className="c-spinner" /></div>
      ) : instruments.length === 0 ? (
        <div className="c-empty">
          <div className="c-empty__icon">🎸</div>
          <h3 className="c-empty__title">No instruments yet</h3>
          <p className="c-empty__text">Add instruments to populate the FOOM Live submission form.</p>
        </div>
      ) : (
        <div className="a-table-wrap">
          <table className="a-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Sort Order</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {instruments.map((inst) => (
                <tr key={inst.id}>
                  <td><strong>{inst.name}</strong></td>
                  <td>{inst.sort_order}</td>
                  <td>
                    <span className={`badge ${inst.is_active ? 'badge--active' : 'badge--inactive'}`}>
                      {inst.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="a-row-actions">
                      <button className="c-btn c-btn--sm c-btn--outline" onClick={() => startEdit(inst)}>
                        Edit
                      </button>
                      <button className="c-btn c-btn--sm c-btn--outline" onClick={() => toggleActive(inst)}>
                        {inst.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button className="c-btn c-btn--sm c-btn--danger" onClick={() => handleDelete(inst)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </>)}
    </div>
  );
}

// ─── VenuesPanel ─────────────────────────────────────────────────────────────
function VenuesPanel() {
  const [venues, setVenues]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [mode, setMode]           = useState(null); // null | 'adding' | { type: 'editing', item }
  const [form, setForm]           = useState({});
  const [formError, setFormError] = useState('');
  const [saving, setSaving]       = useState(false);
  const [venuesOpen, setVenuesOpen] = usePersistentCollapse('catcriteria:venues');

  const load = useCallback(() => {
    setLoading(true);
    api.get('/api/admin/venues')
      .then((res) => setVenues(res.data.venues))
      .catch(() => setError('Failed to load venues.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  function startAdd() {
    setMode('adding');
    setForm({ name: '', address: '' });
    setFormError('');
  }

  function startEdit(venue) {
    setMode({ type: 'editing', item: venue });
    setForm({ name: venue.name, address: venue.address || '' });
    setFormError('');
  }

  function cancelForm() { setMode(null); setForm({}); setFormError(''); }

  async function handleSubmit() {
    if (!form.name?.trim()) { setFormError('Name is required.'); return; }
    setSaving(true);
    setFormError('');
    try {
      if (mode === 'adding') {
        await api.post('/api/admin/venues', form);
      } else {
        await api.put(`/api/admin/venues/${mode.item.id}`, form);
      }
      cancelForm();
      load();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(venue) {
    if (!window.confirm(`Delete venue "${venue.name}"?`)) return;
    try {
      await api.delete(`/api/admin/venues/${venue.id}`);
      setVenues((prev) => prev.filter((v) => v.id !== venue.id));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete.');
    }
  }

  return (
    <div className="a-section">
      <div className="a-section__header" style={{ cursor: 'pointer' }} onClick={() => setVenuesOpen((v) => !v)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <span className="a-chevron" style={{ transform: venuesOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
          <div>
            <div className="a-section__title">Venues</div>
            <div className="a-section__desc">Manage venues available for selection when scheduling Live Heats.</div>
          </div>
        </div>
        {mode === null && (
          <button className="c-btn" onClick={(e) => { e.stopPropagation(); setVenuesOpen(true); startAdd(); }}>+ Add Venue</button>
        )}
      </div>

      {venuesOpen && (<>
      {error && <div className="c-alert">{error}</div>}

      {mode !== null && (
        <div className="c-form-card">
          <div className="c-form-card__title">
            {mode === 'adding' ? 'New Venue' : `Edit — ${mode.item.name}`}
          </div>
          {formError && <div className="c-alert">{formError}</div>}

          <div className="c-form-grid">
            <div className="c-form-group">
              <label className="c-label">Name *</label>
              <input
                className="c-input"
                value={form.name || ''}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g. The Jazz Café"
              />
            </div>
            <div className="c-form-group">
              <label className="c-label">Address</label>
              <input
                className="c-input"
                value={form.address || ''}
                onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
                placeholder="Street, City"
              />
            </div>
          </div>

          <div className="a-form-actions">
            <button className="c-btn" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button className="c-btn c-btn--outline" onClick={cancelForm}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="c-loading"><div className="c-spinner" /></div>
      ) : venues.length === 0 ? (
        <div className="c-empty">
          <div className="c-empty__icon">📍</div>
          <h3 className="c-empty__title">No venues yet</h3>
          <p className="c-empty__text">Add venues to select from when scheduling Live Heats.</p>
        </div>
      ) : (
        <div className="a-table-wrap">
          <table className="a-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Address</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {venues.map((venue) => (
                <tr key={venue.id}>
                  <td><strong>{venue.name}</strong></td>
                  <td>{venue.address || '—'}</td>
                  <td>
                    <div className="a-row-actions">
                      <button className="c-btn c-btn--sm c-btn--outline" onClick={() => startEdit(venue)}>
                        Edit
                      </button>
                      <button className="c-btn c-btn--sm c-btn--danger" onClick={() => handleDelete(venue)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </>)}
    </div>
  );
}

// ─── SortTh ───────────────────────────────────────────────────────────────────
function SortTh({ col, sortCol, sortDir, onSort, children }) {
  const active = sortCol === col;
  return (
    <th
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
      onClick={() => onSort(col)}
    >
      {children}
      <span style={{ marginLeft: '0.3rem', opacity: active ? 1 : 0.3 }}>
        {active && sortDir === 'desc' ? '▲' : '▼'}
      </span>
    </th>
  );
}

// ─── JudgesPanel ──────────────────────────────────────────────────────────────
function JudgesPanel({ mode = 'both' }) {
  const [judges, setJudges]         = useState([]);
  const [songs, setSongs]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  // Add-judge form
  const [showAddJudge, setShowAddJudge] = useState(false);
  const [judgeForm, setJudgeForm]   = useState({ email: '', full_name: '', notes: '', judge_type: 'both' });
  const editNotesRef = useRef(null); // for auto-height on Edit form open
  const [judgeFormErr, setJudgeFormErr] = useState('');
  const [addingJudge, setAddingJudge]   = useState(false);
  const [notifyingAll,   setNotifyingAll]    = useState(false);
  const [notifyAllMsg,   setNotifyAllMsg]    = useState(''); // success/info message
  const [notifyingId,    setNotifyingId]     = useState(null);  // judgeId being individually notified
  // Confirmation dialog state: null | 'all' | { type: 'one', judgeId, notifyState: 1|2|3 }
  const [notifyConfirm,  setNotifyConfirm]  = useState(null);
  // Judge delete state
  const [deleteJudgeConfirm, setDeleteJudgeConfirm] = useState(null); // null | judge object
  const [deletingJudge,      setDeletingJudge]      = useState(false);
  const [deleteJudgeErr,     setDeleteJudgeErr]      = useState('');
  // Edit-judge form
  const [editingJudge, setEditingJudge]   = useState(null); // null | judge object
  const [editForm, setEditForm]           = useState({});
  const [editFormErr, setEditFormErr]     = useState('');
  const [savingJudge, setSavingJudge]     = useState(false);
  const [editSaveStatus, setEditSaveStatus] = useState('');
  // Judges table sort state
  const [judgesSortCol, setJudgesSortCol] = useState('full_name');
  const [judgesSortDir, setJudgesSortDir] = useState('asc');
  // Per-song assign selects & state
  const [assignSelects, setAssignSelects]   = useState({}); // { song_id: judge_id_string }
  const [assigningFor, setAssigningFor]     = useState({}); // { song_id: bool }
  const [assignErrors, setAssignErrors]     = useState({}); // { song_id: string }
  // Admin song entry modal
  const [showSongEntry, setShowSongEntry] = useState(false);
  // Bulk assignment state
  const [selectedSongs, setSelectedSongs]   = useState(new Set()); // Set<song_id>
  const [bulkJudge1,    setBulkJudge1]      = useState('');
  const [bulkJudge2,    setBulkJudge2]      = useState('');
  const [bulkAssigning, setBulkAssigning]   = useState(false);
  const [bulkResult,    setBulkResult]      = useState(null); // { type:'success'|'error', text }
  // Category tabs + sorting
  const [categoryTab,   setCategoryTab]     = useState(''); // '' = All
  const [assignSearch,  setAssignSearch]    = useState(''); // contestant name filter
  const [sortCol,       setSortCol]         = useState(null); // 'title'|'contestant'|'status'|'judges'
  const [sortDir,       setSortDir]         = useState('asc');
  // Audio playback
  const [playingId,     setPlayingId]       = useState(null);
  // Section collapse state
  const [judgesOpen,      setJudgesOpen]      = usePersistentCollapse('judges:all');
  const [judgesNonLiveOpen, setJudgesNonLiveOpen] = usePersistentCollapse('judges:nonlive');
  const [judgesLiveOpen,    setJudgesLiveOpen]    = usePersistentCollapse('judges:live');
  // Starts collapsed: judges who cannot currently judge are the ones you act on
  // least often, and leaving them expanded is what crowded the two panels that
  // actually get used.
  const [judgesIdleOpen, setJudgesIdleOpen] = usePersistentCollapse('judges:idle', false);
  const [assignmentsOpen, setAssignmentsOpen] = usePersistentCollapse('judges:assignments');
  const [draftsOpen,      setDraftsOpen]      = usePersistentCollapse('judges:drafts');

  const load = useCallback(() => {
    setLoading(true);
    const requests = (mode === 'songassignments' || mode === 'both')
      ? [api.get('/api/admin/judges'), api.get('/api/admin/songs')]
      : [api.get('/api/admin/judges')];
    Promise.all(requests)
      .then(([jRes, sRes]) => {
        setJudges(jRes.data.judges);
        if (sRes) setSongs(sRes.data.songs);
      })
      .catch(() => setError('Failed to load data.'))
      .finally(() => setLoading(false));
  }, [mode]);

  useEffect(() => { load(); }, [load]);

  function toggleAudio(songId, shouldPlay) {
    if (playingId && playingId !== songId) {
      const prev = document.getElementById(`audio-${playingId}`);
      if (prev) { prev.pause(); prev.currentTime = 0; }
    }
    const audio = document.getElementById(`audio-${songId}`);
    if (!audio) return;
    if (shouldPlay) { audio.play(); setPlayingId(songId); }
    else            { audio.pause(); audio.currentTime = 0; setPlayingId(null); }
  }

  async function handleAddJudge() {
    const { email, full_name } = judgeForm;
    if (!email || !full_name) {
      setJudgeFormErr('Email and name are required.');
      return;
    }
    setAddingJudge(true);
    setJudgeFormErr('');
    try {
      await api.post('/api/admin/judges', judgeForm);
      setShowAddJudge(false);
      setJudgeForm({ email: '', full_name: '', notes: '', judge_type: 'both' });
      load();
    } catch (err) {
      setJudgeFormErr(err.response?.data?.error || 'Failed to add judge.');
    } finally {
      setAddingJudge(false);
    }
  }

  // Judges table sort
  function handleJudgeSort(col) {
    if (judgesSortCol === col) {
      setJudgesSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setJudgesSortCol(col);
      setJudgesSortDir('asc');
    }
  }

  // Open confirmation dialogs
  function handleNotifyAll() { setNotifyAllMsg(''); setNotifyConfirm('all'); }
  // judge = full judge object — derive notifyState from 3-state workflow fields
  function handleNotifyJudge(judge) {
    // Same rule as the button itself — is_activated, not assignments_notified_at,
    // or the dialog offers to send a different email from the one the button shows.
    const notifyState = (!judge.is_activated && !judge.assignments_notified_at) ? 1
                      : !judge.assignment_email_sent ? 2 : 3;
    setNotifyConfirm({ type: 'one', judgeId: judge.id, notifyState });
  }

  // Actual send — called only after confirmation
  async function doNotifyAll() {
    setNotifyConfirm(null);
    setNotifyingAll(true);
    setNotifyAllMsg('');
    try {
      const res = await api.post('/api/admin/judges/notify-all');
      const { notified, welcomed, assigned, message } = res.data;
      if (notified > 0) {
        const parts = [];
        if (welcomed > 0) parts.push(`${welcomed} invitation${welcomed !== 1 ? 's' : ''}`);
        if (assigned > 0) parts.push(`${assigned} assignment notification${assigned !== 1 ? 's' : ''}`);
        setNotifyAllMsg(`${notified} email${notified !== 1 ? 's' : ''} sent (${parts.join(', ')}).`);
      } else {
        setNotifyAllMsg(message || 'No judges needed notification.');
      }
      load();
    } catch (err) {
      setNotifyAllMsg(err.response?.data?.error || 'Failed to send notifications.');
    } finally {
      setNotifyingAll(false);
    }
  }

  async function doNotifyJudge(judgeId) {
    setNotifyConfirm(null);
    setNotifyingId(judgeId);
    try {
      const res = await api.post(`/api/admin/judges/${judgeId}/notify`);
      const notifiedAt          = res.data.notified_at           || new Date().toISOString();
      const assignmentEmailSent = res.data.assignment_email_sent ?? false;
      // Optimistically update local judge state so the Notified column transitions immediately
      setJudges((prev) => prev.map((j) =>
        j.id === judgeId
          ? { ...j, assignments_notified_at: notifiedAt, assignment_email_sent: assignmentEmailSent }
          : j
      ));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send notification.');
    } finally {
      setNotifyingId(null);
    }
  }

  function startEditJudge(judge) {
    setEditingJudge(judge);
    setEditForm({
      full_name:  judge.full_name,
      judge_type: judge.judge_type || 'both',
      notes:      judge.notes      || '',
      is_active:  judge.is_active,
    });
    setEditFormErr('');
    setEditSaveStatus('');
    setShowAddJudge(false);
  }

  // Auto-size the notes textarea when the Edit form opens with existing data
  useEffect(() => {
    if (editingJudge && editNotesRef.current) {
      const ta = editNotesRef.current;
      ta.style.height = 'auto';
      ta.style.height = ta.scrollHeight + 'px';
    }
  }, [editingJudge]);

  function cancelEditJudge() {
    setEditingJudge(null);
    setEditForm({});
    setEditFormErr('');
    setEditSaveStatus('');
  }

  async function handleSaveJudge() {
    if (!editForm.full_name?.trim()) {
      setEditFormErr('Full name is required.');
      return;
    }
    setSavingJudge(true);
    setEditFormErr('');
    setEditSaveStatus('');
    try {
      const res = await api.put(`/api/admin/judges/${editingJudge.id}`, editForm);
      setJudges((prev) =>
        prev.map((j) => j.id === editingJudge.id ? res.data.judge : j)
      );
      setEditSaveStatus('Saved!');
      setTimeout(() => {
        cancelEditJudge();
      }, 800);
    } catch (err) {
      setEditFormErr(err.response?.data?.error || 'Failed to save judge.');
    } finally {
      setSavingJudge(false);
    }
  }

  async function handleToggleJudgeActive(judge) {
    try {
      const res = await api.put(`/api/admin/judges/${judge.id}`, { is_active: !judge.is_active });
      setJudges((prev) =>
        prev.map((j) => j.id === judge.id ? res.data.judge : j)
      );
      // If we were editing this judge, sync the form
      if (editingJudge?.id === judge.id) {
        setEditForm((prev) => ({ ...prev, is_active: res.data.judge.is_active }));
        setEditingJudge(res.data.judge);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update judge.');
    }
  }

  async function handleDeleteJudge(judge) {
    setDeletingJudge(true);
    setDeleteJudgeErr('');
    try {
      await api.delete(`/api/admin/judges/${judge.id}`);
      setJudges((prev) => prev.filter((j) => j.id !== judge.id));
      setDeleteJudgeConfirm(null);
    } catch (err) {
      setDeleteJudgeErr(err.response?.data?.error || 'Failed to delete judge.');
    } finally {
      setDeletingJudge(false);
    }
  }

  async function handleDeleteSong(songId, songTitle) {
    if (!window.confirm(`Are you sure you want to delete this song? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/admin/songs/${songId}`);
      setSongs((prev) => prev.filter((s) => s.id !== songId));
      setSelectedSongs((prev) => { const next = new Set(prev); next.delete(songId); return next; });
    } catch (err) {
      setError(err.response?.data?.error || `Failed to delete "${songTitle}".`);
    }
  }

  async function handleRemoveAssignment(assignmentId, songId, hasDraft = false) {
    const prompt = hasDraft
      ? 'This judge has an unsubmitted evaluation in progress. Removing the '
        + 'assignment discards that draft and any scores in it. Remove anyway?'
      : 'Remove this judge assignment?';
    if (!window.confirm(prompt)) return;
    try {
      await api.delete(`/api/admin/assignments/${assignmentId}`);
      setSongs((prev) =>
        prev.map((s) => {
          if (s.id !== songId) return s;
          return { ...s, assignments: s.assignments.filter((a) => a.assignment_id !== assignmentId) };
        })
      );
    } catch (err) {
      setError(err.response?.data?.error || 'Cannot remove: this judge may have submitted an evaluation.');
    }
  }

  async function handleAssign(songId) {
    const judgeId = assignSelects[songId];
    if (!judgeId) return;

    // Apply to all checked songs (including the clicked row).
    // If no checkboxes are ticked, fall back to just the clicked row.
    const targetIds = selectedSongs.size > 0
      ? [...selectedSongs]
      : [songId];

    setAssigningFor((prev) => {
      const next = { ...prev };
      targetIds.forEach((id) => { next[id] = true; });
      return next;
    });
    setAssignErrors((prev) => {
      const next = { ...prev };
      targetIds.forEach((id) => { next[id] = ''; });
      return next;
    });

    try {
      const pairs = targetIds.map((id) => ({
        song_id:  Number(id),
        judge_id: Number(judgeId),
      }));
      const res = await api.post('/api/admin/assign', { pairs });
      const { created, skipped } = res.data;

      if (created === 0 && targetIds.length === 1) {
        setAssignErrors((prev) => ({ ...prev, [songId]: 'Judge is already assigned.' }));
      } else {
        const sRes = await api.get('/api/admin/songs');
        setSongs(sRes.data.songs);
        setAssignSelects((prev) => ({ ...prev, [songId]: '' }));
        if (targetIds.length > 1) {
          // The selection is deliberately kept. Songs take two judges, so the
          // usual next step is assigning a second one to the same set; clearing
          // here meant the second judge silently landed on one row only.
          setBulkResult({
            type: created === 0 ? 'error' : 'success',
            text: created === 0
              ? `No new assignments — all ${targetIds.length} selected songs already have that judge.`
              : `Assigned ${created} slot${created !== 1 ? 's' : ''}${skipped > 0 ? `, ${skipped} skipped (already assigned)` : ''}. `
                + `${targetIds.length} songs still selected — choose another judge to add a second, or Deselect All when done.`,
          });
        }
      }
    } catch (err) {
      setAssignErrors((prev) => ({
        ...prev,
        [songId]: err.response?.data?.error || 'Failed to assign.',
      }));
    } finally {
      setAssigningFor((prev) => {
        const next = { ...prev };
        targetIds.forEach((id) => { next[id] = false; });
        return next;
      });
    }
  }

  async function handleBulkAssign() {
    setBulkAssigning(true);
    setBulkResult(null);
    // Filter out songs already at 2 judges
    const eligible = [...selectedSongs].filter((id) => {
      const s = songs.find((s) => s.id === id);
      return (s?.assignments?.length ?? 0) < 2;
    });
    if (eligible.length === 0) {
      setBulkResult({ type: 'error', text: 'All selected songs already have 2 judges assigned.' });
      setBulkAssigning(false);
      return;
    }
    // Build pairs
    const pairs = [];
    for (const songId of eligible) {
      if (bulkJudge1) pairs.push({ judge_id: Number(bulkJudge1), song_id: songId });
      if (bulkJudge2 && bulkJudge2 !== bulkJudge1) pairs.push({ judge_id: Number(bulkJudge2), song_id: songId });
    }
    try {
      const res = await api.post('/api/admin/assign', { pairs });
      const { created, skipped } = res.data;
      setBulkResult({
        type: 'success',
        text: `Assigned ${created} slot${created !== 1 ? 's' : ''}${skipped > 0 ? `, ${skipped} skipped (already assigned)` : ''}. `
          + `${eligible.length} songs still selected — Deselect All when you are done with them.`,
      });
      // Selection kept for the same reason as the per-row path above: a second
      // judge usually follows. The judge dropdowns are cleared so the next
      // choice is deliberate.
      setBulkJudge1('');
      setBulkJudge2('');
      load();
    } catch (err) {
      setBulkResult({ type: 'error', text: err.response?.data?.error ?? 'Assignment failed.' });
    } finally {
      setBulkAssigning(false);
    }
  }

  function toggleSort(col) {
    if (col === sortCol) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  }

  // FOOM Live songs (category_id=22) are performed live at heats and must
  // never appear as assignable options for online judging.
  // Note: node-postgres returns bigint columns as strings, so coerce to Number
  // before comparing to avoid "22" !== 22 always being true.
  const onlineSongs = songs.filter((s) => Number(s.category_id) !== 22);

  // Drafts are entries a contestant started and never submitted: no fee paid,
  // nothing to judge. They only padded the assignment list and skewed its
  // category counts, so they live in their own panel below. Both sets are drawn
  // from onlineSongs, so the Draft Songs panel covers exactly what was taken out
  // of the assignment list — FOOM Live drafts stay out of both.
  const assignableSongs = onlineSongs.filter((s) => s.status !== 'draft');
  const draftSongs      = onlineSongs.filter((s) => s.status === 'draft');

  const uniqueCategories = [...new Map(
    assignableSongs.map((s) => [s.category_name, s.category_name])
  ).keys()].sort();
  const tabSongs = categoryTab
    ? assignableSongs.filter((s) => s.category_name === categoryTab)
    : assignableSongs;

  // Contestant name search, applied on top of the category tab. Matches on any
  // part of the name so "gast" finds Mandi Gaston, and falls back to the
  // account email for the handful of entries whose name is not set.
  const searchTerm    = assignSearch.trim().toLowerCase();
  const searchedSongs = searchTerm
    ? tabSongs.filter((s) =>
        `${s.contestant_name || ''} ${s.contestant_email || ''}`.toLowerCase().includes(searchTerm))
    : tabSongs;

  const visibleSongs = [...searchedSongs].sort((a, b) => {
    if (!sortCol) return 0;
    let av = '', bv = '';
    if (sortCol === 'title')      { av = a.title;           bv = b.title; }
    if (sortCol === 'contestant') { av = a.contestant_name; bv = b.contestant_name; }
    if (sortCol === 'judges')     { av = a.assignments.length; bv = b.assignments.length; }
    if (typeof av === 'string') av = av.toLowerCase();
    if (typeof bv === 'string') bv = bv.toLowerCase();
    if (av < bv) return sortDir === 'asc' ? -1 :  1;
    if (av > bv) return sortDir === 'asc' ?  1 : -1;
    return 0;
  });
  const allVisibleSelected =
    visibleSongs.length > 0 &&
    visibleSongs.every((s) => selectedSongs.has(s.id));
  const someVisibleSelected =
    visibleSongs.some((s) => selectedSongs.has(s.id)) && !allVisibleSelected;

  if (loading) return <div className="c-loading"><div className="c-spinner" /></div>;

  const sortedJudges = [...judges].sort((a, b) => {
    let aVal = a[judgesSortCol] ?? '';
    let bVal = b[judgesSortCol] ?? '';
    if (typeof aVal === 'string') aVal = aVal.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal.toLowerCase();
    if (aVal < bVal) return judgesSortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return judgesSortDir === 'asc' ? 1 : -1;
    return 0;
  });

  // Two independent flags, easily confused: is_activated is "has been through the
  // setup link" (the SETUP column), is_active is "switched on by an admin" (the
  // STATUS column).
  //
  // They do NOT mean the same thing for the two kinds of judging, which is the
  // trap here. Account setup exists so a judge can LOG IN and score online. A
  // Live Heats judge never logs in — they judge on paper, off the running sheet
  // and tally form — so a pending setup does not stop them judging a heat at all.
  // That is the same reason live judges get the purple LIVE badge instead of a
  // notification button. Requiring is_activated for everyone filed Quentin Eyers
  // and Mandy Treagus as "not judging" while they were rostered to Heat 4 and the
  // Grand Final respectively.
  //
  // So the test is per panel, matching what each panel is actually for. "Both"
  // judges still appear in BOTH upper panels — whichever panel an admin is
  // scanning to make an assignment, every eligible judge for that context should
  // be visible there — and a "both" judge who is active but not yet set up shows
  // only in the live panel, which is precisely where they can still work.
  //
  // All derived on every render, so activating or setting up moves a judge
  // between panels with nothing to keep in step by hand.
  const inNonLivePanel = (j) => j.is_active && j.is_activated && j.judge_type !== 'live_only';
  const inLivePanel    = (j) => j.is_active && (j.judge_type === 'live_only' || j.judge_type === 'both');

  const nonLiveJudges = sortedJudges.filter(inNonLivePanel);
  const liveJudges    = sortedJudges.filter(inLivePanel);

  // Whoever the two panels above do not account for — defined as the leftover so
  // a judge can never be in none of the three, or in two of the three by mistake.
  const idleJudges = sortedJudges.filter((j) => !inNonLivePanel(j) && !inLivePanel(j));

  // showLiveBadge: true when rendered inside the "Live Heats & Both" panel —
  // shows the static LIVE badge for every row there (notification schedule is
  // irrelevant, since live judges are notified from the Live Heats tab), even
  // for "both"-type judges who'd otherwise get the notification-button workflow
  // in the "Non-live & Both" panel.
  const renderJudgeRow = (j, showLiveBadge = false) => (
                  <tr key={j.id} className={editingJudge?.id === j.id ? 'is-editing' : ''}>
                    <td><strong>{j.full_name}</strong></td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {j.judge_type === 'live_only'     ? 'Live Heats'
                        : j.judge_type === 'non_live_only' ? 'Non-live'
                        : 'Both'}
                    </td>
                    <td>{j.assignment_count}</td>
                    <td>{j.submitted_count}</td>
                    <td>
                      <span className={`badge ${j.is_active ? 'badge--active' : 'badge--inactive'}`}>
                        {j.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      {j.is_activated
                        ? <span className="badge badge--active">Set up</span>
                        : <span className="badge badge--warning">Pending</span>
                      }
                    </td>
                    <td>
                      {(j.judge_type === 'live_only' || showLiveBadge)
                        ? (
                          <span
                            className="badge"
                            style={{ background: '#7c3aed', color: '#fff', cursor: 'default' }}
                            title="Managed via Live Heats tab"
                          >
                            LIVE
                          </span>
                        )
                        : (() => {
                            // Gold sends the account-setup link, so it needs is_activated
                            // FALSE as well as a null notified date. Assigning songs nulls
                            // that date on purpose, to flag "has new songs to be told
                            // about" — with the null alone deciding, the button turned gold
                            // for judges set up weeks earlier and mailed them a setup link
                            // that could only say their account was already active. It
                            // caught Corey Stewart on 23 Aug 2026.
                            const notifiedTs = j.assignments_notified_at
                              ? new Date(j.assignments_notified_at)
                              : null;
                            const dateStr = notifiedTs
                              ? notifiedTs.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })
                              : null;
                            const fullStr = notifiedTs ? notifiedTs.toLocaleString('en-AU') : 'never';

                            // Colour/label is the EMAIL. The line beneath is the OUTSTANDING
                            // WORK, kept separate so a judge who needs re-notifying is still
                            // obvious without misnaming the email they would receive.
                            const isFirst = !j.is_activated && !notifiedTs;
                            const pending = !notifiedTs && !isFirst;

                            const v = isFirst
                              ? { bg: '#d4af37', fg: '#1a1a2e', label: 'FIRST NOTIFICATION',
                                  title: 'Send welcome/invitation email with account setup link' }
                              : !j.assignment_email_sent
                                ? { bg: '#16a34a', fg: '#fff', label: 'ASSIGNMENT NOTIFICATION',
                                    title: 'Account set up — click to send assignment notification (last notified ' + fullStr + ')' }
                                : { bg: '#0ea5e9', fg: '#fff', label: 'REASSIGN NOTIFICATION',
                                    title: 'Assignment notification last sent ' + fullStr + ' — click to resend' };

                            return (
                              <span>
                                <button
                                  className="c-btn c-btn--sm"
                                  style={{ background: v.bg, color: v.fg, whiteSpace: 'nowrap' }}
                                  onClick={() => handleNotifyJudge(j)}
                                  disabled={notifyingId === j.id || editingJudge !== null || !j.is_active}
                                  title={v.title}
                                >
                                  {notifyingId === j.id ? '…' : v.label}
                                </button>
                                {pending ? (
                                  <span style={{ display: 'block', fontSize: '0.7rem', color: '#b45309', fontWeight: 600, marginTop: '0.15rem' }}>
                                    New songs — not notified
                                  </span>
                                ) : dateStr ? (
                                  <span style={{ display: 'block', fontSize: '0.7rem', color: '#6b7280', marginTop: '0.15rem' }}>{dateStr}</span>
                                ) : null}
                              </span>
                            );
                          })()
                      }
                    </td>
                    <td>
                      <div className="a-row-actions">
                        <EmailEntrantControl
                          user={{ user_id: j.user_id, full_name: j.full_name }}
                          label="Email Judge"
                          noun="judge"
                        />
                        <button
                          className="c-btn c-btn--sm c-btn--outline"
                          onClick={() => startEditJudge(j)}
                          disabled={editingJudge !== null}
                        >
                          Edit
                        </button>
                        <button
                          className="c-btn c-btn--sm c-btn--outline"
                          onClick={() => handleToggleJudgeActive(j)}
                          disabled={editingJudge !== null}
                        >
                          {j.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          className="c-btn c-btn--sm c-btn--danger"
                          onClick={() => { setDeleteJudgeConfirm(j); setDeleteJudgeErr(''); }}
                          disabled={editingJudge !== null}
                          title="Permanently delete this judge and all their data"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
  );

  const judgeTableHead = (
                <tr>
                  <SortTh col="full_name"        sortCol={judgesSortCol} sortDir={judgesSortDir} onSort={handleJudgeSort}>Name</SortTh>
                  <SortTh col="judge_type"       sortCol={judgesSortCol} sortDir={judgesSortDir} onSort={handleJudgeSort}>Type</SortTh>
                  <SortTh col="assignment_count" sortCol={judgesSortCol} sortDir={judgesSortDir} onSort={handleJudgeSort}>Assigned</SortTh>
                  <SortTh col="submitted_count"  sortCol={judgesSortCol} sortDir={judgesSortDir} onSort={handleJudgeSort}>Submitted</SortTh>
                  <SortTh col="is_active"        sortCol={judgesSortCol} sortDir={judgesSortDir} onSort={handleJudgeSort}>Status</SortTh>
                  <SortTh col="is_activated"     sortCol={judgesSortCol} sortDir={judgesSortDir} onSort={handleJudgeSort}>Setup</SortTh>
                  <th>Notified</th>
                  <th>Actions</th>
                </tr>
  );

  return (
    <div>
      {error && <div className="c-alert">{error}</div>}

      {(mode === 'judges' || mode === 'both') && (
      <div className="a-section">
        <div className="a-section__header" style={{ cursor: 'pointer' }} onClick={() => setJudgesOpen((v) => !v)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <span className="a-chevron" style={{ transform: judgesOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
            <div>
              <div className="a-section__title">Judges</div>
              <div className="a-section__desc">{judges.length} judge(s) registered.</div>
            </div>
          </div>
          {editingJudge === null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
              <button
                className="c-btn"
                style={{ background: '#d4af37', color: '#1a1a2e' }}
                onClick={handleNotifyAll}
                disabled={notifyingAll}
                title="Send invitation emails to unactivated judges and assignment emails to activated judges with songs"
              >
                {notifyingAll
                  ? <><span className="c-spinner" style={{ width: '0.9rem', height: '0.9rem', borderWidth: '2px', display: 'inline-block', verticalAlign: 'middle', marginRight: '0.35rem' }} />Notifying…</>
                  : '✉ Notify All Judges'
                }
              </button>
              <button
                className="c-btn"
                onClick={() => { setShowAddJudge((v) => !v); setJudgeFormErr(''); setNotifyAllMsg(''); }}
              >
                {showAddJudge ? 'Cancel' : '+ Add Judge'}
              </button>
            </div>
          )}
          {notifyAllMsg && (
            <p style={{ fontSize: '0.82rem', color: notifyAllMsg.includes('Failed') ? '#dc2626' : '#16a34a', margin: '0.4rem 0 0', fontWeight: 600 }}>
              {notifyAllMsg}
            </p>
          )}
        </div>

        {judgesOpen && (
        <>

        {showAddJudge && (
          <div className="c-form-card">
            <div className="c-form-card__title">New Judge Account</div>
            {judgeFormErr && <div className="c-alert">{judgeFormErr}</div>}

            <div className="c-form-grid">
              <div className="c-form-group">
                <label className="c-label">Full Name *<Tooltip tooltipKey="admin_judge_name" /></label>
                <input
                  className="c-input"
                  value={judgeForm.full_name}
                  onChange={(e) => setJudgeForm((p) => ({ ...p, full_name: e.target.value }))}
                />
              </div>
              <div className="c-form-group">
                <label className="c-label">Email *<Tooltip tooltipKey="admin_judge_email" /></label>
                <input
                  className="c-input"
                  type="email"
                  value={judgeForm.email}
                  onChange={(e) => setJudgeForm((p) => ({ ...p, email: e.target.value }))}
                />
              </div>
              <div className="c-form-group">
                <label className="c-label">Type</label>
                <select
                  className="c-input"
                  value={judgeForm.judge_type}
                  onChange={(e) => setJudgeForm((p) => ({ ...p, judge_type: e.target.value }))}
                >
                  <option value="both">Both</option>
                  <option value="live_only">Live Heats</option>
                  <option value="non_live_only">Non-live</option>
                </select>
              </div>
              <div className="c-form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="c-label">Notes <span className="c-label-optional">(optional)</span></label>
                <textarea
                  className="c-input"
                  value={judgeForm.notes}
                  onChange={(e) => setJudgeForm((p) => ({ ...p, notes: e.target.value }))}
                  onInput={(e) => {
                    e.target.style.height = 'auto';
                    e.target.style.height = e.target.scrollHeight + 'px';
                  }}
                  style={{ fontSize: '0.95rem', overflowY: 'hidden', resize: 'none', minHeight: '80px' }}
                />
              </div>
            </div>

            <div className="a-form-actions">
              <button className="c-btn" onClick={handleAddJudge} disabled={addingJudge}>
                {addingJudge ? 'Creating…' : 'Create Judge'}
              </button>
            </div>
          </div>
        )}

        {editingJudge !== null && (
          <div className="c-form-card">
            <div className="c-form-card__title">Edit — {editingJudge.full_name}</div>
            {editFormErr && <div className="c-alert">{editFormErr}</div>}

            <div className="c-form-grid">
              <div className="c-form-group">
                <label className="c-label">Full Name *</label>
                <input
                  className="c-input"
                  value={editForm.full_name || ''}
                  onChange={(e) => setEditForm((p) => ({ ...p, full_name: e.target.value }))}
                />
              </div>
              <div className="c-form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="c-label" style={{ color: '#6b7280' }}>Email</label>
                <input
                  className="c-input"
                  value={editingJudge.email || ''}
                  readOnly
                  style={{ background: '#f3f4f6', color: '#6b7280', cursor: 'default' }}
                />
              </div>
              <div className="c-form-group">
                <label className="c-label">Type</label>
                <select
                  className="c-input"
                  value={editForm.judge_type || 'both'}
                  onChange={(e) => setEditForm((p) => ({ ...p, judge_type: e.target.value }))}
                >
                  <option value="both">Both</option>
                  <option value="live_only">Live Heats</option>
                  <option value="non_live_only">Non-live</option>
                </select>
              </div>
              <div className="c-form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="c-label">Notes <span className="c-label-optional">(optional)</span></label>
                <textarea
                  ref={editNotesRef}
                  className="c-input"
                  value={editForm.notes || ''}
                  onChange={(e) => setEditForm((p) => ({ ...p, notes: e.target.value }))}
                  onInput={(e) => {
                    e.target.style.height = 'auto';
                    e.target.style.height = e.target.scrollHeight + 'px';
                  }}
                  style={{ fontSize: '0.95rem', overflowY: 'hidden', resize: 'none', minHeight: '80px' }}
                />
              </div>
              <div className="a-toggle-row" style={{ gridColumn: '1 / -1' }}>
                <span className="c-label" style={{ margin: 0 }}>Active</span>
                <Toggle
                  id="judge-edit-active"
                  checked={editForm.is_active}
                  onChange={(v) => setEditForm((p) => ({ ...p, is_active: v }))}
                />
              </div>
            </div>

            <div className="a-form-actions">
              <button className="c-btn" onClick={handleSaveJudge} disabled={savingJudge}>
                {savingJudge ? 'Saving…' : 'Save Changes'}
              </button>
              <button className="c-btn c-btn--outline" onClick={cancelEditJudge}>Cancel</button>
              {editSaveStatus && <span className="a-save-status">{editSaveStatus}</span>}
            </div>
          </div>
        )}

        {judges.length === 0 ? (
          <div className="c-empty">
            <div className="c-empty__icon">👩‍⚖️</div>
            <h3 className="c-empty__title">No judges yet</h3>
            <p className="c-empty__text">Add your first judge to get started.</p>
          </div>
        ) : (
          <>
            {/* Panel 1 — Non-live & Both */}
            <div style={{ marginTop: '0.5rem' }}>
              <div className="a-section__header" style={{ cursor: 'pointer' }} onClick={() => setJudgesNonLiveOpen((v) => !v)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <span className="a-chevron" style={{ transform: judgesNonLiveOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                  <div>
                    <div className="a-section__title">Non-live &amp; Both</div>
                    <div className="a-section__desc">{nonLiveJudges.length} judge(s)</div>
                  </div>
                </div>
              </div>
              {judgesNonLiveOpen && (
                nonLiveJudges.length === 0 ? (
                  <div className="c-empty">
                    <p className="c-empty__text">No non-live or both-type judges.</p>
                  </div>
                ) : (
                  <div className="a-table-wrap">
                    <table className="a-table">
                      <thead>{judgeTableHead}</thead>
                      <tbody>{nonLiveJudges.map((j) => renderJudgeRow(j, false))}</tbody>
                    </table>
                  </div>
                )
              )}
            </div>

            {/* Panel 2 — Live Heats & Both */}
            <div style={{ marginTop: '1.25rem' }}>
              <div className="a-section__header" style={{ cursor: 'pointer' }} onClick={() => setJudgesLiveOpen((v) => !v)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <span className="a-chevron" style={{ transform: judgesLiveOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                  <div>
                    <div className="a-section__title">Live Heats &amp; Both</div>
                    <div className="a-section__desc">{liveJudges.length} judge(s)</div>
                  </div>
                </div>
              </div>
              {judgesLiveOpen && (
                liveJudges.length === 0 ? (
                  <div className="c-empty">
                    <p className="c-empty__text">No live-heats or both-type judges.</p>
                  </div>
                ) : (
                  <div className="a-table-wrap">
                    <table className="a-table">
                      <thead>{judgeTableHead}</thead>
                      <tbody>{liveJudges.map((j) => renderJudgeRow(j, true))}</tbody>
                    </table>
                  </div>
                )
              )}
            </div>

            {/* Panel 3 — held back by either flag. Rendered with showLiveBadge
                false so a non-live or both judge keeps their notification button
                and can be chased from here; live-only rows still fall through to
                the LIVE badge on their own, as everywhere else. The SETUP and
                STATUS badges on each row say which flag put them here. */}
            <div style={{ marginTop: '1.25rem' }}>
              <div className="a-section__header" style={{ cursor: 'pointer' }} onClick={() => setJudgesIdleOpen((v) => !v)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <span className="a-chevron" style={{ transform: judgesIdleOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                  <div>
                    <div className="a-section__title">
                      Not Currently Judging
                      {idleJudges.length > 0 && (
                        <span style={{ marginLeft: '0.5rem', color: '#6b7280', fontWeight: 500 }}>
                          ({idleJudges.length})
                        </span>
                      )}
                    </div>
                    <div className="a-section__desc">
                      Deactivated, or awaiting the account setup their kind of judging needs. Live Heats judges score on paper, so setup does not hold them back.
                    </div>
                  </div>
                </div>
              </div>
              {judgesIdleOpen && (
                idleJudges.length === 0 ? (
                  <div className="c-empty">
                    <p className="c-empty__text">Every judge is set up and active.</p>
                  </div>
                ) : (
                  <div className="a-table-wrap">
                    <table className="a-table">
                      <thead>{judgeTableHead}</thead>
                      <tbody>{idleJudges.map((j) => renderJudgeRow(j, false))}</tbody>
                    </table>
                  </div>
                )
              )}
            </div>
          </>
        )}
        </>
        )}
      </div>
      )}

      {/* Admin song entry modal */}
      {showSongEntry && (
        <AdminSongEntryModal
          onClose={() => setShowSongEntry(false)}
          onSuccess={() => { setShowSongEntry(false); load(); }}
        />
      )}

      {/* Notify-all confirmation dialog */}
      {notifyConfirm === 'all' && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: '#fff', borderRadius: '10px', padding: '1.75rem 2rem',
            maxWidth: '420px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          }}>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1a1a2e', marginBottom: '0.75rem' }}>Notify All Judges</div>
            <p style={{ margin: '0 0 1.5rem', lineHeight: 1.5, color: '#374151' }}>
              Emails STATE 1 and STATE 2 judges only (live-only and STATE 3 judges are skipped):
              <br />• <strong>STATE 1</strong> — Welcome/invitation email with fresh account setup link
              <br />• <strong>STATE 2</strong> — Assignment notification email (only if songs are assigned)
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button className="c-btn c-btn--outline" onClick={() => setNotifyConfirm(null)}>Cancel</button>
              <button className="c-btn" style={{ background: '#0ea5e9' }} onClick={doNotifyAll}>
                Send Emails
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Per-judge notify confirmation dialog — 3-state aware */}
      {notifyConfirm?.type === 'one' && (() => {
        const s      = notifyConfirm.notifyState;
        const btnBg  = s === 1 ? '#d4af37' : s === 2 ? '#16a34a' : '#0ea5e9';
        const btnClr = s === 1 ? '#1a1a2e' : '#fff';
        const title  = s === 1 ? 'Send First Notification'
                     : s === 2 ? 'Send Assignment Notification'
                     :           'Resend Assignment Notification';
        const body   = s === 1
          ? 'Send a welcome/invitation email with a fresh account setup link. Any previous setup link will be invalidated.'
          : s === 2
            ? 'Send the assignment notification email to this judge, letting them know their song assignments are ready.'
            : 'Resend the assignment notification email to this judge. The "last notified" date will be updated to now.';
        return (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000,
          }}>
            <div style={{
              background: '#fff', borderRadius: '10px', padding: '1.75rem 2rem',
              maxWidth: '420px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            }}>
              <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1a1a2e', marginBottom: '0.75rem' }}>
                {title}
              </div>
              <p style={{ margin: '0 0 1.5rem', lineHeight: 1.5, color: '#374151' }}>
                {body}
              </p>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button className="c-btn c-btn--outline" onClick={() => setNotifyConfirm(null)}>Cancel</button>
                <button className="c-btn" style={{ background: btnBg, color: btnClr }} onClick={() => doNotifyJudge(notifyConfirm.judgeId)}>
                  Send Email
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Delete judge confirmation dialog */}
      {deleteJudgeConfirm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: '#fff', borderRadius: '10px', padding: '1.75rem 2rem',
            maxWidth: '440px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          }}>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1a1a2e', marginBottom: '0.75rem' }}>Delete Judge</div>
            <p style={{ margin: '0 0 0.75rem', lineHeight: 1.5, color: '#374151' }}>
              Are you sure you want to delete <strong>{deleteJudgeConfirm.full_name}</strong>? This will permanently remove all their assignments and evaluation history.
            </p>
            {deleteJudgeConfirm.submitted_count > 0 && (
              <div className="c-alert" style={{ marginBottom: '0.75rem' }}>
                ⚠ This judge has submitted {deleteJudgeConfirm.submitted_count} evaluation{deleteJudgeConfirm.submitted_count !== 1 ? 's' : ''} — deleting will permanently remove their scoring history.
              </div>
            )}
            {deleteJudgeErr && (
              <div className="c-alert" style={{ marginBottom: '0.75rem' }}>{deleteJudgeErr}</div>
            )}
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                className="c-btn c-btn--outline"
                onClick={() => { setDeleteJudgeConfirm(null); setDeleteJudgeErr(''); }}
                disabled={deletingJudge}
              >
                Cancel
              </button>
              <button
                className="c-btn c-btn--danger"
                onClick={() => handleDeleteJudge(deleteJudgeConfirm)}
                disabled={deletingJudge}
              >
                {deletingJudge ? 'Deleting…' : 'Delete Judge'}
              </button>
            </div>
          </div>
        </div>
      )}

      {(mode === 'songassignments' || mode === 'both') && (
      <div className="a-section">
        <div className="a-section__header" style={{ cursor: 'pointer' }} onClick={() => setAssignmentsOpen((v) => !v)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <span className="a-chevron" style={{ transform: assignmentsOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
            <div>
              <div className="a-section__title">Assignments by Song</div>
              <div className="a-section__desc">Assign judges to songs or remove assignments (blocked once a judge has submitted an evaluation).</div>
            </div>
          </div>
          <button
            className="c-btn"
            onClick={(e) => { e.stopPropagation(); setShowSongEntry(true); }}
            title="Submit a song on behalf of a contestant who has paid in cash."
          >
            + Admin Song Entry
          </button>
        </div>

        {assignmentsOpen && (
        <>

        {songs.length === 0 ? (
          <div className="c-empty">
            <div className="c-empty__icon">🎵</div>
            <h3 className="c-empty__title">No songs submitted yet</h3>
            <p className="c-empty__text">Assignments will appear here once songs are submitted.</p>
          </div>
        ) : (
            <>
              {/* ── Category tabs ── */}
              {uniqueCategories.length > 1 && (
                <div className="a-cat-tabs">
                  <button
                    className={`a-cat-tab${categoryTab === '' ? ' a-cat-tab--active' : ''}`}
                    onClick={() => setCategoryTab('')}
                  >
                    All ({assignableSongs.length})
                  </button>
                  {uniqueCategories.map((cat) => (
                    <button
                      key={cat}
                      className={`a-cat-tab${categoryTab === cat ? ' a-cat-tab--active' : ''}`}
                      onClick={() => setCategoryTab(cat)}
                    >
                      {cat} ({assignableSongs.filter((s) => s.category_name === cat).length})
                    </button>
                  ))}
                </div>
              )}

              {/* ── Contestant search ── */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', margin: '0.75rem 0 1rem' }}>
                <input
                  className="c-input"
                  type="search"
                  value={assignSearch}
                  onChange={(e) => setAssignSearch(e.target.value)}
                  placeholder="Search contestant name…"
                  aria-label="Search by contestant name"
                  style={{ maxWidth: 300 }}
                />
                {searchTerm && (
                  <>
                    <span style={{ fontSize: '0.82rem', color: visibleSongs.length ? '#6b7280' : '#b91c1c' }}>
                      {visibleSongs.length} of {tabSongs.length} song{tabSongs.length === 1 ? '' : 's'}
                      {categoryTab ? ` in ${categoryTab}` : ''}
                      {visibleSongs.length === 0 ? ' — no contestant matches' : ''}
                    </span>
                    <button
                      type="button"
                      className="c-btn c-btn--sm c-btn--outline"
                      onClick={() => setAssignSearch('')}
                    >
                      Clear
                    </button>
                  </>
                )}
              </div>

              {/* ── Bulk assignment panel (visible when songs selected) ── */}
              {selectedSongs.size > 0 && (
                <div className="a-bulk-panel">
                  <span className="a-bulk-panel__label">
                    Assign {selectedSongs.size} selected song{selectedSongs.size !== 1 ? 's' : ''} to:
                  </span>
                  <select
                    value={bulkJudge1}
                    onChange={(e) => setBulkJudge1(e.target.value)}
                    className="a-assign-select"
                  >
                    <option value="">— Judge 1 —</option>
                    {judges.filter((j) => j.is_active && j.judge_type !== 'live_only').map((j) => {
                      const conflicts = j.conflicted_categories || [];
                      const hasAssignments = (j.assignment_count || 0) > 0;
                      const optColor = conflicts.length > 0 ? '#dc2626' : hasAssignments ? '#2563eb' : '#1a1a2e';
                      const label = conflicts.length > 0
                        ? `⚠ ${j.full_name} (${conflicts.map((c) => c.category_name).join(', ')} entrant)`
                        : j.full_name;
                      return <option key={j.id} value={j.id} style={{ color: optColor }}>{label}</option>;
                    })}
                  </select>
                  <select
                    value={bulkJudge2}
                    onChange={(e) => setBulkJudge2(e.target.value)}
                    className="a-assign-select"
                  >
                    <option value="">— Judge 2 (optional) —</option>
                    {judges.filter((j) => j.is_active && j.judge_type !== 'live_only').map((j) => {
                      const conflicts = j.conflicted_categories || [];
                      const hasAssignments = (j.assignment_count || 0) > 0;
                      const optColor = conflicts.length > 0 ? '#dc2626' : hasAssignments ? '#2563eb' : '#1a1a2e';
                      const label = conflicts.length > 0
                        ? `⚠ ${j.full_name} (${conflicts.map((c) => c.category_name).join(', ')} entrant)`
                        : j.full_name;
                      return (
                        <option key={j.id} value={j.id} style={{ color: optColor }} disabled={String(j.id) === String(bulkJudge1)}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                  <button
                    className="c-btn c-btn--sm"
                    onClick={handleBulkAssign}
                    disabled={bulkAssigning || !bulkJudge1}
                  >
                    {bulkAssigning ? 'Assigning…' : 'Assign Selected'}
                  </button>
                  <button
                    className="c-btn c-btn--sm c-btn--outline"
                    onClick={() => { setSelectedSongs(new Set()); setBulkResult(null); }}
                  >
                    Deselect All
                  </button>
                  {(() => {
                    const fullCount = [...selectedSongs].filter((id) => {
                      const s = songs.find((s) => s.id === id);
                      return (s?.assignments?.length ?? 0) >= 2;
                    }).length;
                    return fullCount > 0 ? (
                      <span style={{ color: '#d97706', fontSize: '0.8rem' }}>
                        ⚠ {fullCount} song{fullCount !== 1 ? 's' : ''} already have 2 judges and will be skipped
                      </span>
                    ) : null;
                  })()}
                </div>
              )}
              {bulkResult && (
                <div
                  className={`c-alert${bulkResult.type === 'success' ? ' c-alert--success' : ''}`}
                  role="alert"
                  style={{ marginBottom: '0.75rem' }}
                >
                  {bulkResult.text}
                </div>
              )}

              <div className="a-table-wrap">
                <table className="a-table">
                  <thead>
                    <tr>
                      <th style={{ width: 34, textAlign: 'right', color: '#6b7280' }}>#</th>
                      <th style={{ width: 36 }}>
                        <input
                          type="checkbox"
                          checked={allVisibleSelected}
                          ref={(el) => {
                            if (el) el.indeterminate = someVisibleSelected;
                          }}
                          onChange={(e) => {
                            setSelectedSongs((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) {
                                visibleSongs.forEach((s) => next.add(s.id));
                              } else {
                                visibleSongs.forEach((s) => next.delete(s.id));
                              }
                              return next;
                            });
                          }}
                          aria-label="Select all visible songs"
                        />
                      </th>
                      <SortTh col="title" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort}>Song</SortTh>
                      <SortTh col="contestant" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort}>Contestant</SortTh>
                      <th>Status</th>
                      <th>Payment</th>
                      <th>Category</th>
                      <SortTh col="judges" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort}>Assigned Judges</SortTh>
                      <th>Assign</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleSongs.map((song, rowIndex) => (
                      <tr key={song.id}>
                        <td style={{ textAlign: 'right', color: '#6b7280', fontVariantNumeric: 'tabular-nums' }}>
                          {rowIndex + 1}
                        </td>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedSongs.has(song.id)}
                            onChange={() => setSelectedSongs((prev) => {
                              const next = new Set(prev);
                              next.has(song.id) ? next.delete(song.id) : next.add(song.id);
                              return next;
                            })}
                            aria-label={`Select ${song.title}`}
                          />
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <AudioControls
                              songId={song.id}
                              audioUrl={song.audio_url ? audioPath(song.audio_url) : null}
                              playingId={playingId}
                              onToggle={toggleAudio}
                            />
                            <div>
                          <Link
                            to={`/admin/songs/${song.id}/review`}
                            className="song-title-link"
                            style={{ fontWeight: 600 }}
                          >
                            {song.title}
                          </Link>
                          {song.evaluation_status === 'conflict' && (
                            <span style={{
                              display:      'inline-block',
                              marginLeft:   '0.45rem',
                              background:   '#fef3c7',
                              color:        '#92400e',
                              border:       '1px solid #fde68a',
                              borderRadius: '4px',
                              fontSize:     '0.68rem',
                              fontWeight:   700,
                              padding:      '1px 6px',
                              textTransform:'uppercase',
                              letterSpacing:'0.4px',
                              verticalAlign:'middle',
                            }}>
                              Conflict
                            </span>
                          )}
                            </div>
                          </div>
                        </td>
                        <td>{song.contestant_name}</td>
                        <td><SongStatusBadge status={song.status} /></td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                            <span className={`badge badge--${song.payment_status === 'paid' || song.payment_status === 'waived' ? 'active' : 'pending'}`}>
                              {song.payment_status}
                            </span>
                            <RefundControl
                              song={song}
                              onRefunded={(id) => setSongs((prev) => prev.map((s) => (
                                s.id === id ? { ...s, payment_status: 'refunded' } : s
                              )))}
                            />
                            <EmailEntrantControl song={song} />
                          </div>
                        </td>
                        <td>{song.category_name}</td>
                        <td>
                          <div className="a-assign-tags">
                            {song.assignments.length === 0 ? (
                              <span style={{ color: '#9ca3af', fontSize: '0.82rem' }}>None</span>
                            ) : (
                              song.assignments.map((a) => (
                                <span key={a.assignment_id} className="a-assign-tag">
                                  {a.judge_name}
                                  {/* Same precedence as the judge's own dashboard: submitted
                                      outranks ready. This showed ready_for_submission only and
                                      ignored evaluation_submitted, which the API has always
                                      sent — so a judge who had finished and submitted every
                                      song showed nothing, while one who had marked songs ready
                                      but submitted none wore a green tick. Exactly backwards.
                                      Bill Swampy and Jude Aquilla had submitted 58 evaluations
                                      between them and looked untouched (26 Aug 2026). */}
                                  {a.evaluation_submitted ? (
                                    <span style={{
                                      background:   '#16a34a',
                                      color:        '#fff',
                                      borderRadius: '4px',
                                      padding:      '0 0.4rem',
                                      fontSize:     '0.75rem',
                                      marginLeft:   '0.3rem',
                                      flexShrink:   0,
                                    }}>
                                      ✓ Submitted
                                    </span>
                                  ) : a.ready_for_submission ? (
                                    /* Amber, not green: scored but NOT submitted, so it is work
                                       in progress rather than work finished. */
                                    <span style={{
                                      background:   '#f59e0b',
                                      color:        '#1a1a2e',
                                      borderRadius: '4px',
                                      padding:      '0 0.4rem',
                                      fontSize:     '0.75rem',
                                      fontWeight:   600,
                                      marginLeft:   '0.3rem',
                                      flexShrink:   0,
                                    }}>
                                      Ready
                                    </span>
                                  ) : null}
                                  {!a.evaluation_submitted && (
                                    <button
                                      className="a-assign-tag__remove"
                                      title={a.has_evaluation
                                        ? 'Remove assignment (discards the unsubmitted draft evaluation)'
                                        : 'Remove assignment'}
                                      onClick={() => handleRemoveAssignment(a.assignment_id, song.id, a.has_evaluation)}
                                    >
                                      ✕
                                    </button>
                                  )}
                                </span>
                              ))
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="a-assign-control">
                            <select
                              className="a-assign-select"
                              value={assignSelects[song.id] || ''}
                              onChange={(e) =>
                                setAssignSelects((prev) => ({ ...prev, [song.id]: e.target.value }))
                              }
                            >
                              <option value="">— Judge —</option>
                              {judges.filter((j) => j.is_active && j.judge_type !== 'live_only').map((j) => {
                                const conflict = (j.conflicted_categories || []).find(
                                  (c) => c.category_id === Number(song.category_id)
                                );
                                const hasAssignments = (j.assignment_count || 0) > 0;
                                const optColor = conflict ? '#dc2626' : hasAssignments ? '#2563eb' : '#1a1a2e';
                                return (
                                  <option key={j.id} value={j.id} style={{ color: optColor }}>
                                    {conflict ? `⚠ ${j.full_name} (${conflict.category_name} entrant)` : j.full_name}
                                  </option>
                                );
                              })}
                            </select>
                            <button
                              className="c-btn c-btn--sm"
                              disabled={!assignSelects[song.id] || assigningFor[song.id]}
                              onClick={() => handleAssign(song.id)}
                            >
                              {assigningFor[song.id] ? '…' : 'Assign'}
                            </button>
                          </div>
                          {assignErrors[song.id] && (
                            <p style={{ color: '#dc2626', fontSize: '0.75rem', margin: '0.25rem 0 0' }}>
                              {assignErrors[song.id]}
                            </p>
                          )}
                        </td>
                        <td>
                          <button
                            className="c-btn c-btn--sm c-btn--danger"
                            onClick={() => handleDeleteSong(song.id, song.title)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
        )}
        </>
        )}
      </div>
      )}

      {(mode === 'songassignments' || mode === 'both') && (
      <div className="a-section">
        <div className="a-section__header" style={{ cursor: 'pointer' }} onClick={() => setDraftsOpen((v) => !v)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <span className="a-chevron" style={{ transform: draftsOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
            <div>
              <div className="a-section__title">
                Draft Songs
                {draftSongs.length > 0 && (
                  <span style={{ marginLeft: '0.5rem', color: '#6b7280', fontWeight: 500 }}>
                    ({draftSongs.length})
                  </span>
                )}
              </div>
              <div className="a-section__desc">
                Entries started but never submitted — no fee paid and nothing to judge, so they are
                not assignable. Chase the contestant with Email Entrant.
              </div>
            </div>
          </div>
        </div>

        {draftsOpen && (
          draftSongs.length === 0 ? (
            <div className="c-empty">
              <div className="c-empty__icon">✅</div>
              <h3 className="c-empty__title">No draft songs</h3>
              <p className="c-empty__text">Every entry has been submitted.</p>
            </div>
          ) : (
            <div className="a-table-wrap">
              <table className="a-table">
                <thead>
                  <tr>
                    <th style={{ width: 34, textAlign: 'right', color: '#6b7280' }}>#</th>
                    <th>Song</th>
                    <th>Contestant</th>
                    <th>Status</th>
                    <th>Payment</th>
                    <th>Category</th>
                    <th>Started</th>
                    <th>Last Saved</th>
                    <th></th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {draftSongs.map((song, rowIndex) => (
                    <tr key={song.id}>
                      <td style={{ textAlign: 'right', color: '#6b7280', fontVariantNumeric: 'tabular-nums' }}>
                        {rowIndex + 1}
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <AudioControls
                            songId={song.id}
                            audioUrl={song.audio_url ? audioPath(song.audio_url) : null}
                            playingId={playingId}
                            onToggle={toggleAudio}
                          />
                          <Link
                            to={`/admin/songs/${song.id}/review`}
                            className="song-title-link"
                            style={{ fontWeight: 600 }}
                          >
                            {song.title || '(untitled)'}
                          </Link>
                        </div>
                      </td>
                      <td>{song.contestant_name}</td>
                      <td><SongStatusBadge status={song.status} /></td>
                      <td>
                        <span className={`badge badge--${song.payment_status === 'paid' || song.payment_status === 'waived' ? 'active' : 'pending'}`}>
                          {song.payment_status}
                        </span>
                      </td>
                      <td>{song.category_name}</td>
                      <td style={{ whiteSpace: 'nowrap', color: '#6b7280', fontSize: '0.85rem' }}>
                        {song.created_at
                          ? new Date(song.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
                          : '—'}
                      </td>
                      <td style={{ whiteSpace: 'nowrap', color: '#6b7280', fontSize: '0.85rem' }}>
                        {song.updated_at
                          ? new Date(song.updated_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
                          : '—'}
                      </td>
                      <td><EmailEntrantControl song={song} /></td>
                      <td>
                        <button
                          className="c-btn c-btn--sm c-btn--danger"
                          onClick={() => handleDeleteSong(song.id, song.title)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
      )}

    </div>
  );
}

// ─── SongStatusBadge ──────────────────────────────────────────────────────────
// The admin songs list is unfiltered, so drafts sit alongside real submissions
// and used to look identical — a draft with payment PENDING read as a submitted
// entry nobody had paid for. This says which is which.
function SongStatusBadge({ status }) {
  const map = {
    draft:        'badge badge--pending',
    submitted:    'badge badge--submitted',
    approved:     'badge badge--approved',
    withdrawn:    'badge badge--withdrawn',
    disqualified: 'badge badge--disqualified',
  };
  return <span className={map[status] || 'badge badge--pending'}>{status || 'unknown'}</span>;
}

// ─── RefundControl ────────────────────────────────────────────────────────────
// Refund button + dialog for one song's entry fee. Self-contained so it can sit
// wherever a song appears: the Assignments by Song list, the Unassigned Songs
// table, and the rows inside each heat. FOOM Live entries (category 22) never
// appear in the assignments list, so without the latter two there would be no
// way to refund a live entry at all.
//
// Renders nothing unless the song is actually paid.
function RefundControl({ song, onRefunded }) {
  const [open,    setOpen]    = useState(false);
  const [info,    setInfo]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy,    setBusy]    = useState(false);
  const [error,   setError]   = useState('');
  const [whole,   setWhole]   = useState(false);
  const [result,  setResult]  = useState(null);

  const money = (cents, cur) => `${(cents / 100).toFixed(2)} ${(cur || 'aud').toUpperCase()}`;

  async function openDialog() {
    setOpen(true);
    setInfo(null); setError(''); setResult(null); setWhole(false);
    setLoading(true);
    try {
      const res = await api.get(`/api/admin/songs/${song.id}/payment`);
      if (res.data.found === false) setError(res.data.error || 'No payment found.');
      else setInfo(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not look up that payment.');
    } finally {
      setLoading(false);
    }
  }

  async function doRefund() {
    if (!info) return;
    const amount = whole ? info.refundable_cents : info.per_song_cents;
    setBusy(true);
    setError('');
    try {
      const res = await api.post(`/api/admin/songs/${song.id}/refund`, {
        payment_intent_id: info.payment_intent_id,
        amount_cents:      amount,
      });
      setResult(res.data);
      onRefunded?.(song.id);
    } catch (err) {
      setError(err.response?.data?.error || 'The refund did not go through.');
    } finally {
      setBusy(false);
    }
  }

  if (song.payment_status !== 'paid') return null;

  const amount = info ? (whole ? info.refundable_cents : info.per_song_cents) : 0;

  return (
    <>
      <button
        className="c-btn c-btn--outline c-btn--sm"
        onClick={(e) => { e.stopPropagation(); openDialog(); }}
        title={`Refund the entry fee for "${song.title}"`}
      >
        Refund
      </button>

      {open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: '10px', padding: '1.75rem 2rem', maxWidth: '520px', width: '92%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', textAlign: 'left' }}>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1a1a2e', marginBottom: '0.75rem' }}>
              Refund entry fee
            </div>

            {loading && <p style={{ color: '#6b7280', margin: '0 0 1.5rem' }}>Looking up the payment in Stripe…</p>}

            {result ? (
              <>
                <p style={{ margin: '0 0 0.75rem', lineHeight: 1.5, color: '#374151' }}>
                  Refunded <strong>{money(result.amount_cents, result.currency)}</strong> to{' '}
                  <strong>{result.contestant}</strong> for <strong>{result.song_title}</strong>.
                </p>
                <p style={{ margin: '0 0 1.25rem', fontSize: '0.85rem', color: '#6b7280', lineHeight: 1.5 }}>
                  Stripe reference <code>{result.refund_id}</code> (status: {result.status}). It usually reaches
                  their card in 5–10 business days.
                  {result.status_updated === false && (
                    <><br /><strong style={{ color: '#b45309' }}>
                      The refund went through, but marking the song refunded failed — set it by hand.
                    </strong></>
                  )}
                  <br />The song is still in the competition and in its heat — withdraw it separately if that is what you want.
                </p>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button className="c-btn" onClick={() => setOpen(false)}>Close</button>
                </div>
              </>
            ) : (
              <>
                {error && <div className="c-alert" style={{ marginBottom: '1rem' }}>{error}</div>}

                {info && (
                  <>
                    <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.875rem', lineHeight: 1.7 }}>
                      <div><span style={{ color: '#6b7280' }}>Contestant:</span> <strong>{info.song.full_name}</strong> ({info.song.email})</div>
                      <div><span style={{ color: '#6b7280' }}>Song:</span> <strong>{info.song.title}</strong></div>
                      <div><span style={{ color: '#6b7280' }}>Paid:</span>{' '}
                        {new Date(info.created).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}
                        {info.card_last4 && <> · {info.card_brand} ····{info.card_last4}</>}
                      </div>
                      <div><span style={{ color: '#6b7280' }}>Payment total:</span> <strong>{money(info.amount_cents, info.currency)}</strong>
                        {info.song_count > 1 && <> — covers {info.song_count} songs</>}
                      </div>
                      {info.refunded_cents > 0 && (
                        <div style={{ color: '#b45309' }}>Already refunded: {money(info.refunded_cents, info.currency)}</div>
                      )}
                    </div>

                    {info.song_count > 1 && (
                      <div style={{ marginBottom: '1rem', fontSize: '0.875rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.35rem' }}>
                          <input type="radio" checked={!whole} onChange={() => setWhole(false)} />{' '}
                          Just this song — {money(info.per_song_cents, info.currency)}
                        </label>
                        <label style={{ display: 'block' }}>
                          <input type="radio" checked={whole} onChange={() => setWhole(true)} />{' '}
                          The whole payment — {money(info.refundable_cents, info.currency)} (all {info.song_count} songs)
                        </label>
                      </div>
                    )}

                    <p style={{ margin: '0 0 1.25rem', fontSize: '0.85rem', color: '#6b7280', lineHeight: 1.5 }}>
                      This moves real money — the account is in live mode. Stripe's processing fee on the original
                      payment is not returned, so the refund costs you that fee. The song stays in the competition
                      and in its heat; only its payment status changes.
                    </p>
                  </>
                )}

                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                  <button className="c-btn c-btn--outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</button>
                  {info && (
                    <button className="c-btn c-btn--danger" onClick={doRefund} disabled={busy || amount <= 0}>
                      {busy ? 'Refunding…' : `Refund ${money(amount, info.currency)}`}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ─── EmailEntrantControl ──────────────────────────────────────────────────────
// Free-text email to the contestant behind one song, for anything the templates
// do not cover. The recipient comes from the server, not from the row, because
// heat rows carry no contestant details — and so an address can never be
// supplied by the browser.
// Also reached from the MC and Stage hand roster rows (`mc` / `stageHand`),
// which are roster entries rather than users; the server resolves those
// addresses from their roster tables in the same way.
function EmailEntrantControl({ song, user, mc, stageHand, label = 'Email Entrant', noun = 'entrant' }) {
  const base = song
    ? `/api/admin/songs/${song.id}`
    : mc
      ? `/api/admin/mcs/${mc.id}`
      : stageHand
        ? `/api/admin/stage-hands/${stageHand.id}`
        : `/api/admin/users/${user.user_id}`;
  const targetName = song ? null : (mc || stageHand || user)?.full_name;

  const [open,    setOpen]    = useState(false);
  const [to,      setTo]      = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy,    setBusy]    = useState(false);
  const [error,   setError]   = useState('');
  const [sent,    setSent]    = useState(null);
  const [subject, setSubject] = useState('');
  const [bodyTxt, setBodyTxt] = useState('');

  async function openDialog() {
    setOpen(true);
    setTo(null); setError(''); setSent(null);
    setSubject(''); setBodyTxt('');
    setLoading(true);
    try {
      const res = await api.get(`${base}/entrant`);
      setTo(res.data);
      setSubject(res.data.song_title ? `FOOM 2026 — ${res.data.song_title}` : 'FOOM 2026');
      setBodyTxt(`Hi ${res.data.first_name},\n\n\n\n— The SCALA FOOM Team`);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load that entrant.');
    } finally {
      setLoading(false);
    }
  }

  async function send() {
    setBusy(true);
    setError('');
    try {
      const res = await api.post(`${base}/email`, {
        subject,
        body: bodyTxt,
      });
      setSent(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'The email did not send.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        className="c-btn c-btn--sm"
        // Same blue family as the Notify buttons — comms actions read as blue —
        // but several shades back, so a one-off email is never mistaken for a
        // heat notification at a glance. Dark navy text keeps it legible on the
        // pale fill, which white would not.
        style={{ background: '#bae6fd', color: '#075985', border: '1px solid #7dd3fc' }}
        onClick={(e) => { e.stopPropagation(); openDialog(); }}
        title={song ? `Email the entrant of "${song.title}"` : `Email ${targetName}`}
      >
        {label}
      </button>

      {open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: '10px', padding: '1.75rem 2rem', maxWidth: '620px', width: '94%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', textAlign: 'left' }}>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1a1a2e', marginBottom: '0.75rem' }}>
              Email {noun}
            </div>

            {loading && <p style={{ color: '#6b7280', margin: '0 0 1.5rem' }}>Loading…</p>}

            {sent ? (
              <>
                <p style={{ margin: '0 0 1.25rem', lineHeight: 1.5, color: '#374151' }}>
                  Sent to <strong>{sent.full_name}</strong> ({sent.email}).
                </p>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button className="c-btn" onClick={() => setOpen(false)}>Close</button>
                </div>
              </>
            ) : (
              <>
                {error && <div className="c-alert" style={{ marginBottom: '1rem' }}>{error}</div>}

                {to && (
                  <>
                    <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.6rem 0.9rem', marginBottom: '1rem', fontSize: '0.83rem', lineHeight: 1.6 }}>
                      <div><span style={{ color: '#6b7280' }}>To:</span> <strong>{to.full_name}</strong> &lt;{to.email}&gt;</div>
                      <div><span style={{ color: '#6b7280' }}>From:</span> {to.from_email}</div>
                      {to.song_title && <div><span style={{ color: '#6b7280' }}>Re:</span> {to.song_title}</div>}
                      {to.cc?.length > 0 && (
                        <div><span style={{ color: '#6b7280' }}>Cc:</span> {to.cc.map((m) => m.email).join(', ')}</div>
                      )}
                    </div>

                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.3rem' }}>Subject</label>
                    <input
                      className="c-input"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      style={{ marginBottom: '0.75rem' }}
                    />

                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.3rem' }}>Message</label>
                    <textarea
                      className="c-textarea"
                      rows={11}
                      value={bodyTxt}
                      onChange={(e) => setBodyTxt(e.target.value)}
                      style={{ fontSize: '0.85rem', width: '100%', boxSizing: 'border-box' }}
                    />
                    <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.3rem 0 1.25rem' }}>
                      Sends immediately — there is no draft or undo. Blank lines become paragraph breaks.
                      The managers above are copied in and listed at the foot of the email, as on every FOOM email.
                    </p>
                  </>
                )}

                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                  <button className="c-btn c-btn--outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</button>
                  {to && (
                    <button
                      className="c-btn"
                      style={{ background: '#0ea5e9' }}
                      onClick={send}
                      disabled={busy || !subject.trim() || !bodyTxt.trim()}
                    >
                      {busy ? 'Sending…' : 'Send Email'}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ─── OverviewPanel ────────────────────────────────────────────────────────────
function StatCard({ label, value, sub }) {
  return (
    <div className="a-stat-card">
      <div className="a-stat-card__value">{value}</div>
      <div className="a-stat-card__label">{label}</div>
      {sub && <div className="a-stat-card__sub">{sub}</div>}
    </div>
  );
}

function countdown(isoDate) {
  if (!isoDate) return { date: 'Not set', sub: null };
  const d       = new Date(isoDate);
  const diffDays = Math.ceil((d - Date.now()) / 86400000);
  const dateStr  = d.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
  if (diffDays < 0)  return { date: dateStr, sub: 'Closed' };
  if (diffDays === 0) return { date: dateStr, sub: 'Today!' };
  return { date: dateStr, sub: `${diffDays} day${diffDays !== 1 ? 's' : ''} remaining` };
}

function OverviewPanel() {
  // ── Stats ──────────────────────────────────────────────────────────────────
  const [stats,        setStats]        = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError,   setStatsError]   = useState('');

  // ── Competition status ────────────────────────────────────────────────────
  const [competitions,         setCompetitions]         = useState(null);
  const [publishStatus,        setPublishStatus]        = useState({ loading: true, allComplete: false, resultsPublished: false, total: 0, judged: 0 });
  const [statusLoading,        setStatusLoading]        = useState(false);
  const [downloadingSnapshot,  setDownloadingSnapshot]  = useState(false);
  const [downloadingAudio,     setDownloadingAudio]     = useState(false);
  const [archiving,            setArchiving]            = useState(false);
  const [previewingPdfs,       setPreviewingPdfs]       = useState(false);
  const [publishing,           setPublishing]           = useState(false);
  const [sendResultsMsg,       setSendResultsMsg]       = useState('');
  const [previewError,         setPreviewError]         = useState('');
  const [compError,            setCompError]            = useState('');
  const [ovConfirm,            setOvConfirm]            = useState(null); // 'finish' | 'publish'
  const [creatingNew,          setCreatingNew]          = useState(false);
  const [newName,              setNewName]              = useState('');
  const [downloading,          setDownloading]          = useState(null); // competition_id

  const loadComp = useCallback(() => {
    api.get('/api/admin/competitions')
      .then((r) => setCompetitions(r.data.competitions))
      .catch(() => setCompError('Failed to load competition status.'));
  }, []);

  useEffect(() => {
    api.get('/api/admin/overview-stats')
      .then((r) => setStats(r.data))
      .catch(() => setStatsError('Failed to load overview stats.'))
      .finally(() => setStatsLoading(false));
    loadComp();
    api.get('/api/admin/publish-readiness')
      .then((r) => setPublishStatus({
        loading: false,
        allComplete:      r.data.all_complete,
        resultsPublished: r.data.results_published,
        total:            r.data.total_assigned_songs,
        judged:           r.data.fully_judged_songs,
      }))
      .catch(() => setPublishStatus((p) => ({ ...p, loading: false })));
  }, [loadComp]);

  const current  = competitions?.find((c) => c.status !== 'archived') ?? null;
  const isBusy   = statusLoading || archiving;

  const STATUS_BADGE = {
    active:         { label: 'Active',         cls: 'badge--active' },
    entries_paused: { label: 'Entries Paused', cls: 'badge--pending' },
    all_paused:     { label: 'All Paused',     cls: 'badge--withdrawn' },
    finished:       { label: 'Finished',       cls: 'badge--winner' },
  };

  async function ovDownloadSnapshot() {
    setDownloadingSnapshot(true);
    try {
      const res = await api.get('/api/admin/competition/snapshot', { responseType: 'blob' });
      const dateStr = new Date().toISOString().slice(0, 10);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `SCALA_Snapshot_${dateStr}.json`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch { alert('Failed to download snapshot.'); }
    finally { setDownloadingSnapshot(false); }
  }

  async function ovChangeStatus(endpoint) {
    setStatusLoading(true);
    setCompError('');
    try { await api.post(endpoint); loadComp(); }
    catch (err) { setCompError(err.response?.data?.error || 'Failed to update status.'); }
    finally { setStatusLoading(false); }
  }

  async function ovArchive() {
    setArchiving(true);
    setCompError('');
    try { await api.post('/api/admin/competition/archive'); loadComp(); }
    catch (err) { setCompError(err.response?.data?.error || 'Failed to archive.'); }
    finally { setArchiving(false); }
  }

  async function ovPreviewPdfs() {
    setPreviewingPdfs(true);
    setPreviewError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/admin/competition/preview-results-pdfs', {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || `Error ${res.status}`); }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = 'FOOM_Results_Preview.pdf'; a.click(); URL.revokeObjectURL(url);
    } catch (err) { setPreviewError(err.message || 'Failed to generate preview.'); }
    finally { setPreviewingPdfs(false); }
  }

  // Publish Results now emails each contestant their results PDF as well as
  // making results visible, so it asks first (13 Sep 2026).
  function ovPublish() { setOvConfirm('publish'); }

  // The send runs in the background on the server (37 PDFs took longer than
  // the proxy's 60-second limit on 13 Sep 2026, and the browser reported a
  // failure while every email went out). The route answers at once and this
  // polls the status until it finishes, reporting progress as it goes.
  async function watchResultsSend() {
    for (;;) {
      await new Promise((r) => setTimeout(r, 3000));
      let s;
      try { s = (await api.get('/api/admin/competition/results-send-status')).data; }
      catch { continue; }
      if (s.running) {
        setSendResultsMsg(`Sending results… ${s.sent + s.failed} of ${s.total} emails so far.`);
        continue;
      }
      let msg = `Scoring closed and results published. ${s.sent} results email${s.sent !== 1 ? 's' : ''} sent.`;
      if (s.skipped > 0) msg += ` ${s.skipped} contestant${s.skipped !== 1 ? 's' : ''} skipped (no judged songs or no email).`;
      if (s.failed > 0) msg += ` ${s.failed} failed: ${(s.errors || []).join('; ')}`;
      setSendResultsMsg(msg);
      return;
    }
  }

  async function ovPublishAndSend() {
    setPublishing(true);
    setSendResultsMsg('');
    setCompError('');
    try {
      await api.post('/api/admin/competition/publish-and-send-results');
      setPublishStatus((p) => ({ ...p, resultsPublished: true }));
      setSendResultsMsg('Scoring closed and results published. Sending the results emails…');
      await watchResultsSend();
    } catch (err) {
      if (err.response?.status === 409) {
        // Already published (or a send is running): nothing was sent by this press.
        setCompError(err.response.data?.error || 'Results are already published.');
        setPublishStatus((p) => ({ ...p, resultsPublished: true }));
        if (err.response.data?.running) await watchResultsSend();
      } else if (!err.response) {
        // No answer at all (proxy timeout, dropped connection). The server may
        // well have carried on, so look at the status rather than crying failure.
        setSendResultsMsg('No reply from the server — checking whether the send is running…');
        setPublishStatus((p) => ({ ...p, resultsPublished: true }));
        await watchResultsSend();
      } else {
        setCompError(err.response?.data?.error || 'Failed to publish results.');
      }
    } finally { setPublishing(false); }
  }

  async function ovDownloadAudioArchive() {
    setDownloadingAudio(true);
    setCompError('');
    try {
      const res = await api.get('/api/admin/competition/audio-archive', { responseType: 'blob', timeout: 0 });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `FOOM_${current?.year || ''}_Audio_Archive.zip`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (err) { setCompError(err.response?.data?.error || 'Failed to download the audio archive.'); }
    finally { setDownloadingAudio(false); }
  }

  async function ovUnpublish() {
    setPublishing(true);
    try {
      await api.put('/api/admin/settings', { results_published: false });
      setPublishStatus((p) => ({ ...p, resultsPublished: false }));
    } catch (err) { alert(err.response?.data?.error || 'Failed to unpublish results.'); }
    finally { setPublishing(false); }
  }


  async function handleNewComp() {
    const name = newName.trim() || `SCALA Song Competition ${new Date().getFullYear() + 1}`;
    if (!window.confirm(`Create new competition "${name}"?`)) return;
    setCreatingNew(true); setCompError('');
    try { await api.post('/api/admin/competition/new', { name }); setNewName(''); loadComp(); }
    catch (err) { setCompError(err.response?.data?.error || 'Failed to create competition.'); }
    finally { setCreatingNew(false); }
  }

  async function downloadExport(competitionId, year) {
    setDownloading(competitionId);
    try {
      const res = await api.get(`/api/admin/competition/${competitionId}/export`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `SCALA_Competition_${year}_archive.json`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch { alert('Failed to download archive.'); }
    finally { setDownloading(null); }
  }

  const archived = competitions?.filter((c) => c.status === 'archived') || [];
  const sub = stats ? countdown(stats.submission_deadline) : { date: '—', sub: null };
  const jud = stats ? countdown(stats.judging_deadline)    : { date: '—', sub: null };

  return (
    <div>
      {ovConfirm === 'finish' && (
        <ConfirmModal
          message="Finish this competition? This is irreversible — no further status changes will be possible."
          confirmLabel="Finish Competition"
          onConfirm={() => { setOvConfirm(null); ovChangeStatus('/api/admin/competition/finish'); }}
          onCancel={() => setOvConfirm(null)}
        />
      )}
      {ovConfirm === 'publish' && (
        <ConfirmModal
          message="This will close scoring (judges can no longer submit), make results visible on the contestant dashboards, AND email every contestant with judged songs their own results PDF using the Results Email template. Contestants with only FOOM Live entries have no scoresheet and are not emailed. Each press sends the emails again, so do this once. Continue?"
          confirmLabel="Publish & Send Results"
          onConfirm={() => { setOvConfirm(null); ovPublishAndSend(); }}
          onCancel={() => setOvConfirm(null)}
        />
      )}

      {/* ── Competition Status ── */}
      <div className="a-section" style={{ marginBottom: '1.5rem' }}>
        <div className="a-section__header">
          <div>
            <div className="a-section__title">Competition Status</div>
            <div className="a-section__desc">Lifecycle controls for the current competition.</div>
          </div>
          {current && (() => {
            const b = STATUS_BADGE[current.status] || { label: current.status, cls: 'badge--pending' };
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span className={`badge ${b.cls}`}>{b.label}</span>
                <strong style={{ fontSize: '0.95rem', color: '#1a1a2e' }}>{current.name}</strong>
              </div>
            );
          })()}
        </div>

        {compError && (
          <div className="c-alert" style={{ marginBottom: '0.75rem', display: 'flex', justifyContent: 'space-between' }}>
            {compError}
            <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }} onClick={() => setCompError('')}>✕</button>
          </div>
        )}
        {previewError && (
          <div className="c-alert" style={{ marginBottom: '0.75rem' }}>Preview failed: {previewError}</div>
        )}
        {sendResultsMsg && (
          <div className="c-alert c-alert--success" style={{ marginBottom: '0.75rem' }}>{sendResultsMsg}</div>
        )}

        {!current && competitions !== null && (
          <div style={{ padding: '1.25rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', marginBottom: '1rem' }}>
            <div className="a-section__title" style={{ marginBottom: '0.75rem' }}>Start New Competition</div>
            {compError && <div className="c-alert" style={{ marginBottom: '0.75rem' }}>{compError}</div>}
            <div className="c-form-group" style={{ maxWidth: '420px', marginBottom: '0.75rem' }}>
              <label className="c-label" htmlFor="ov-new-comp-name">Competition Name</label>
              <input
                id="ov-new-comp-name"
                className="c-input"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder={`SCALA Song Competition ${new Date().getFullYear() + 1}`}
                onKeyDown={(e) => { if (e.key === 'Enter') handleNewComp(); }}
              />
            </div>
            <button className="c-btn" style={{ background: '#16a34a' }} onClick={handleNewComp} disabled={creatingNew}>
              {creatingNew ? 'Creating…' : 'Start New Competition'}
            </button>
          </div>
        )}

        {current && current.status !== 'finished' && (
          <>
            {/* Row 1 — lifecycle buttons */}
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.5rem' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <button className="c-btn c-btn--sm c-btn--outline"
                  title="Save a full JSON export of all competition data at this point in time"
                  onClick={ovDownloadSnapshot} disabled={downloadingSnapshot}>
                  {downloadingSnapshot ? 'Downloading…' : '⬇ Download Snapshot'}
                </button>
                <Tooltip text="Save a full JSON export of all competition data at this point in time. Names each song's audio file but does not contain the audio — use Download Audio Archive for that." />
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <button className="c-btn c-btn--sm c-btn--outline"
                  onClick={ovDownloadAudioArchive} disabled={downloadingAudio}>
                  {downloadingAudio ? 'Preparing…' : '⬇ Download Audio Archive'}
                </button>
                <Tooltip text="One zip of every audio file in this competition, named by contestant and title, with a manifest.csv. Large — hundreds of MB — so give it a minute." />
              </span>

              {current.status === 'active' && (
                <>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    <button className="c-btn c-btn--sm" style={{ background: '#d97706' }}
                      onClick={() => ovChangeStatus('/api/admin/competition/pause-entries')} disabled={isBusy}>
                      Pause Entries
                    </button>
                    <Tooltip text="Stop new song submissions while judging continues" />
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    <button className="c-btn c-btn--sm" style={{ background: '#ea580c' }}
                      onClick={() => ovChangeStatus('/api/admin/competition/pause-all')} disabled={isBusy}>
                      Pause All
                    </button>
                    <Tooltip text="Suspend both submissions and judging temporarily" />
                  </span>
                </>
              )}

              {(current.status === 'entries_paused' || current.status === 'all_paused') && (
                <button className="c-btn c-btn--sm" style={{ background: '#16a34a' }}
                  onClick={() => ovChangeStatus('/api/admin/competition/resume')} disabled={isBusy}>
                  Resume
                </button>
              )}

              {current.status === 'entries_paused' && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <button className="c-btn c-btn--sm" style={{ background: '#ea580c' }}
                    onClick={() => ovChangeStatus('/api/admin/competition/pause-all')} disabled={isBusy}>
                    Pause All
                  </button>
                  <Tooltip text="Suspend both submissions and judging temporarily" />
                </span>
              )}

              {/* "Send FOOM Heat Invitations" and "Send Heat Reminder" were
                  removed from here on 13 Sep 2026: heats are allocated from
                  the Live Heats tab now, so contestants no longer choose a
                  preferred heat and those emails have nothing to invite them
                  to. The server routes and templates remain. */}

              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <button className="c-btn c-btn--sm c-btn--danger"
                  onClick={() => setOvConfirm('finish')} disabled={isBusy}>
                  Finish Competition
                </button>
                <Tooltip text="Mark the competition as finished. This cannot be undone." />
              </span>

              {isBusy && <span className="c-spinner c-spinner--sm" aria-hidden="true" />}
            </div>

            {/* Row 2 — results actions */}
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <button className="c-btn c-btn--sm" style={{ background: '#1a1a2e' }}
                  onClick={ovPreviewPdfs} disabled={previewingPdfs}>
                  {previewingPdfs ? 'Generating…' : 'Preview Results PDFs'}
                </button>
                <Tooltip text="Download one PDF containing the results pages of every contestant with judged songs — exactly what Publish Results will email, one contestant per email" />
              </span>

              {/* One button (13 Sep 2026). "Publish Results" and "Close & Send
                  Results" did the same send; the only difference was that
                  Close & Send also closed scoring and was locked until Finish
                  Competition. Two buttons for one send invited a double email
                  to every contestant, so this one closes scoring, publishes
                  and emails, as soon as all judging is complete. */}
              {publishStatus.resultsPublished ? (
                <>
                  <span className="badge badge--active" style={{ fontSize: '0.8rem', padding: '0.3rem 0.7rem' }}>✓ Results Published</span>
                  <button className="c-btn c-btn--sm c-btn--outline" onClick={ovUnpublish} disabled={publishing}>
                    Unpublish
                  </button>
                  <Tooltip text="Hide results from the contestant dashboards again. Emails already sent are not recalled." />
                </>
              ) : (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <button
                    className="c-btn c-btn--sm"
                    style={{ background: publishStatus.allComplete && publishStatus.total > 0 ? '#16a34a' : '#9ca3af', cursor: publishStatus.allComplete && publishStatus.total > 0 ? 'pointer' : 'not-allowed' }}
                    onClick={publishStatus.allComplete && publishStatus.total > 0 ? ovPublish : undefined}
                    disabled={!publishStatus.allComplete || publishStatus.total === 0 || publishing}
                    title={publishStatus.total === 0 ? 'No songs assigned to judges yet' : !publishStatus.allComplete ? `${publishStatus.judged}/${publishStatus.total} songs fully judged` : 'All judging complete — ready to publish'}
                  >
                    {publishing ? 'Sending…' : (
                      <>Publish &amp; Send Results{publishStatus.total > 0 && <span style={{ marginLeft: '0.4rem', background: 'rgba(255,255,255,0.25)', borderRadius: '4px', padding: '0 0.35rem', fontSize: '0.72rem' }}>{publishStatus.judged}/{publishStatus.total}</span>}</>
                    )}
                  </button>
                  <Tooltip text="Close scoring, make results visible on the contestant dashboards, and email each contestant with judged songs their own results PDF (Results Email template)" />
                </span>
              )}
            </div>
          </>
        )}

        {current && current.status === 'finished' && (
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <button className="c-btn c-btn--sm c-btn--outline" onClick={ovDownloadSnapshot} disabled={downloadingSnapshot}>
                {downloadingSnapshot ? 'Downloading…' : '⬇ Download Snapshot'}
              </button>
              <Tooltip tooltipKey="admin_snapshot" />
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <button className="c-btn c-btn--sm c-btn--outline" onClick={ovDownloadAudioArchive} disabled={downloadingAudio}>
                {downloadingAudio ? 'Preparing…' : '⬇ Download Audio Archive'}
              </button>
              <Tooltip text="One zip of every audio file in this competition, named by contestant and title, with a manifest.csv. Large — hundreds of MB — so give it a minute." />
            </span>

            <button className="c-btn c-btn--sm c-btn--outline" onClick={ovArchive} disabled={archiving}>
              {archiving ? 'Archiving…' : 'Archive to History'}
            </button>

            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <button className="c-btn c-btn--sm" style={{ background: '#1a1a2e' }}
                onClick={ovPreviewPdfs} disabled={previewingPdfs}>
                {previewingPdfs ? 'Generating…' : 'Preview Results PDFs'}
              </button>
              <Tooltip text="Generate a sample results PDF for up to 2 fully judged contestants" />
            </span>

            {publishStatus.resultsPublished ? (
              <>
                <span className="badge badge--active" style={{ fontSize: '0.8rem', padding: '0.3rem 0.7rem' }}>✓ Results Published</span>
                <button className="c-btn c-btn--sm c-btn--outline" onClick={ovUnpublish} disabled={publishing}>
                  Unpublish
                </button>
                <Tooltip text="Hide results from the contestant dashboards again. Emails already sent are not recalled." />
              </>
            ) : (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <button className="c-btn c-btn--sm" style={{ background: '#16a34a' }}
                  onClick={ovPublish} disabled={publishing}>
                  {publishing ? 'Sending…' : 'Publish & Send Results'}
                </button>
                <Tooltip text="Close scoring, make results visible on the contestant dashboards, and email each contestant with judged songs their own results PDF (Results Email template)" />
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Entries ── */}
      {statsLoading && <div className="c-loading"><div className="c-spinner" /></div>}
      {statsError   && <div className="c-alert">{statsError}</div>}
      {stats && (
        <>
          <div className="a-section">
            <div className="a-section__header">
              <div>
                <div className="a-section__title">Entries</div>
                <div className="a-section__desc">Song submission and payment overview.</div>
              </div>
            </div>
            <div className="a-stat-grid">
              <StatCard label="Total songs"      value={stats.total_songs} />
              <StatCard label="Paid songs"       value={stats.paid_songs} />
              <StatCard label="Pending payment"  value={stats.pending_songs} />
              <StatCard label="Total revenue"    value={`$${(stats.total_revenue_cents / 100).toFixed(2)}`} />
            </div>
          </div>

          <div className="a-section">
            <div className="a-section__header">
              <div>
                <div className="a-section__title">Judging Progress</div>
                <div className="a-section__desc">Evaluation completion across all assigned songs.</div>
              </div>
            </div>
            <div className="a-stat-grid">
              <StatCard label="Fully judged"     value={stats.fully_judged} />
              <StatCard label="Partially judged" value={stats.partially_judged} />
              <StatCard label="Not yet started"  value={stats.not_started} />
              <StatCard label="Active judges"    value={stats.active_judges} />
              <StatCard
                label="Average score"
                value={stats.avg_score != null ? String(stats.avg_score) : '—'}
                sub={stats.avg_score != null ? 'out of 5' : null}
              />
            </div>
          </div>

          <div className="a-section">
            <div className="a-section__header">
              <div>
                <div className="a-section__title">Deadlines</div>
                <div className="a-section__desc">Configure deadline dates in the Settings tab.</div>
              </div>
            </div>
            <div className="a-stat-grid">
              <StatCard label="Submission deadline" value={sub.date} sub={sub.sub} />
              <StatCard label="Judging deadline"    value={jud.date} sub={jud.sub} />
            </div>
          </div>
        </>
      )}

      {/* ── Past Competitions ── */}
      {archived.length > 0 && (
        <div className="a-section">
          <div className="a-section__header">
            <div>
              <div className="a-section__title">Past Competitions</div>
            </div>
          </div>
          <div className="a-table-wrap">
            <table className="a-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Year</th>
                  <th>Archived Date</th>
                  <th>Songs</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {archived.map((c) => (
                  <tr key={c.competition_id}>
                    <td>{c.name}</td>
                    <td>{c.year}</td>
                    <td>{c.archived_at ? new Date(c.archived_at).toLocaleDateString() : '—'}</td>
                    <td>{c.song_count}</td>
                    <td>
                      <button
                        className="c-btn c-btn--sm c-btn--outline"
                        onClick={() => downloadExport(c.competition_id, c.year)}
                        disabled={downloading === c.competition_id}
                      >
                        {downloading === c.competition_id ? 'Downloading…' : 'Download Archive'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── CompetitionPanel ─────────────────────────────────────────────────────────
// ── ConfirmModal ─────────────────────────────────────────────────────────────
function ConfirmModal({ message, confirmLabel, onConfirm, onCancel }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        background: '#fff', borderRadius: '10px', padding: '1.75rem 2rem',
        maxWidth: '420px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      }}>
        <p style={{ margin: '0 0 1.5rem', lineHeight: 1.5, color: '#1a1a2e' }}>{message}</p>
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
          <button className="c-btn c-btn--outline" onClick={onCancel}>Cancel</button>
          <button className="c-btn c-btn--danger" onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}


// ─── EmailsPanel ─────────────────────────────────────────────────────────────
/**
 * Manage the addresses that receive heat-email test sends.
 * One shared list across all the heat templates.
 */
function TestRecipientsManager({ recipients, onAdd, onRemove, error }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy]   = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const ok = await onAdd(email.trim());
      if (ok) setEmail('');
    } finally {
      setBusy(false);
    }
  }

  // Light yellow (#fffbeb) matches the Grand Final panel, marking this out as a
  // control that applies to the whole page rather than to one template.
  return (
    <div style={{ border: '1px solid #fde68a', borderRadius: 8, padding: '1rem', marginBottom: '1.75rem', background: '#fffbeb' }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#92400e', marginBottom: '0.4rem' }}>
        Test email recipients
      </div>
      <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: '0 0 0.75rem' }}>
        These addresses receive every <strong>Test</strong> send on this page. For heat
        emails the heat name, date, arrival time, venue and calendar links appear exactly
        as they will in the real email; per-person fields such as{' '}
        <code>{'{{firstname}}'}</code> stay as placeholders. Other emails have no heat
        context, so all their placeholders stay visible.
      </p>

      {error && <div className="c-alert" style={{ marginBottom: '0.6rem' }}>{error}</div>}

      <form onSubmit={submit} style={{ display: 'flex', gap: '0.5rem', marginBottom: recipients.length ? '0.75rem' : 0, flexWrap: 'wrap' }}>
        <input
          className="c-input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@example.com"
          style={{ maxWidth: 280 }}
          aria-label="Test recipient email address"
        />
        <button type="submit" className="c-btn c-btn--sm" disabled={busy || !email.trim()}>
          {busy ? 'Adding…' : '+ Add'}
        </button>
      </form>

      {recipients.length === 0 ? (
        <p style={{ fontSize: '0.8rem', color: '#9ca3af', margin: 0 }}>
          No test recipients yet — add one before sending a test.
        </p>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
          {recipients.map((r) => (
            <span key={r.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: '#fff', border: '1px solid #d1d5db', borderRadius: 999, padding: '0.2rem 0.5rem 0.2rem 0.7rem', fontSize: '0.82rem' }}>
              {r.email}
              <button
                type="button"
                onClick={() => onRemove(r.id)}
                title={`Remove ${r.email}`}
                aria-label={`Remove ${r.email}`}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#b91c1c', fontWeight: 700, lineHeight: 1, padding: '0 0.1rem' }}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Collapsible category heading on the Emails tab — one per audience. Click
 * anywhere on the bar to fold the group away.
 */
function EmailGroup({ title, count, open, onToggle, children }) {
  return (
    <div style={{ marginBottom: open ? '2.5rem' : '0.6rem' }}>
      <div
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer', userSelect: 'none',
          background: '#000', color: '#fff', padding: '0.6rem 1rem', borderRadius: '6px',
          marginBottom: open ? '1.5rem' : 0,
        }}
      >
        <span className="a-chevron" style={{ transform: open ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}>▶</span>
        <span style={{ fontSize: '1.02rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {title}
        </span>
        <span style={{ fontSize: '0.8rem', opacity: 0.7, marginLeft: 'auto' }}>
          {count} email{count === 1 ? '' : 's'}
        </span>
      </div>
      {open && children}
    </div>
  );
}

/**
 * "Test" button + heat selector, shown beside a heat template's Save button.
 * The heat choice matters: the point of the test is to see this heat's real
 * number, date, time and venue rendered.
 */
function EmailTestButton({ templateKey, heats = [], recipientCount, needsHeat = false }) {
  const [heatId, setHeatId] = useState('');
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState('');

  // Default to the next upcoming heat, else the first in the list.
  useEffect(() => {
    if (!needsHeat || heatId || heats.length === 0) return;
    const today = new Date().toISOString().slice(0, 10);
    const upcoming = heats.find((h) => h.heat_date && String(h.heat_date).slice(0, 10) >= today);
    setHeatId(String((upcoming || heats[0]).heat_id));
  }, [needsHeat, heats, heatId]);

  async function send() {
    setSending(true);
    setMsg('');
    try {
      // Generate the same running sheet the real notify buttons send, so a
      // test shows the attachment rather than silently omitting it.
      let pdfBase64;
      if (needsHeat) {
        const heat = heats.find((h) => String(h.heat_id) === String(heatId));
        if (heat) pdfBase64 = getRunningSheetPdfBase64(heat);
      }
      const res = await api.post('/api/admin/emails/test-send', {
        template: templateKey,
        ...(needsHeat ? { heat_id: heatId, pdfBase64 } : {}),
      });
      const { sent, failed } = res.data;
      setMsg(failed ? `Sent ${sent}, ${failed} failed` : `Test sent to ${sent} recipient${sent === 1 ? '' : 's'} ✓`);
    } catch (err) {
      setMsg(err.response?.data?.error || 'Failed to send test.');
    } finally {
      setSending(false);
    }
  }

  const disabled = sending || recipientCount === 0 || (needsHeat && !heatId);

  return (
    <>
      {needsHeat && (
        <select
          className="c-select"
          value={heatId}
          onChange={(e) => { setHeatId(e.target.value); setMsg(''); }}
          style={{ width: 'auto', fontSize: '0.82rem' }}
          aria-label="Heat to use for the test email"
        >
          {heats.map((h) => (
            <option key={h.heat_id} value={h.heat_id}>{h.heat_name}</option>
          ))}
        </select>
      )}
      <button
        type="button"
        className="c-btn c-btn--sm c-btn--outline"
        onClick={send}
        disabled={disabled}
        title={recipientCount === 0
          ? 'Add a test recipient first'
          : needsHeat
            ? 'Send this email to the test recipients, using the selected heat’s real details'
            : 'Send this email to the test recipients (placeholders are left visible)'}
      >
        {sending ? 'Sending…' : 'Test'}
      </button>
      {msg && <span style={{ fontSize: '0.75rem', color: msg.includes('✓') ? '#16a34a' : '#b91c1c' }}>{msg}</span>}
    </>
  );
}

function EmailsPanel() {
  const [loading,   setLoading]   = useState(true);
  const [loadError, setLoadError] = useState('');

  // Heat-email test sends: shared recipient list + the heats to test against
  const [testRecipients, setTestRecipients] = useState([]);
  const [testRecipError,  setTestRecipError] = useState('');
  const [testHeats,       setTestHeats]      = useState([]);

  // Collapsible groups. The page is long enough that the template you want is
  // usually off-screen; collapsing the groups you are not working on makes it
  // navigable.
  // Each group remembers its own state across refreshes (usePersistentCollapse),
  // composed back into the { key: bool } shape the EmailGroup rows read.
  const [judgesGroupOpen,      setJudgesGroupOpen]      = usePersistentCollapse('emails:judges');
  const [contestantsGroupOpen, setContestantsGroupOpen] = usePersistentCollapse('emails:contestants');
  const [mcsGroupOpen,         setMcsGroupOpen]         = usePersistentCollapse('emails:mcs');
  const [accountGroupOpen,     setAccountGroupOpen]     = usePersistentCollapse('emails:account');
  const openGroups = {
    judges: judgesGroupOpen, contestants: contestantsGroupOpen, mcs: mcsGroupOpen, account: accountGroupOpen,
  };
  const groupSetters = {
    judges: setJudgesGroupOpen, contestants: setContestantsGroupOpen, mcs: setMcsGroupOpen, account: setAccountGroupOpen,
  };
  const toggleGroup = (key) => groupSetters[key]?.((v) => !v);

  const loadTestRecipients = useCallback(async () => {
    try {
      const res = await api.get('/api/admin/test-recipients');
      setTestRecipients(res.data.recipients || []);
    } catch {
      setTestRecipError('Failed to load test recipients.');
    }
  }, []);

  useEffect(() => { loadTestRecipients(); }, [loadTestRecipients]);

  useEffect(() => {
    // The active competition's heats, not the calendar year's.
    loadCompetition()
      .then((s) => (s.ok && s.competition ? s.competition.year : new Date().getFullYear()))
      .then((y) => api.get(`/api/admin/heats?year=${y}`))
      .then((r) => setTestHeats(r.data.heats || []))
      .catch(() => {});
  }, []);

  async function addTestRecipient(email) {
    setTestRecipError('');
    try {
      await api.post('/api/admin/test-recipients', { email });
      await loadTestRecipients();
      return true;
    } catch (err) {
      setTestRecipError(err.response?.data?.error || 'Failed to add that address.');
      return false;
    }
  }

  async function removeTestRecipient(id) {
    setTestRecipError('');
    try {
      await api.delete(`/api/admin/test-recipients/${id}`);
      await loadTestRecipients();
    } catch (err) {
      setTestRecipError(err.response?.data?.error || 'Failed to remove that address.');
    }
  }

  // Judge Welcome Email
  const [welcomeSubject,      setWelcomeSubject]      = useState('');
  const [welcomeBody,         setWelcomeBody]         = useState('');
  const [welcomeSubjectDraft, setWelcomeSubjectDraft] = useState('');
  const [welcomeBodyDraft,    setWelcomeBodyDraft]    = useState('');
  const [welcomeSaving,       setWelcomeSaving]       = useState(false);
  const [welcomeSaved,        setWelcomeSaved]        = useState(false);
  const [welcomeError,        setWelcomeError]        = useState('');

  // Password Reset Email
  const [resetSubject,      setResetSubject]      = useState('');
  const [resetBody,         setResetBody]         = useState('');
  const [resetSubjectDraft, setResetSubjectDraft] = useState('');
  const [resetBodyDraft,    setResetBodyDraft]    = useState('');
  const [resetSaving,       setResetSaving]       = useState(false);
  const [resetSaved,        setResetSaved]        = useState(false);
  const [resetError,        setResetError]        = useState('');

  // Judge Assignment Email
  const [assignSubject,      setAssignSubject]      = useState('');
  const [assignBody,         setAssignBody]         = useState('');
  const [assignSubjectDraft, setAssignSubjectDraft] = useState('');
  const [assignBodyDraft,    setAssignBodyDraft]    = useState('');
  const [assignSaving,       setAssignSaving]       = useState(false);
  const [assignSaved,        setAssignSaved]        = useState(false);
  const [assignError,        setAssignError]        = useState('');

  // Results Email
  const [resultsBody,      setResultsBody]      = useState('');
  const [resultsBodyDraft, setResultsBodyDraft] = useState('');
  const [resultsSaving,    setResultsSaving]    = useState(false);
  const [resultsSaved,     setResultsSaved]     = useState(false);
  const [resultsError,     setResultsError]     = useState('');

  // FOOM Heat Invite Email
  const [inviteSubject,      setInviteSubject]      = useState('');
  const [inviteBody,         setInviteBody]         = useState('');
  const [inviteSubjectDraft, setInviteSubjectDraft] = useState('');
  const [inviteBodyDraft,    setInviteBodyDraft]    = useState('');
  const [inviteSaving,       setInviteSaving]       = useState(false);
  const [inviteSaved,        setInviteSaved]        = useState(false);
  const [inviteError,        setInviteError]        = useState('');

  // FOOM Heat Reminder Email
  const [reminderSubject,      setReminderSubject]      = useState('');
  const [reminderBody,         setReminderBody]         = useState('');
  const [reminderSubjectDraft, setReminderSubjectDraft] = useState('');
  const [reminderBodyDraft,    setReminderBodyDraft]    = useState('');
  const [reminderSaving,       setReminderSaving]       = useState(false);
  const [reminderSaved,        setReminderSaved]        = useState(false);
  const [reminderError,        setReminderError]        = useState('');

  // FOOM Heat Confirmation Email
  const [confirmSubject,      setConfirmSubject]      = useState('');
  const [confirmBody,         setConfirmBody]         = useState('');
  const [confirmSubjectDraft, setConfirmSubjectDraft] = useState('');
  const [confirmBodyDraft,    setConfirmBodyDraft]    = useState('');
  const [confirmSaving,       setConfirmSaving]       = useState(false);
  const [confirmSaved,        setConfirmSaved]        = useState(false);
  const [confirmError,        setConfirmError]        = useState('');

  // Heat Notify Judge Email
  const [hnjSubject,      setHnjSubject]      = useState('');
  const [hnjBody,         setHnjBody]         = useState('');
  const [hnjSubjectDraft, setHnjSubjectDraft] = useState('');
  const [hnjBodyDraft,    setHnjBodyDraft]    = useState('');
  const [hnjSaving,       setHnjSaving]       = useState(false);
  const [hnjSaved,        setHnjSaved]        = useState(false);
  const [hnjError,        setHnjError]        = useState('');

  // Heat Notify MC Email
  const [hnmcSubject,      setHnmcSubject]      = useState('');
  const [hnmcBody,         setHnmcBody]         = useState('');
  const [hnmcSubjectDraft, setHnmcSubjectDraft] = useState('');
  const [hnmcBodyDraft,    setHnmcBodyDraft]    = useState('');
  const [hnmcSaving,       setHnmcSaving]       = useState(false);
  const [hnmcSaved,        setHnmcSaved]        = useState(false);
  const [hnmcError,        setHnmcError]        = useState('');

  // Heat Notify Stage Hand Email (migration 046)
  const [hnshSubject,      setHnshSubject]      = useState('');
  const [hnshBody,         setHnshBody]         = useState('');
  const [hnshSubjectDraft, setHnshSubjectDraft] = useState('');
  const [hnshBodyDraft,    setHnshBodyDraft]    = useState('');
  const [hnshSaving,       setHnshSaving]       = useState(false);
  const [hnshSaved,        setHnshSaved]        = useState(false);
  const [hnshError,        setHnshError]        = useState('');

  // Heat Notify Performer Email
  const [hnpSubject,      setHnpSubject]      = useState('');
  const [hnpBody,         setHnpBody]         = useState('');
  const [hnpSubjectDraft, setHnpSubjectDraft] = useState('');
  const [hnpBodyDraft,    setHnpBodyDraft]    = useState('');
  const [hnpSaving,       setHnpSaving]       = useState(false);
  const [hnpSaved,        setHnpSaved]        = useState(false);
  const [hnpError,        setHnpError]        = useState('');

  // Heat Reminder Judge Email
  const [hrjSubject,      setHrjSubject]      = useState('');
  const [hrjBody,         setHrjBody]         = useState('');
  const [hrjSubjectDraft, setHrjSubjectDraft] = useState('');
  const [hrjBodyDraft,    setHrjBodyDraft]    = useState('');
  const [hrjSaving,       setHrjSaving]       = useState(false);
  const [hrjSaved,        setHrjSaved]        = useState(false);
  const [hrjError,        setHrjError]        = useState('');

  useEffect(() => {
    api.get('/api/admin/settings')
      .then((res) => {
        const s = res.data.settings || {};
        setWelcomeSubject(s.judge_welcome_subject || '');
        setWelcomeBody(s.judge_welcome_body || '');
        setWelcomeSubjectDraft(s.judge_welcome_subject || '');
        setWelcomeBodyDraft(s.judge_welcome_body || '');
        setResetSubject(s.reset_password_subject || '');
        setResetBody(s.reset_password_body || '');
        setResetSubjectDraft(s.reset_password_subject || '');
        setResetBodyDraft(s.reset_password_body || '');
        setAssignSubject(s.judge_assignment_subject || '');
        setAssignBody(s.judge_assignment_body || '');
        setAssignSubjectDraft(s.judge_assignment_subject || '');
        setAssignBodyDraft(s.judge_assignment_body || '');
        setResultsBody(s.results_email_body || '');
        setResultsBodyDraft(s.results_email_body || '');

        const invSubj = s.foom_heat_invite_subject || 'FOOM Heats are now open!';
        const invBody = s.foom_heat_invite_body    ||
          `Hi {{firstname}},\n\nFOOM Heats are now open!\n\nPlease use the link below to log on to SCALA's FOOM site. Then choose which Heat you would like to perform in.\n\n{{link}}\n\nPlease do this as soon as possible. The earlier you register your preference the more likely you are to get into the Heat you want.\n\nThank you for participating in FOOM 2026.\n\n— The SCALA FOOM Team`;
        setInviteSubject(invSubj); setInviteSubjectDraft(invSubj);
        setInviteBody(invBody);    setInviteBodyDraft(invBody);

        const remSubj = s.foom_heat_reminder_subject || 'Reminder — Please Choose Your FOOM 2026 Heat';
        const remBody = s.foom_heat_reminder_body    ||
          `Hi {{firstname}},\n\nThis is your last reminder to choose your preferred FOOM 2026 heat.\n\nYou haven't yet indicated which heat you'd like to perform in. Heat spots are filling up — please log in now and register your preference before it's too late.\n\n{{link}}\n\nThank you for participating in FOOM 2026.\n\n— The SCALA FOOM Team`;
        setReminderSubject(remSubj); setReminderSubjectDraft(remSubj);
        setReminderBody(remBody);    setReminderBodyDraft(remBody);

        const conSubj = s.foom_heat_confirm_subject || 'Your FOOM 2026 Heat Preference is Confirmed';
        const conBody = s.foom_heat_confirm_body    ||
          `Hi {{firstname}},\n\nThank you for registering your heat preference!\n\nYour song {{song_title}} has been registered for {{heat_name}} on {{heat_date}} at {{venue_name}}. Please make sure you arrive by {{arrival_time}}.\n\nPlease note that final heat placements are confirmed by the FOOM organisers. We'll be in touch if anything changes.\n\nThank you for participating in FOOM 2026.\n\n— The SCALA FOOM Team`;
        setConfirmSubject(conSubj); setConfirmSubjectDraft(conSubj);
        setConfirmBody(conBody);    setConfirmBodyDraft(conBody);

        const hnjSubj = s.heat_notify_judge_subject || 'SCALA FOOM — Your Heat Assignment';
        const hnjBod  = s.heat_notify_judge_body    ||
          `Hi {{firstname}},\n\nYou have been assigned to judge a FOOM Live heat.\n\nHeat: {{heat_name}}\nDate: {{heat_date}}\nPerformer Arrival Time: {{arrival_time}}\nVenue: {{venue_name}}\n\nPlease make sure you arrive on time. If you have any questions, please get in touch.\n\n— The SCALA FOOM Team`;
        setHnjSubject(hnjSubj); setHnjSubjectDraft(hnjSubj);
        setHnjBody(hnjBod);     setHnjBodyDraft(hnjBod);

        const hnmcSubj = s.heat_notify_mc_subject || 'SCALA FOOM — You are MC for this heat';
        const hnmcBod  = s.heat_notify_mc_body    ||
          `Hi {{firstname}},\n\nThank you for agreeing to MC a FOOM Live heat. Here are the details:\n\nHeat: {{heat_name}}\nDate: {{heat_date}}\nArrival Time: {{arrival_time}}\nVenue: {{venue_name}}\nAddress: {{venue_address}}\n\nThe running sheet is attached.\n\n— The SCALA FOOM Team`;
        setHnmcSubject(hnmcSubj); setHnmcSubjectDraft(hnmcSubj);
        setHnmcBody(hnmcBod);     setHnmcBodyDraft(hnmcBod);

        const hnshSubj = s.heat_notify_stage_hand_subject || 'SCALA FOOM — You are stage hand for this heat';
        const hnshBod  = s.heat_notify_stage_hand_body    ||
          `Hi {{firstname}},\n\nThank you for agreeing to be the stage hand for a FOOM Live heat. Here are the details:\n\nHeat: {{heat_name}}\nDate: {{heat_date}}\nArrival Time: {{arrival_time}}\nVenue: {{venue_name}}\nAddress: {{venue_address}}\n\nThe running sheet and the technical requirements sheet are attached.\n\nPlease confirm you can be stage hand for this heat:\n{{confirmLink}}\n\n— The SCALA FOOM Team`;
        setHnshSubject(hnshSubj); setHnshSubjectDraft(hnshSubj);
        setHnshBody(hnshBod);     setHnshBodyDraft(hnshBod);

        const hnpSubj = s.heat_notify_performer_subject || 'SCALA FOOM — Your Heat Details';
        const hnpBod  = s.heat_notify_performer_body    ||
          `Hi {{firstname}},\n\nYou are performing in a FOOM Live heat.\n\nHeat: {{heat_name}}\nDate: {{heat_date}}\nPerformer Arrival Time: {{arrival_time}}\nVenue: {{venue_name}}\n\nPlease make sure you arrive by the performer arrival time. If you have any questions, please get in touch.\n\n— The SCALA FOOM Team`;
        setHnpSubject(hnpSubj); setHnpSubjectDraft(hnpSubj);
        setHnpBody(hnpBod);     setHnpBodyDraft(hnpBod);

        const hrjSubj = s.heat_reminder_judge_subject || 'SCALA FOOM — Heat Reminder';
        const hrjBod  = s.heat_reminder_judge_body    ||
          `Hi {{firstname}},\n\nThis is a reminder that you are judging a FOOM Live heat in one week.\n\nHeat: {{heat_name}}\nDate: {{heat_date}}\nPerformer Arrival Time: {{arrival_time}}\nVenue: {{venue_name}}\n\nPlease make sure you arrive on time. If you have any questions, please get in touch.\n\n— The SCALA FOOM Team`;
        setHrjSubject(hrjSubj); setHrjSubjectDraft(hrjSubj);
        setHrjBody(hrjBod);     setHrjBodyDraft(hrjBod);
      })
      .catch(() => setLoadError('Failed to load email settings.'))
      .finally(() => setLoading(false));
  }, []);

  async function handleSaveWelcomeEmail() {
    if (!welcomeBodyDraft.includes('{{link}}')) {
      setWelcomeError('The body must contain {{link}} so judges receive their setup link.');
      return;
    }
    setWelcomeSaving(true); setWelcomeError(''); setWelcomeSaved(false);
    try {
      await api.put('/api/admin/settings', {
        judge_welcome_subject: welcomeSubjectDraft,
        judge_welcome_body:    welcomeBodyDraft,
      });
      setWelcomeSubject(welcomeSubjectDraft);
      setWelcomeBody(welcomeBodyDraft);
      setWelcomeSaved(true);
      setTimeout(() => setWelcomeSaved(false), 3000);
    } catch (err) { setWelcomeError(err.response?.data?.error || 'Save failed.'); }
    finally { setWelcomeSaving(false); }
  }

  async function handleSaveResetEmail() {
    if (!resetBodyDraft.includes('{{link}}')) {
      setResetError('The body must contain {{link}} so users receive their reset link.');
      return;
    }
    setResetSaving(true); setResetError(''); setResetSaved(false);
    try {
      await api.put('/api/admin/settings', {
        reset_password_subject: resetSubjectDraft,
        reset_password_body:    resetBodyDraft,
      });
      setResetSubject(resetSubjectDraft);
      setResetBody(resetBodyDraft);
      setResetSaved(true);
      setTimeout(() => setResetSaved(false), 3000);
    } catch (err) { setResetError(err.response?.data?.error || 'Save failed.'); }
    finally { setResetSaving(false); }
  }

  async function handleSaveAssignEmail() {
    if (!assignBodyDraft.includes('{{link}}')) {
      setAssignError('The body must contain {{link}} so judges can reach their dashboard.');
      return;
    }
    setAssignSaving(true); setAssignError(''); setAssignSaved(false);
    try {
      await api.put('/api/admin/settings', {
        judge_assignment_subject: assignSubjectDraft,
        judge_assignment_body:    assignBodyDraft,
      });
      setAssignSubject(assignSubjectDraft);
      setAssignBody(assignBodyDraft);
      setAssignSaved(true);
      setTimeout(() => setAssignSaved(false), 3000);
    } catch (err) { setAssignError(err.response?.data?.error || 'Save failed.'); }
    finally { setAssignSaving(false); }
  }

  async function handleSaveResultsEmail() {
    setResultsSaving(true); setResultsError(''); setResultsSaved(false);
    try {
      await api.put('/api/admin/settings', { results_email_body: resultsBodyDraft });
      setResultsBody(resultsBodyDraft);
      setResultsSaved(true);
      setTimeout(() => setResultsSaved(false), 3000);
    } catch (err) { setResultsError(err.response?.data?.error || 'Save failed.'); }
    finally { setResultsSaving(false); }
  }

  async function handleSaveInviteEmail() {
    if (!inviteBodyDraft.includes('{{link}}')) {
      setInviteError('The body must contain {{link}} so contestants can reach the heat selection page.');
      return;
    }
    setInviteSaving(true); setInviteError(''); setInviteSaved(false);
    try {
      await api.put('/api/admin/settings', {
        foom_heat_invite_subject: inviteSubjectDraft,
        foom_heat_invite_body:    inviteBodyDraft,
      });
      setInviteSubject(inviteSubjectDraft); setInviteBody(inviteBodyDraft);
      setInviteSaved(true);
      setTimeout(() => setInviteSaved(false), 3000);
    } catch (err) { setInviteError(err.response?.data?.error || 'Save failed.'); }
    finally { setInviteSaving(false); }
  }

  async function handleSaveReminderEmail() {
    if (!reminderBodyDraft.includes('{{link}}')) {
      setReminderError('The body must contain {{link}} so contestants can reach the heat selection page.');
      return;
    }
    setReminderSaving(true); setReminderError(''); setReminderSaved(false);
    try {
      await api.put('/api/admin/settings', {
        foom_heat_reminder_subject: reminderSubjectDraft,
        foom_heat_reminder_body:    reminderBodyDraft,
      });
      setReminderSubject(reminderSubjectDraft); setReminderBody(reminderBodyDraft);
      setReminderSaved(true);
      setTimeout(() => setReminderSaved(false), 3000);
    } catch (err) { setReminderError(err.response?.data?.error || 'Save failed.'); }
    finally { setReminderSaving(false); }
  }

  async function handleSaveConfirmEmail() {
    setConfirmSaving(true); setConfirmError(''); setConfirmSaved(false);
    try {
      await api.put('/api/admin/settings', {
        foom_heat_confirm_subject: confirmSubjectDraft,
        foom_heat_confirm_body:    confirmBodyDraft,
      });
      setConfirmSubject(confirmSubjectDraft); setConfirmBody(confirmBodyDraft);
      setConfirmSaved(true);
      setTimeout(() => setConfirmSaved(false), 3000);
    } catch (err) { setConfirmError(err.response?.data?.error || 'Save failed.'); }
    finally { setConfirmSaving(false); }
  }

  async function handleSaveHnmcEmail() {
    setHnmcSaving(true); setHnmcError(''); setHnmcSaved(false);
    try {
      await api.put('/api/admin/settings', {
        heat_notify_mc_subject: hnmcSubjectDraft,
        heat_notify_mc_body:    hnmcBodyDraft,
      });
      setHnmcSubject(hnmcSubjectDraft); setHnmcBody(hnmcBodyDraft);
      setHnmcSaved(true);
      setTimeout(() => setHnmcSaved(false), 3000);
    } catch (err) { setHnmcError(err.response?.data?.error || 'Save failed.'); }
    finally { setHnmcSaving(false); }
  }

  async function handleSaveHnshEmail() {
    setHnshSaving(true); setHnshError(''); setHnshSaved(false);
    try {
      await api.put('/api/admin/settings', {
        heat_notify_stage_hand_subject: hnshSubjectDraft,
        heat_notify_stage_hand_body:    hnshBodyDraft,
      });
      setHnshSubject(hnshSubjectDraft); setHnshBody(hnshBodyDraft);
      setHnshSaved(true);
      setTimeout(() => setHnshSaved(false), 3000);
    } catch (err) { setHnshError(err.response?.data?.error || 'Save failed.'); }
    finally { setHnshSaving(false); }
  }

  async function handleSaveHnjEmail() {
    setHnjSaving(true); setHnjError(''); setHnjSaved(false);
    try {
      await api.put('/api/admin/settings', {
        heat_notify_judge_subject: hnjSubjectDraft,
        heat_notify_judge_body:    hnjBodyDraft,
      });
      setHnjSubject(hnjSubjectDraft); setHnjBody(hnjBodyDraft);
      setHnjSaved(true);
      setTimeout(() => setHnjSaved(false), 3000);
    } catch (err) { setHnjError(err.response?.data?.error || 'Save failed.'); }
    finally { setHnjSaving(false); }
  }

  async function handleSaveHnpEmail() {
    setHnpSaving(true); setHnpError(''); setHnpSaved(false);
    try {
      await api.put('/api/admin/settings', {
        heat_notify_performer_subject: hnpSubjectDraft,
        heat_notify_performer_body:    hnpBodyDraft,
      });
      setHnpSubject(hnpSubjectDraft); setHnpBody(hnpBodyDraft);
      setHnpSaved(true);
      setTimeout(() => setHnpSaved(false), 3000);
    } catch (err) { setHnpError(err.response?.data?.error || 'Save failed.'); }
    finally { setHnpSaving(false); }
  }

  async function handleSaveHrjEmail() {
    setHrjSaving(true); setHrjError(''); setHrjSaved(false);
    try {
      await api.put('/api/admin/settings', {
        heat_reminder_judge_subject: hrjSubjectDraft,
        heat_reminder_judge_body:    hrjBodyDraft,
      });
      setHrjSubject(hrjSubjectDraft); setHrjBody(hrjBodyDraft);
      setHrjSaved(true);
      setTimeout(() => setHrjSaved(false), 3000);
    } catch (err) { setHrjError(err.response?.data?.error || 'Save failed.'); }
    finally { setHrjSaving(false); }
  }

  if (loading) return <div className="c-loading"><div className="c-spinner" /></div>;
  if (loadError) return <div className="c-alert">{loadError}</div>;

  const welcomeDirty  = welcomeSubjectDraft  !== welcomeSubject  || welcomeBodyDraft  !== welcomeBody;
  const resetDirty    = resetSubjectDraft    !== resetSubject    || resetBodyDraft    !== resetBody;
  const assignDirty   = assignSubjectDraft   !== assignSubject   || assignBodyDraft   !== assignBody;
  const resultsDirty  = resultsBodyDraft     !== resultsBody;
  const inviteDirty   = inviteSubjectDraft   !== inviteSubject   || inviteBodyDraft   !== inviteBody;
  const reminderDirty = reminderSubjectDraft !== reminderSubject || reminderBodyDraft !== reminderBody;
  const confirmDirty  = confirmSubjectDraft  !== confirmSubject  || confirmBodyDraft  !== confirmBody;
  const hnjDirty      = hnjSubjectDraft      !== hnjSubject      || hnjBodyDraft      !== hnjBody;
  const hnmcDirty     = hnmcSubjectDraft     !== hnmcSubject     || hnmcBodyDraft     !== hnmcBody;
  const hnshDirty     = hnshSubjectDraft     !== hnshSubject     || hnshBodyDraft     !== hnshBody;
  const hnpDirty      = hnpSubjectDraft      !== hnpSubject      || hnpBodyDraft      !== hnpBody;
  const hrjDirty      = hrjSubjectDraft      !== hrjSubject      || hrjBodyDraft      !== hrjBody;

  return (
    <div className="a-section">
      <div className="a-section__header">
        <div>
          <div className="a-section__title">Email Templates</div>
          <div className="a-section__desc">
            Edit the emails sent automatically by the system. Use <code>{'{{link}}'}</code> and <code>{'{{firstname}}'}</code> as placeholders.
          </div>
        </div>
      </div>

      {/* Test recipients — shared by every Test button on this page */}
      <TestRecipientsManager
        recipients={testRecipients}
        onAdd={addTestRecipient}
        onRemove={removeTestRecipient}
        error={testRecipError}
      />

      <EmailGroup
        title="Emails to judges"
        count={4}
        open={openGroups.judges}
        onToggle={() => toggleGroup('judges')}
      >
        {/* Judge Welcome Email */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ fontSize: '1.08rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#000', marginBottom: '0.75rem', paddingBottom: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>
            Judge Welcome Email
            <span style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 'normal', marginLeft: '0.5rem' }}>
              {"(Sent by pressing the FIRST NOTIFICATION button under 'Judges & Assignments')"}
            </span>
          </div>
          {welcomeError && <div className="c-alert" style={{ marginBottom: '0.75rem' }}>{welcomeError}</div>}
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.3rem' }}>Subject</label>
            <input className="c-input" value={welcomeSubjectDraft}
              onChange={(e) => { setWelcomeSubjectDraft(e.target.value); setWelcomeError(''); setWelcomeSaved(false); }}
              placeholder="Email subject line" />
          </div>
          <div style={{ marginBottom: '0.5rem' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.3rem' }}>Body</label>
            <textarea className="c-textarea" rows={10} value={welcomeBodyDraft}
              onChange={(e) => { setWelcomeBodyDraft(e.target.value); setWelcomeError(''); setWelcomeSaved(false); }}
              placeholder="Email body…" style={{ fontSize: '0.82rem', fontFamily: 'monospace' }} />
            <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.3rem 0 0' }}>
              Available placeholders: <code>{'{{firstname}}'}</code>, <code>{'{{link}}'}</code> — <code>{'{{link}}'}</code> is required
            </p>
            {!welcomeBodyDraft.includes('{{link}}') && welcomeBodyDraft.length > 0 && (
              <p style={{ fontSize: '0.75rem', color: '#dc2626', margin: '0.25rem 0 0', fontWeight: 600 }}>
                Body must contain {'{{link}}'} or judges won't receive their setup link.
              </p>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button className="c-btn c-btn--sm" onClick={handleSaveWelcomeEmail} disabled={welcomeSaving || !welcomeDirty}>
              {welcomeSaving ? '…' : 'Save'}
            </button>
            {welcomeSaved && <span style={{ fontSize: '0.75rem', color: '#16a34a' }}>Saved ✓</span>}
            <EmailTestButton
              templateKey="judge_welcome"
              recipientCount={testRecipients.length}
            />
          </div>
        </div>

        {/* Judge Assignment Email */}
        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1.5rem' }}>
          <div style={{ fontSize: '1.08rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#000', marginBottom: '0.75rem', paddingBottom: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>
            Judge Assignment Email
            <span style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 'normal', marginLeft: '0.5rem' }}>
              {"(Sent by pressing the ASSIGNMENT NOTIFICATION or REASSIGN NOTIFICATION button under 'Judges & Assignments')"}
            </span>
          </div>
          {assignError && <div className="c-alert" style={{ marginBottom: '0.75rem' }}>{assignError}</div>}
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.3rem' }}>Subject</label>
            <input className="c-input" value={assignSubjectDraft}
              onChange={(e) => { setAssignSubjectDraft(e.target.value); setAssignError(''); setAssignSaved(false); }}
              placeholder="Email subject line" />
          </div>
          <div style={{ marginBottom: '0.5rem' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.3rem' }}>Body</label>
            <textarea className="c-textarea" rows={10} value={assignBodyDraft}
              onChange={(e) => { setAssignBodyDraft(e.target.value); setAssignError(''); setAssignSaved(false); }}
              placeholder="Email body…" style={{ fontSize: '0.82rem', fontFamily: 'monospace' }} />
            <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.3rem 0 0' }}>
              Available placeholders: <code>{'{{firstname}}'}</code>, <code>{'{{link}}'}</code> — <code>{'{{link}}'}</code> is required
            </p>
            {!assignBodyDraft.includes('{{link}}') && assignBodyDraft.length > 0 && (
              <p style={{ fontSize: '0.75rem', color: '#dc2626', margin: '0.25rem 0 0', fontWeight: 600 }}>
                Body must contain {'{{link}}'} or judges won't receive a link to their dashboard.
              </p>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button className="c-btn c-btn--sm" onClick={handleSaveAssignEmail} disabled={assignSaving || !assignDirty}>
              {assignSaving ? '…' : 'Save'}
            </button>
            {assignSaved && <span style={{ fontSize: '0.75rem', color: '#16a34a' }}>Saved ✓</span>}
            <EmailTestButton
              templateKey="judge_assignment"
              recipientCount={testRecipients.length}
            />
          </div>
        </div>

        {/* Heat Notify Judge Email */}
        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1.5rem', marginBottom: '2rem' }}>
          <div style={{ fontSize: '1.08rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#000', marginBottom: '0.75rem', paddingBottom: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>
            Heat Judge Notification Email
            <span style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 'normal', marginLeft: '0.5rem' }}>
              {"(Sent by pressing the NOTIFY JUDGES button on a heat under 'Live Heats')"}
            </span>
          </div>
          {hnjError && <div className="c-alert" style={{ marginBottom: '0.75rem' }}>{hnjError}</div>}
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.3rem' }}>Subject</label>
            <input className="c-input" value={hnjSubjectDraft}
              onChange={(e) => { setHnjSubjectDraft(e.target.value); setHnjError(''); setHnjSaved(false); }}
              placeholder="Email subject line" />
          </div>
          <div style={{ marginBottom: '0.5rem' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.3rem' }}>Body</label>
            <textarea className="c-textarea" rows={12} value={hnjBodyDraft}
              onChange={(e) => { setHnjBodyDraft(e.target.value); setHnjError(''); setHnjSaved(false); }}
              placeholder="Email body…" style={{ fontSize: '0.82rem', fontFamily: 'monospace' }} />
            <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.3rem 0 0' }}>
              Placeholders: <code>{'{{firstname}}'}</code>, <code>{'{{heat_name}}'}</code>, <code>{'{{heat_date}}'}</code>, <code>{'{{judge_arrival_time}}'}</code>, <code>{'{{venue_name}}'}</code>, <code>{'{{venue_address}}'}</code>, <code>{'{{calendar_google_link}}'}</code>, <code>{'{{calendar_ics_link}}'}</code>
              <br />
              <code>{'{{judge_arrival_time}}'}</code> is the judges&apos; call time — performer arrival + 30 minutes unless the heat sets its own. <code>{'{{arrival_time}}'}</code> still means the <em>performers&apos;</em> time, so use it here only if you deliberately want that.
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button className="c-btn c-btn--sm" onClick={handleSaveHnjEmail} disabled={hnjSaving || !hnjDirty}>
              {hnjSaving ? '…' : 'Save'}
            </button>
            {hnjSaved && <span style={{ fontSize: '0.75rem', color: '#16a34a' }}>Saved ✓</span>}
            <EmailTestButton
              needsHeat
              templateKey="heat_notify_judge"
              heats={testHeats}
              recipientCount={testRecipients.length}
            />
          </div>
        </div>

        {/* Heat Reminder Judge Email */}
        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1.5rem' }}>
          <div style={{ fontSize: '1.08rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#000', marginBottom: '0.75rem', paddingBottom: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>
            Heat Judge Reminder Email
            <span style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 'normal', marginLeft: '0.5rem' }}>
              {"(No button — sent automatically 7 days before the heat, once per heat, excluding Grand Finals)"}
            </span>
          </div>
          {hrjError && <div className="c-alert" style={{ marginBottom: '0.75rem' }}>{hrjError}</div>}
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.3rem' }}>Subject</label>
            <input className="c-input" value={hrjSubjectDraft}
              onChange={(e) => { setHrjSubjectDraft(e.target.value); setHrjError(''); setHrjSaved(false); }}
              placeholder="Email subject line" />
          </div>
          <div style={{ marginBottom: '0.5rem' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.3rem' }}>Body</label>
            <textarea className="c-textarea" rows={12} value={hrjBodyDraft}
              onChange={(e) => { setHrjBodyDraft(e.target.value); setHrjError(''); setHrjSaved(false); }}
              placeholder="Email body…" style={{ fontSize: '0.82rem', fontFamily: 'monospace' }} />
            <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.3rem 0 0' }}>
              Placeholders: <code>{'{{firstname}}'}</code>, <code>{'{{heat_name}}'}</code>, <code>{'{{heat_date}}'}</code>, <code>{'{{judge_arrival_time}}'}</code>, <code>{'{{venue_name}}'}</code>, <code>{'{{venue_address}}'}</code>, <code>{'{{calendar_google_link}}'}</code>, <code>{'{{calendar_ics_link}}'}</code>
              <br />
              <code>{'{{judge_arrival_time}}'}</code> is the judges&apos; call time — performer arrival + 30 minutes unless the heat sets its own. <code>{'{{arrival_time}}'}</code> still means the <em>performers&apos;</em> time, so use it here only if you deliberately want that.
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button className="c-btn c-btn--sm" onClick={handleSaveHrjEmail} disabled={hrjSaving || !hrjDirty}>
              {hrjSaving ? '…' : 'Save'}
            </button>
            {hrjSaved && <span style={{ fontSize: '0.75rem', color: '#16a34a' }}>Saved ✓</span>}
            <EmailTestButton
              needsHeat
              templateKey="heat_reminder_judge"
              heats={testHeats}
              recipientCount={testRecipients.length}
            />
          </div>
        </div>
      </EmailGroup>

      <EmailGroup
        title="Emails to contestants"
        count={5}
        open={openGroups.contestants}
        onToggle={() => toggleGroup('contestants')}
      >
        {/* FOOM Heat Invite Email */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ fontSize: '1.08rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#000', marginBottom: '0.75rem', paddingBottom: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>
            FOOM Heats Open Email
          </div>
          <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '1rem' }}>
            Sent when you click <strong>Send FOOM Heat Invitations</strong> on the Overview tab.
          </p>
          {inviteError && <div className="c-alert" style={{ marginBottom: '0.75rem' }}>{inviteError}</div>}
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.3rem' }}>Subject</label>
            <input className="c-input" value={inviteSubjectDraft}
              onChange={(e) => { setInviteSubjectDraft(e.target.value); setInviteError(''); setInviteSaved(false); }}
              placeholder="Email subject line" />
          </div>
          <div style={{ marginBottom: '0.5rem' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.3rem' }}>Body</label>
            <textarea className="c-textarea" rows={12} value={inviteBodyDraft}
              onChange={(e) => { setInviteBodyDraft(e.target.value); setInviteError(''); setInviteSaved(false); }}
              placeholder="Email body…" style={{ fontSize: '0.82rem', fontFamily: 'monospace' }} />
            <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.3rem 0 0' }}>
              Placeholders: <code>{'{{firstname}}'}</code>, <code>{'{{link}}'}</code> — <code>{'{{link}}'}</code> is required
            </p>
            {!inviteBodyDraft.includes('{{link}}') && inviteBodyDraft.length > 0 && (
              <p style={{ fontSize: '0.75rem', color: '#dc2626', margin: '0.25rem 0 0', fontWeight: 600 }}>
                Body must contain {'{{link}}'} or contestants won't receive a link to choose their heat.
              </p>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button className="c-btn c-btn--sm" onClick={handleSaveInviteEmail} disabled={inviteSaving || !inviteDirty}>
              {inviteSaving ? '…' : 'Save'}
            </button>
            {inviteSaved && <span style={{ fontSize: '0.75rem', color: '#16a34a' }}>Saved ✓</span>}
            <EmailTestButton
              needsHeat
              templateKey="foom_heat_invite"
              heats={testHeats}
              recipientCount={testRecipients.length}
            />
          </div>
        </div>

        {/* FOOM Heat Reminder Email */}
        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1.5rem', marginBottom: '2rem' }}>
          <div style={{ fontSize: '1.08rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#000', marginBottom: '0.75rem', paddingBottom: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>
            FOOM Heat Reminder Email
          </div>
          <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '1rem' }}>
            Sent when you click <strong>Send Heat Reminder</strong> on the Overview tab. Only goes to contestants who haven't chosen a heat yet.
          </p>
          {reminderError && <div className="c-alert" style={{ marginBottom: '0.75rem' }}>{reminderError}</div>}
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.3rem' }}>Subject</label>
            <input className="c-input" value={reminderSubjectDraft}
              onChange={(e) => { setReminderSubjectDraft(e.target.value); setReminderError(''); setReminderSaved(false); }}
              placeholder="Email subject line" />
          </div>
          <div style={{ marginBottom: '0.5rem' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.3rem' }}>Body</label>
            <textarea className="c-textarea" rows={12} value={reminderBodyDraft}
              onChange={(e) => { setReminderBodyDraft(e.target.value); setReminderError(''); setReminderSaved(false); }}
              placeholder="Email body…" style={{ fontSize: '0.82rem', fontFamily: 'monospace' }} />
            <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.3rem 0 0' }}>
              Placeholders: <code>{'{{firstname}}'}</code>, <code>{'{{link}}'}</code> — <code>{'{{link}}'}</code> is required
            </p>
            {!reminderBodyDraft.includes('{{link}}') && reminderBodyDraft.length > 0 && (
              <p style={{ fontSize: '0.75rem', color: '#dc2626', margin: '0.25rem 0 0', fontWeight: 600 }}>
                Body must contain {'{{link}}'} or contestants won't receive a link to choose their heat.
              </p>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button className="c-btn c-btn--sm" onClick={handleSaveReminderEmail} disabled={reminderSaving || !reminderDirty}>
              {reminderSaving ? '…' : 'Save'}
            </button>
            {reminderSaved && <span style={{ fontSize: '0.75rem', color: '#16a34a' }}>Saved ✓</span>}
            <EmailTestButton
              needsHeat
              templateKey="foom_heat_reminder"
              heats={testHeats}
              recipientCount={testRecipients.length}
            />
          </div>
        </div>

        {/* FOOM Heat Confirmation Email */}
        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1.5rem' }}>
          <div style={{ fontSize: '1.08rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#000', marginBottom: '0.75rem', paddingBottom: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>
            FOOM Heat Confirmation Email
          </div>
          <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '1rem' }}>
            Sent automatically when a contestant saves their heat preference.
          </p>
          {confirmError && <div className="c-alert" style={{ marginBottom: '0.75rem' }}>{confirmError}</div>}
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.3rem' }}>Subject</label>
            <input className="c-input" value={confirmSubjectDraft}
              onChange={(e) => { setConfirmSubjectDraft(e.target.value); setConfirmError(''); setConfirmSaved(false); }}
              placeholder="Email subject line" />
          </div>
          <div style={{ marginBottom: '0.5rem' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.3rem' }}>Body</label>
            <textarea className="c-textarea" rows={12} value={confirmBodyDraft}
              onChange={(e) => { setConfirmBodyDraft(e.target.value); setConfirmError(''); setConfirmSaved(false); }}
              placeholder="Email body…" style={{ fontSize: '0.82rem', fontFamily: 'monospace' }} />
            <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.3rem 0 0' }}>
              Placeholders: <code>{'{{firstname}}'}</code>, <code>{'{{song_title}}'}</code>, <code>{'{{heat_name}}'}</code>, <code>{'{{heat_date}}'}</code>, <code>{'{{venue_name}}'}</code>, <code>{'{{arrival_time}}'}</code>, <code>{'{{calendar_google_link}}'}</code>, <code>{'{{calendar_ics_link}}'}</code>
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button className="c-btn c-btn--sm" onClick={handleSaveConfirmEmail} disabled={confirmSaving || !confirmDirty}>
              {confirmSaving ? '…' : 'Save'}
            </button>
            {confirmSaved && <span style={{ fontSize: '0.75rem', color: '#16a34a' }}>Saved ✓</span>}
            <EmailTestButton
              needsHeat
              templateKey="foom_heat_confirm"
              heats={testHeats}
              recipientCount={testRecipients.length}
            />
          </div>
        </div>

        {/* Heat Notify Performer Email */}
        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1.5rem', marginBottom: '2rem' }}>
          <div style={{ fontSize: '1.08rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#000', marginBottom: '0.75rem', paddingBottom: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>
            Heat Performer Notification Email
          </div>
          <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '1rem' }}>
            Sent to contestants when you click <strong>Notify Performers</strong> on a heat in the Live Heats tab.
          </p>
          {hnpError && <div className="c-alert" style={{ marginBottom: '0.75rem' }}>{hnpError}</div>}
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.3rem' }}>Subject</label>
            <input className="c-input" value={hnpSubjectDraft}
              onChange={(e) => { setHnpSubjectDraft(e.target.value); setHnpError(''); setHnpSaved(false); }}
              placeholder="Email subject line" />
          </div>
          <div style={{ marginBottom: '0.5rem' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.3rem' }}>Body</label>
            <textarea className="c-textarea" rows={12} value={hnpBodyDraft}
              onChange={(e) => { setHnpBodyDraft(e.target.value); setHnpError(''); setHnpSaved(false); }}
              placeholder="Email body…" style={{ fontSize: '0.82rem', fontFamily: 'monospace' }} />
            <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.3rem 0 0' }}>
              Placeholders: <code>{'{{firstname}}'}</code>, <code>{'{{song_title}}'}</code>, <code>{'{{heat_name}}'}</code>, <code>{'{{heat_date}}'}</code>, <code>{'{{arrival_time}}'}</code>, <code>{'{{venue_name}}'}</code>, <code>{'{{venue_address}}'}</code>, <code>{'{{confirmLink}}'}</code>, <code>{'{{calendar_google_link}}'}</code>, <code>{'{{calendar_ics_link}}'}</code>
            </p>
            <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.3rem 0 0' }}>
              <code>{'{{confirmLink}}'}</code> is the performer's confirmation link. Remove it
              and contestants have no way to confirm their heat. <code>{'{{song_title}}'}</code> is
              the one song when you use <strong>Notify Performer</strong> on a single row, and all
              of that contestant's songs in the heat when you use <strong>Notify Performers</strong>.
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button className="c-btn c-btn--sm" onClick={handleSaveHnpEmail} disabled={hnpSaving || !hnpDirty}>
              {hnpSaving ? '…' : 'Save'}
            </button>
            {hnpSaved && <span style={{ fontSize: '0.75rem', color: '#16a34a' }}>Saved ✓</span>}
            <EmailTestButton
              needsHeat
              templateKey="heat_notify_performer"
              heats={testHeats}
              recipientCount={testRecipients.length}
            />
          </div>
        </div>

        {/* Results Email */}
        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1.5rem', marginBottom: '2rem' }}>
          <div style={{ fontSize: '1.08rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#000', marginBottom: '0.75rem', paddingBottom: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>
            Results Email Body
          </div>
          <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '1rem' }}>
            Sent to each contestant with judged songs when you press <strong>Publish Results</strong> (or <strong>Close &amp; Send Results</strong>) on the Overview tab,
            with their own results PDF attached — scores per judge, comments and placements for their songs only.
            Placeholders: <code>{'{{firstname}}'}</code>, <code>{'{{name}}'}</code>, <code>{'{{year}}'}</code>, <code>{'{{dashboard_link}}'}</code> (a clickable link to their dashboard).
            <br />
            <strong>Test</strong> sends a real sample: the first judged contestant&apos;s PDF, with their name filled in and the subject marked as a sample.
          </p>
          {resultsError && <div className="c-alert" style={{ marginBottom: '0.75rem' }}>{resultsError}</div>}
          <div style={{ marginBottom: '0.5rem' }}>
            <textarea className="c-textarea" rows={10} value={resultsBodyDraft}
              onChange={(e) => { setResultsBodyDraft(e.target.value); setResultsError(''); setResultsSaved(false); }}
              placeholder="Email body sent to contestants when results are published…"
              style={{ fontSize: '0.82rem', fontFamily: 'monospace' }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button className="c-btn c-btn--sm" onClick={handleSaveResultsEmail} disabled={resultsSaving || !resultsDirty}>
              {resultsSaving ? '…' : 'Save'}
            </button>
            {resultsSaved && <span style={{ fontSize: '0.75rem', color: '#16a34a' }}>Saved ✓</span>}
            <EmailTestButton
              templateKey="results_email"
              recipientCount={testRecipients.length}
            />
          </div>
        </div>
      </EmailGroup>

      <EmailGroup
        title="Emails to MCs & stage hands"
        count={2}
        open={openGroups.mcs}
        onToggle={() => toggleGroup('mcs')}
      >
        {/* Heat Notify MC Email */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ fontSize: '1.08rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#000', marginBottom: '0.75rem', paddingBottom: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>
            Heat MC Notification Email
          </div>
          <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '1rem' }}>
            Sent to the allocated MC when you click <strong>Notify MC</strong> on a heat in the Live Heats tab.
            The running sheet is attached.
          </p>
          {hnmcError && <div className="c-alert" style={{ marginBottom: '0.75rem' }}>{hnmcError}</div>}
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.3rem' }}>Subject</label>
            <input className="c-input" value={hnmcSubjectDraft}
              onChange={(e) => { setHnmcSubjectDraft(e.target.value); setHnmcError(''); setHnmcSaved(false); }}
              placeholder="Email subject line" />
          </div>
          <div style={{ marginBottom: '0.5rem' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.3rem' }}>Body</label>
            <textarea className="c-textarea" rows={12} value={hnmcBodyDraft}
              onChange={(e) => { setHnmcBodyDraft(e.target.value); setHnmcError(''); setHnmcSaved(false); }}
              placeholder="Email body…" style={{ fontSize: '0.82rem', fontFamily: 'monospace' }} />
            <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.3rem 0 0' }}>
              Placeholders: <code>{'{{firstname}}'}</code>, <code>{'{{heat_name}}'}</code>, <code>{'{{heat_date}}'}</code>, <code>{'{{mc_arrival_time}}'}</code>, <code>{'{{venue_name}}'}</code>, <code>{'{{venue_address}}'}</code>, <code>{'{{confirmLink}}'}</code>, <code>{'{{calendar_google_link}}'}</code>, <code>{'{{calendar_ics_link}}'}</code>
              <br />
              <code>{'{{mc_arrival_time}}'}</code> is the same time as the judges&apos; — performer arrival + 30 minutes unless the heat sets its own. <code>{'{{arrival_time}}'}</code> still means the <em>performers&apos;</em> time.
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button className="c-btn c-btn--sm" onClick={handleSaveHnmcEmail} disabled={hnmcSaving || !hnmcDirty}>
              {hnmcSaving ? '…' : 'Save'}
            </button>
            {hnmcSaved && <span style={{ fontSize: '0.75rem', color: '#16a34a' }}>Saved ✓</span>}
            <EmailTestButton
              needsHeat
              templateKey="heat_notify_mc"
              heats={testHeats}
              recipientCount={testRecipients.length}
            />
          </div>
        </div>

        {/* Heat Notify Stage Hand Email (migration 046) */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ fontSize: '1.08rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#000', marginBottom: '0.75rem', paddingBottom: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>
            Heat Stage Hand Notification Email
          </div>
          <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '1rem' }}>
            Sent to the allocated stage hand when you click <strong>Notify SH</strong> on a heat in the Live Heats tab.
            The running sheet and the technical requirements sheet are attached. A test send attaches the running sheet only.
          </p>
          {hnshError && <div className="c-alert" style={{ marginBottom: '0.75rem' }}>{hnshError}</div>}
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.3rem' }}>Subject</label>
            <input className="c-input" value={hnshSubjectDraft}
              onChange={(e) => { setHnshSubjectDraft(e.target.value); setHnshError(''); setHnshSaved(false); }}
              placeholder="Email subject line" />
          </div>
          <div style={{ marginBottom: '0.5rem' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.3rem' }}>Body</label>
            <textarea className="c-textarea" rows={12} value={hnshBodyDraft}
              onChange={(e) => { setHnshBodyDraft(e.target.value); setHnshError(''); setHnshSaved(false); }}
              placeholder="Email body…" style={{ fontSize: '0.82rem', fontFamily: 'monospace' }} />
            <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.3rem 0 0' }}>
              Placeholders: <code>{'{{firstname}}'}</code>, <code>{'{{heat_name}}'}</code>, <code>{'{{heat_date}}'}</code>, <code>{'{{arrival_time}}'}</code>, <code>{'{{venue_name}}'}</code>, <code>{'{{venue_address}}'}</code>, <code>{'{{confirmLink}}'}</code>, <code>{'{{calendar_google_link}}'}</code>, <code>{'{{calendar_ics_link}}'}</code>
              <br />
              <code>{'{{arrival_time}}'}</code> is the <em>performers&apos;</em> arrival time — the stage hand sets up for the acts, so they arrive with them.
              Keep <code>{'{{confirmLink}}'}</code> in the body, or the stage hand has no way to confirm and the header tick stays red.
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button className="c-btn c-btn--sm" onClick={handleSaveHnshEmail} disabled={hnshSaving || !hnshDirty}>
              {hnshSaving ? '…' : 'Save'}
            </button>
            {hnshSaved && <span style={{ fontSize: '0.75rem', color: '#16a34a' }}>Saved ✓</span>}
            <EmailTestButton
              needsHeat
              templateKey="heat_notify_stage_hand"
              heats={testHeats}
              recipientCount={testRecipients.length}
            />
          </div>
        </div>
      </EmailGroup>

      <EmailGroup
        title="Account emails"
        count={1}
        open={openGroups.account}
        onToggle={() => toggleGroup('account')}
      >
        {/* Password Reset Email */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ fontSize: '1.08rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#000', marginBottom: '0.75rem', paddingBottom: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>
            Password Reset Email
          </div>
          <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '1rem' }}>
            Sent to any user who requests a password reset. The link expires after 1 hour.
          </p>
          {resetError && <div className="c-alert" style={{ marginBottom: '0.75rem' }}>{resetError}</div>}
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.3rem' }}>Subject</label>
            <input className="c-input" value={resetSubjectDraft}
              onChange={(e) => { setResetSubjectDraft(e.target.value); setResetError(''); setResetSaved(false); }}
              placeholder="Email subject line" />
          </div>
          <div style={{ marginBottom: '0.5rem' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.3rem' }}>Body</label>
            <textarea className="c-textarea" rows={10} value={resetBodyDraft}
              onChange={(e) => { setResetBodyDraft(e.target.value); setResetError(''); setResetSaved(false); }}
              placeholder="Email body…" style={{ fontSize: '0.82rem', fontFamily: 'monospace' }} />
            <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.3rem 0 0' }}>
              Available placeholders: <code>{'{{firstname}}'}</code>, <code>{'{{link}}'}</code> — <code>{'{{link}}'}</code> is required
            </p>
            {!resetBodyDraft.includes('{{link}}') && resetBodyDraft.length > 0 && (
              <p style={{ fontSize: '0.75rem', color: '#dc2626', margin: '0.25rem 0 0', fontWeight: 600 }}>
                Body must contain {'{{link}}'} or users won't receive their reset link.
              </p>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button className="c-btn c-btn--sm" onClick={handleSaveResetEmail} disabled={resetSaving || !resetDirty}>
              {resetSaving ? '…' : 'Save'}
            </button>
            {resetSaved && <span style={{ fontSize: '0.75rem', color: '#16a34a' }}>Saved ✓</span>}
            <EmailTestButton
              templateKey="reset_password"
              recipientCount={testRecipients.length}
            />
          </div>
        </div>
      </EmailGroup>
    </div>
  );
}

// ─── ManageHelpText ───────────────────────────────────────────────────────────
// Groups tooltip keys by prefix and lets admins edit + save each one via
// PUT /api/tooltips/:key.

const TOOLTIP_GROUPS = [
  {
    label: 'Contestant Registration',
    prefix: 'contestant_',
    keys: [
      'contestant_full_name',
      'contestant_email',
      'contestant_phone',
      'contestant_membership',
      'contestant_membership_number',
    ],
  },
  {
    label: 'Song Submission',
    prefix: 'song_',
    keys: [
      'song_title',
      'song_category',
      'song_music_file',
      'song_lyrics_text',
      'song_notes',
    ],
  },
  {
    label: 'Payment',
    prefix: 'payment_',
    keys: [
      'payment_member_fee',
      'payment_nonmember_fee',
    ],
  },
  {
    label: 'Judge Evaluation',
    prefix: 'judge_',
    keys: [
      'judge_scoring_criteria',
      'judge_comments',
      'judge_winner',
      'judge_runner_up',
      'judge_submit',
    ],
  },
  {
    label: 'Admin — Settings',
    prefix: 'admin_',
    keys: [
      'admin_member_fee',
      'admin_nonmember_fee',
      'admin_max_songs',
      'admin_submission_deadline',
      'admin_judging_deadline',
      'admin_judges_per_song',
      'admin_competition_status',
      'admin_judge_comments_instructions',
    ],
  },
  {
    label: 'Admin — Judge Management',
    prefix: 'admin_judge_',
    keys: [
      'admin_judge_name',
      'admin_judge_email',
      'admin_judge_credentials',
      'admin_judge_assign',
    ],
  },
  {
    label: 'Admin — Categories & Criteria',
    prefix: 'admin_cat_',
    keys: [
      'admin_category_name',
      'admin_category_requires_music',
      'admin_category_requires_lyrics',
      'admin_criteria_name',
      'admin_criteria_weight',
    ],
  },
  {
    label: 'Admin — Results',
    prefix: 'admin_results_',
    keys: [
      'admin_results_publish',
      'admin_snapshot',
    ],
  },
];

function ManageHelpText() {
  const [tooltips,  setTooltips]  = useState({});
  const [drafts,    setDrafts]    = useState({});
  const [saving,    setSaving]    = useState({});
  const [saved,     setSaved]     = useState({});
  const [saveError, setSaveError] = useState({});
  const [loading,   setLoading]   = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    api.get('/api/tooltips')
      .then((res) => {
        const t = res.data.tooltips || {};
        setTooltips(t);
        setDrafts(t);
      })
      .catch(() => setLoadError('Failed to load help text.'))
      .finally(() => setLoading(false));
  }, []);

  async function handleSave(key) {
    const text = (drafts[key] ?? '').trim();
    if (!text) return;
    setSaving((p) => ({ ...p, [key]: true }));
    setSaved((p)  => ({ ...p, [key]: false }));
    setSaveError((p) => ({ ...p, [key]: '' }));
    try {
      const res = await api.put(`/api/tooltips/${key}`, { tooltip_text: text });
      const updated = res.data.tooltip.tooltip_text;
      setTooltips((p) => ({ ...p, [key]: updated }));
      setDrafts((p)   => ({ ...p, [key]: updated }));
      setSaved((p)    => ({ ...p, [key]: true }));
      setTimeout(() => setSaved((p) => ({ ...p, [key]: false })), 3000);
    } catch (err) {
      setSaveError((p) => ({
        ...p,
        [key]: err.response?.data?.error || 'Save failed.',
      }));
    } finally {
      setSaving((p) => ({ ...p, [key]: false }));
    }
  }

  if (loading) return <div className="c-loading"><div className="c-spinner" /></div>;
  if (loadError) return <div className="c-alert">{loadError}</div>;

  return (
    <div className="a-section">
      <div className="a-section__header">
        <div>
          <div className="a-section__title">Manage Help Text</div>
          <div className="a-section__desc">
            Edit the tooltip text shown to users throughout the application.
            Changes take effect immediately without reloading.
          </div>
        </div>
      </div>

      {TOOLTIP_GROUPS.map((group) => (
        <div key={group.label} style={{ marginBottom: '2rem' }}>
          <div
            style={{
              fontSize: '0.72rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.07em',
              color: '#6b7280',
              marginBottom: '0.6rem',
              paddingBottom: '0.4rem',
              borderBottom: '1px solid #e5e7eb',
            }}
          >
            {group.label}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {group.keys.map((key) => {
              // Only show keys that actually exist in the DB (fail silently for any not seeded)
              if (!(key in tooltips) && !(key in drafts)) return null;
              const isDirty = (drafts[key] ?? '') !== (tooltips[key] ?? '');
              return (
                <div key={key} style={{ display: 'grid', gridTemplateColumns: '220px 1fr auto', gap: '0.75rem', alignItems: 'flex-start' }}>
                  {/* Key label */}
                  <div style={{ paddingTop: '0.45rem' }}>
                    <code style={{ fontSize: '0.74rem', background: '#f3f4f6', padding: '2px 6px', borderRadius: '4px', color: '#374151' }}>
                      {key}
                    </code>
                  </div>

                  {/* Textarea */}
                  <div>
                    <textarea
                      className="c-textarea"
                      rows={2}
                      value={drafts[key] ?? ''}
                      onChange={(e) => {
                        setDrafts((p) => ({ ...p, [key]: e.target.value }));
                        setSaved((p) => ({ ...p, [key]: false }));
                        setSaveError((p) => ({ ...p, [key]: '' }));
                      }}
                      style={{ fontSize: '0.82rem' }}
                    />
                    {saveError[key] && (
                      <p style={{ color: '#dc2626', fontSize: '0.75rem', margin: '0.2rem 0 0' }}>
                        {saveError[key]}
                      </p>
                    )}
                  </div>

                  {/* Save button + confirmation */}
                  <div style={{ paddingTop: '0.35rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <button
                      className="c-btn c-btn--sm"
                      onClick={() => handleSave(key)}
                      disabled={saving[key] || !isDirty}
                    >
                      {saving[key] ? '…' : 'Save'}
                    </button>
                    {saved[key] && (
                      <span style={{ fontSize: '0.75rem', color: '#16a34a', whiteSpace: 'nowrap' }}>
                        Saved ✓
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

    </div>
  );
}

// ─── UsersPanel ───────────────────────────────────────────────────────────────
function UsersPanel() {
  const [contestants,    setContestants]    = useState(null); // null = loading
  const [registeredOnly, setRegisteredOnly] = useState(null);
  const [error,          setError]          = useState('');
  const [deleting,       setDeleting]       = useState(null); // userId being deleted
  const [deleteError,    setDeleteError]    = useState('');
  const [deleted,        setDeleted]        = useState({}); // { [userId]: true } briefly shown
  const [contestantsOpen, setContestantsOpen] = usePersistentCollapse('users:contestants');
  // Contestants list: free-text filter + column sort
  const [search,    setSearch]    = useState('');
  const [sortKey,   setSortKey]   = useState('registered_at'); // 'full_name' | 'registered_at'
  const [sortDir,   setSortDir]   = useState('desc');          // 'asc' | 'desc'
  const [registeredOpen,  setRegisteredOpen]  = usePersistentCollapse('users:registered');

  useEffect(() => {
    api.get('/api/admin/users')
      .then((res) => {
        setContestants(res.data.contestants);
        setRegisteredOnly(res.data.registered_only ?? []);
      })
      .catch(() => setError('Failed to load users.'));
  }, []);

  async function handleDelete(user) {
    const warning = user.current_comp_songs > 0
      ? `This contestant has ${user.current_comp_songs} song(s) in the current competition. Deleting them will also delete those entries and any associated scores.\n\nThis cannot be undone.`
      : `This will permanently delete this contestant and all their competition history including songs, scores and comments.\n\nThis cannot be undone.`;

    if (!window.confirm(`Delete ${user.full_name} (${user.email})?\n\n${warning}`)) return;

    setDeleting(user.user_id);
    setDeleteError('');
    try {
      await api.delete(`/api/admin/users/${user.user_id}`);
      // Mark as deleted (brief flash) then remove from list
      setDeleted((prev) => ({ ...prev, [user.user_id]: true }));
      setTimeout(() => {
        setContestants((prev) => prev.filter((c) => c.user_id !== user.user_id));
        setDeleted((prev) => { const n = { ...prev }; delete n[user.user_id]; return n; });
      }, 800);
    } catch (err) {
      setDeleteError(err.response?.data?.error || `Failed to delete ${user.full_name}.`);
    } finally {
      setDeleting(null);
    }
  }

  if (error) {
    return <div className="c-alert" role="alert">{error}</div>;
  }

  const loading = contestants === null || registeredOnly === null;

  // Clicking a sortable header toggles direction; switching column starts on
  // ascending for names and most-recent-first for dates.
  function toggleSort(key) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'registered_at' ? 'desc' : 'asc');
    }
  }

  const sortArrow = (key) => (sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

  // Filter on name + email, then sort. Names compare case-insensitively so
  // "MARTIN SUMMER" doesn't sort apart from lower-case names.
  const visibleContestants = (contestants ?? [])
    .filter((c) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return (c.full_name || '').toLowerCase().includes(q)
          || (c.email     || '').toLowerCase().includes(q);
    })
    .sort((a, b) => {
      let cmp;
      if (sortKey === 'full_name') {
        cmp = (a.full_name || '').localeCompare(b.full_name || '', undefined, { sensitivity: 'base' });
      } else {
        cmp = new Date(a.registered_at) - new Date(b.registered_at);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

  return (
    <div>
      {deleteError && (
        <div className="c-alert" role="alert" style={{ marginBottom: '1rem' }}>{deleteError}</div>
      )}

      {/* ── Contestants ── */}
      <div className="a-section" style={{ marginBottom: '2rem' }}>
        <div className="a-section__header" style={{ cursor: 'pointer' }} onClick={() => setContestantsOpen((v) => !v)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <span className="a-chevron" style={{ transform: contestantsOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
            <div>
              <div className="a-section__title">Contestants</div>
              <div className="a-section__desc">
                All registered contestant accounts. Deleting a contestant removes all their songs,
                evaluations and competition history.
              </div>
            </div>
          </div>
        </div>

        {contestantsOpen && (<>
        {loading ? (
          <div className="c-loading" style={{ padding: '2rem' }}>
            <div className="c-spinner" aria-label="Loading…" />
          </div>
        ) : contestants.length === 0 ? (
          <div className="c-empty" style={{ margin: '1rem 0' }}>No contestant accounts.</div>
        ) : (
          <>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.75rem',
            margin: '0.75rem 0 1rem', flexWrap: 'wrap',
          }}>
            <input
              className="c-input"
              type="search"
              placeholder="Search name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ maxWidth: 320 }}
              aria-label="Search contestants by name or email"
            />
            {search.trim() && (
              <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                {visibleContestants.length} of {contestants.length} shown
                <button
                  type="button"
                  className="c-btn c-btn--sm c-btn--outline"
                  style={{ marginLeft: '0.6rem' }}
                  onClick={() => setSearch('')}
                >
                  Clear
                </button>
              </span>
            )}
          </div>

          <div className="a-table-wrap">
            <table className="a-table">
              <thead>
                <tr>
                  <th>
                    <button type="button" className="a-sort-btn" onClick={() => toggleSort('full_name')}>
                      Name{sortArrow('full_name')}
                    </button>
                  </th>
                  <th>Email</th>
                  <th>Member</th>
                  <th>Songs (all time)</th>
                  <th>Current competition</th>
                  <th>
                    <button type="button" className="a-sort-btn" onClick={() => toggleSort('registered_at')}>
                      Registered{sortArrow('registered_at')}
                    </button>
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {visibleContestants.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ color: '#6b7280', padding: '1rem' }}>
                      No contestants match “{search.trim()}”.
                    </td>
                  </tr>
                )}
                {visibleContestants.map((c) => (
                  <tr
                    key={c.user_id}
                    style={deleted[c.user_id] ? { opacity: 0.4, background: '#dcfce7' } : {}}
                  >
                    <td>
                      <strong>{c.full_name}</strong>
                      {!c.profile_active && (
                        <span className="badge badge--withdrawn" style={{ marginLeft: 6 }}>
                          inactive
                        </span>
                      )}
                    </td>
                    <td style={{ color: '#6b7280', fontSize: '0.85rem' }}>{c.email}</td>
                    <td>
                      <span className={`badge ${c.is_member ? 'badge--active' : 'badge--pending'}`}>
                        {c.is_member ? 'Member' : 'Non-member'}
                      </span>
                    </td>
                    <td>{c.total_songs}</td>
                    <td>
                      {c.current_comp_songs > 0 ? (
                        <span className="badge badge--submitted">{c.current_comp_songs} song{c.current_comp_songs !== 1 ? 's' : ''}</span>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>—</span>
                      )}
                    </td>
                    <td style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                      {new Date(c.registered_at).toLocaleDateString()}
                    </td>
                    <td>
                      <div className="a-row-actions">
                        {deleted[c.user_id] ? (
                          <span style={{ color: '#16a34a', fontSize: '0.85rem', fontWeight: 600 }}>
                            ✓ Deleted
                          </span>
                        ) : (
                          <>
                            <EmailEntrantControl user={c} />
                            <button
                              className="c-btn c-btn--danger c-btn--sm"
                              onClick={() => handleDelete({ ...c, role: 'contestant' })}
                              disabled={deleting === c.user_id}
                            >
                              {deleting === c.user_id ? 'Deleting…' : 'Delete'}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
        </>)}
      </div>

      {/* ── Registered, Not Yet Contestants ── */}
      {!loading && registeredOnly.length > 0 && (
        <div className="a-section" style={{ marginBottom: '2rem' }}>
          <div className="a-section__header" style={{ cursor: 'pointer' }} onClick={() => setRegisteredOpen((v) => !v)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <span className="a-chevron" style={{ transform: registeredOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
              <div>
                <div className="a-section__title">Registered — No Submission Yet</div>
                <div className="a-section__desc">
                  Users who created an account but have never submitted a song.
                  They have no competition history and can be safely deleted.
                </div>
              </div>
            </div>
          </div>

          {registeredOpen && (
          <div className="a-table-wrap">
            <table className="a-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Membership (stated)</th>
                  <th>Registered</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {registeredOnly.map((u) => (
                  <tr
                    key={u.user_id}
                    style={deleted[u.user_id] ? { opacity: 0.4, background: '#dcfce7' } : {}}
                  >
                    <td><strong>{u.full_name}</strong></td>
                    <td style={{ color: '#6b7280', fontSize: '0.85rem' }}>{u.email}</td>
                    <td>
                      <span className={`badge ${u.is_member ? 'badge--active' : 'badge--pending'}`}>
                        {u.is_member ? 'Member' : 'Non-member'}
                      </span>
                    </td>
                    <td style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                      {new Date(u.registered_at).toLocaleDateString()}
                    </td>
                    <td>
                      <div className="a-row-actions">
                        {deleted[u.user_id] ? (
                          <span style={{ color: '#16a34a', fontSize: '0.85rem', fontWeight: 600 }}>
                            ✓ Deleted
                          </span>
                        ) : (
                          <button
                            className="c-btn c-btn--danger c-btn--sm"
                            disabled={deleting === u.user_id}
                            onClick={async () => {
                              if (!window.confirm(
                                `Delete ${u.full_name} (${u.email})?\n\nThis user registered but has never submitted a song. They have no competition history.\n\nThis cannot be undone.`
                              )) return;
                              setDeleting(u.user_id);
                              setDeleteError('');
                              try {
                                await api.delete(`/api/admin/users/${u.user_id}`);
                                setDeleted((prev) => ({ ...prev, [u.user_id]: true }));
                                setTimeout(() => {
                                  setRegisteredOnly((prev) => prev.filter((x) => x.user_id !== u.user_id));
                                  setDeleted((prev) => { const n = { ...prev }; delete n[u.user_id]; return n; });
                                }, 800);
                              } catch (err) {
                                setDeleteError(err.response?.data?.error || `Failed to delete ${u.full_name}.`);
                              } finally {
                                setDeleting(null);
                              }
                            }}
                          >
                            {deleting === u.user_id ? 'Deleting…' : 'Delete'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>
      )}

    </div>
  );
}

// ─── InsertBreakControl ───────────────────────────────────────────────────────
// Sits in a heat's header. Choosing a number inserts a BREAK immediately after
// that performer. The list stops one short of the final performer: a break
// after the last act is the end of the night, not a break. Positions that
// already carry a break are omitted rather than shown and rejected.
function InsertBreakControl({ heat, onInsert, busy }) {
  const [value, setValue] = useState('');
  const songs   = (heat.songs || []).length;
  const taken   = new Set((heat.breaks || []).map(Number));
  const options = [];
  for (let i = 1; i < songs; i++) if (!taken.has(i)) options.push(i);

  if (songs < 2) return null;   // nothing to break up

  return (
    <select
      className="a-assign-select"
      value={value}
      disabled={busy || options.length === 0}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => {
        e.stopPropagation();
        const n = Number(e.target.value);
        setValue('');
        if (n) onInsert(heat.heat_id, n);
      }}
      style={{ width: 'auto', fontSize: '0.8rem' }}
      title={options.length === 0
        ? 'Every position already has a break'
        : 'Insert a break after a performer'}
      aria-label="Insert a break after a performer"
    >
      <option value="">{busy ? 'Inserting…' : '⏸ Insert Break'}</option>
      {options.map((i) => (
        <option key={i} value={i}>After performer {i}</option>
      ))}
    </select>
  );
}

// ─── HeatBreakRow ─────────────────────────────────────────────────────────────
function HeatBreakRow({ position, onRemove, busy }) {
  return (
    <li
      className="a-heat-card__item"
      style={{
        background: '#fef3c7', borderTop: '2px dashed #d97706', borderBottom: '2px dashed #d97706',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}
    >
      <span style={{ fontWeight: 800, letterSpacing: '0.18em', color: '#92400e', fontSize: '0.9rem' }}>
        BREAK
      </span>
      <button
        className="c-btn c-btn--outline c-btn--sm"
        onClick={() => onRemove(position)}
        disabled={busy}
        title={`Remove the break after performer ${position}`}
      >
        Remove break
      </button>
    </li>
  );
}

// ─── Heat Summary sub-components (module scope — no nested definitions) ──────

function ResultDropdown({ heatId, song, onToggleResult, onDeselectAll, savingResultKeys, onClose }) {
  const hasPc = song.results.some((r) => r.result === 'peoples_choice');
  const hasJs = song.results.some((r) => r.result === 'judge_selection');
  const anySelected = hasPc || hasJs;
  const bothSelected = hasPc && hasJs;
  const busyPc       = savingResultKeys.has(`${heatId}-${song.id}-peoples_choice`);
  const busyJs       = savingResultKeys.has(`${heatId}-${song.id}-judge_selection`);
  const busyBoth     = savingResultKeys.has(`${heatId}-${song.id}-both`);
  const busyDeselect = savingResultKeys.has(`${heatId}-${song.id}-deselect`);
  const anyBusy      = busyPc || busyJs || busyBoth || busyDeselect;

  return (
    <div className="a-result-dropdown" onClick={(e) => e.stopPropagation()}>
      <label className="a-result-dropdown__item">
        <input
          type="checkbox"
          checked={hasPc}
          disabled={anyBusy}
          onChange={() => onToggleResult(heatId, song.id, 'peoples_choice', song.results)}
        />
        <span>People's Choice</span>
      </label>
      <label className="a-result-dropdown__item">
        <input
          type="checkbox"
          checked={hasJs}
          disabled={anyBusy}
          onChange={() => onToggleResult(heatId, song.id, 'judge_selection', song.results)}
        />
        <span>Judge Selection</span>
      </label>
      <label className="a-result-dropdown__item">
        <input
          type="checkbox"
          checked={bothSelected}
          disabled={anyBusy || bothSelected}
          onChange={() => onToggleResult(heatId, song.id, 'both', song.results)}
        />
        <span>Both</span>
      </label>
      <button
        className="a-result-dropdown__deselect"
        disabled={!anySelected || anyBusy}
        onClick={() => onDeselectAll(heatId, song.id, song.results)}
      >
        × Deselect All
      </button>
    </div>
  );
}

function HeatSongRow({
  heatId, song, idx, totalSongs, isGrandFinal,
  reordering, removing, unassigning,
  onReorder, onUnassign, onRemove,
  openDropdownKey, setOpenDropdownKey,
  onToggleResult, onDeselectAll, savingResultKeys,
  onNotifyPerformer, notifyingSong, onRefunded,
}) {
  const hasPc      = song.results.some((r) => r.result === 'peoples_choice');
  const hasJs      = song.results.some((r) => r.result === 'judge_selection');
  const hasResult  = hasPc || hasJs;
  const dropKey    = `${heatId}-${song.id}`;
  const dropOpen   = openDropdownKey === dropKey;
  const reordering_ = reordering[song.id];
  const actionBusy = isGrandFinal ? removing === song.id : unassigning === song.id;
  // The Grand Final is included now that the notify routes understand it.
  // They previously selected on s.heat_id, which for a finalist still points at
  // the heat they qualified from, so every click would have 404'd.
  const canNotify   = !!onNotifyPerformer
    && !['withdrawn', 'disqualified'].includes(song.status);
  const notifyingThis = notifyingSong === song.id;

  return (
    <li className="a-heat-card__item" style={{ position: 'relative' }}>
      {/* Numbered in the Grand Final too, not just the regular heats. The
          running order matters as much on the night for the final as for a
          heat — it is what the MC, the sound engineer and the judges read from.
          Breaks are rendered between rows and are not counted, so the numbers
          run 1..n over the songs exactly as they do in a heat. */}
      <span style={{ fontWeight: 700, color: '#1a1a2e', minWidth: '1.4rem', textAlign: 'center', fontSize: '0.9rem' }}>
        {idx + 1}
      </span>
      <div className="a-heat-card__item-info">
        <span className="a-heat-card__item-title">{song.title}</span>
        {song.artist && <span className="a-heat-card__item-artist">{song.artist}</span>}
        {hasPc && (
          <span className="a-result-badge a-result-badge--pc">People's Choice</span>
        )}
        {hasJs && (
          <span className="a-result-badge a-result-badge--js">Judge Selection</span>
        )}
      </div>
      <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
        {/* Re-notify just this song's performer, for when its heat allocation
            changes. Not offered on the Grand Final card, which has no performer
            notification at all, nor for songs that are out of the running. */}
        {canNotify && (
          <button
            className="c-btn c-btn--sm"
            style={{ background: '#0ea5e9', marginRight: '0.25rem' }}
            disabled={notifyingThis}
            onClick={() => onNotifyPerformer(heatId, song)}
            title={`Email the performer of "${song.title}" their current heat details`}
          >
            {notifyingThis ? '✉ Sending…' : '✉ Notify Performer'}
          </button>
        )}
        <button
          className="c-btn c-btn--outline c-btn--sm"
          style={{ padding: '0.15rem 0.45rem', minWidth: 0 }}
          disabled={idx === 0 || reordering_}
          onClick={() => onReorder(heatId, song.id, 'up')}
          aria-label="Move up"
        >↑</button>
        <button
          className="c-btn c-btn--outline c-btn--sm"
          style={{ padding: '0.15rem 0.45rem', minWidth: 0 }}
          disabled={idx === totalSongs - 1 || reordering_}
          onClick={() => onReorder(heatId, song.id, 'down')}
          aria-label="Move down"
        >↓</button>
        {/* Confirmed status — sits between reorder controls and Result/Unassign */}
        <span
          title={song.confirmed ? 'Performer confirmed' : 'Not yet confirmed'}
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: '1.75rem', flexShrink: 0, fontSize: '1.2rem', fontWeight: 700,
            color: song.confirmed ? '#15803d' : '#dc2626',
          }}
        >
          {song.confirmed ? '✓' : '✗'}
        </span>
        <div style={{ position: 'relative' }}>
          <button
            className={`c-btn c-btn--outline c-btn--sm${hasResult ? ' a-result-btn--active' : ''}`}
            onClick={() => setOpenDropdownKey(dropOpen ? null : dropKey)}
            aria-expanded={dropOpen}
          >
            Result {hasResult ? '▲' : '▼'}
          </button>
          {dropOpen && (
            <>
              <div
                style={{ position: 'fixed', inset: 0, zIndex: 98 }}
                onClick={() => setOpenDropdownKey(null)}
              />
              <ResultDropdown
                heatId={heatId}
                song={song}
                onToggleResult={onToggleResult}
                onDeselectAll={onDeselectAll}
                savingResultKeys={savingResultKeys}
                onClose={() => setOpenDropdownKey(null)}
              />
            </>
          )}
        </div>
        {isGrandFinal ? (
          <button
            className="c-btn c-btn--sm"
            style={{ borderColor: '#ef4444', color: '#ef4444', background: 'transparent' }}
            onClick={() => onRemove(heatId, song.id)}
            disabled={actionBusy}
          >
            {actionBusy ? '…' : 'Remove'}
          </button>
        ) : (
          <button
            className="c-btn c-btn--outline c-btn--sm"
            onClick={() => onUnassign(song.id)}
            disabled={actionBusy}
          >
            {actionBusy ? '…' : 'Unassign'}
          </button>
        )}
        {/* FOOM Live entries never appear in Assignments by Song, so this is
            the only place their entry fee can be refunded. */}
        {/* Refund and Email Entrant act on the song by id and need no heat
            relationship, so they work identically in the Grand Final.

            Notify Performer works here too, since heatSongPredicate() taught
            the notify routes the Grand Final's own song set. */}
        <RefundControl song={song} onRefunded={onRefunded} />
        <EmailEntrantControl song={song} />
      </div>
    </li>
  );
}

function HeatPdfButtons({ heat, pdfGenerating, setPdfGenerating, downloadingJudgeForms, onJudgeForms }) {
  const noSongs = heat.songs.length === 0;
  return (
    <div style={{ padding: '0.75rem 1rem 0.75rem', borderTop: '1px solid #f3f4f6', display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
      <button
        className="c-btn c-btn--outline c-btn--sm"
        disabled={pdfGenerating[`${heat.heat_id}-lyrics`] || noSongs}
        onClick={() => downloadLyricsPdf(heat, {
          onStart: () => setPdfGenerating((p) => ({ ...p, [`${heat.heat_id}-lyrics`]: true })),
          onDone:  () => setPdfGenerating((p) => ({ ...p, [`${heat.heat_id}-lyrics`]: false })),
        })}
      >
        {pdfGenerating[`${heat.heat_id}-lyrics`] ? '⏳ Generating…' : '🖨 Print Lyrics'}
      </button>
      <button
        className="c-btn c-btn--outline c-btn--sm"
        disabled={pdfGenerating[`${heat.heat_id}-running`] || noSongs}
        onClick={() => downloadRunningSheetPdf(heat, {
          onStart: () => setPdfGenerating((p) => ({ ...p, [`${heat.heat_id}-running`]: true })),
          onDone:  () => setPdfGenerating((p) => ({ ...p, [`${heat.heat_id}-running`]: false })),
        })}
      >
        {pdfGenerating[`${heat.heat_id}-running`] ? '⏳ Generating…' : '📄 Running Sheet'}
      </button>
      <button
        className="c-btn c-btn--outline c-btn--sm"
        disabled={pdfGenerating[`${heat.heat_id}-voting`] || noSongs}
        onClick={() => downloadVotingSlipPdf(heat, {
          onStart: () => setPdfGenerating((p) => ({ ...p, [`${heat.heat_id}-voting`]: true })),
          onDone:  () => setPdfGenerating((p) => ({ ...p, [`${heat.heat_id}-voting`]: false })),
        })}
      >
        {pdfGenerating[`${heat.heat_id}-voting`] ? '⏳ Generating…' : "🎵 People's Choice"}
      </button>
      <button
        className="c-btn c-btn--outline c-btn--sm"
        disabled={pdfGenerating[`${heat.heat_id}-tech`] || noSongs}
        onClick={() => downloadTechPdf(heat, {
          onStart: () => setPdfGenerating((p) => ({ ...p, [`${heat.heat_id}-tech`]: true })),
          onDone:  () => setPdfGenerating((p) => ({ ...p, [`${heat.heat_id}-tech`]: false })),
        })}
      >
        {pdfGenerating[`${heat.heat_id}-tech`] ? '⏳ Generating…' : '📋 Technical Requirements'}
      </button>
      <button
        className="c-btn c-btn--outline c-btn--sm"
        disabled={downloadingJudgeForms === heat.heat_id || noSongs}
        onClick={() => onJudgeForms(heat)}
      >
        {downloadingJudgeForms === heat.heat_id ? '⏳ Generating…' : '🖨 Print Judge Forms'}
      </button>
      {/* Tally Form — the worksheet for counting People's Choice votes by hand
          at the end of the night. Sits beside Print Judge Forms deliberately:
          both are things you print and write on, unlike the sheets above. */}
      <button
        className="c-btn c-btn--outline c-btn--sm"
        disabled={pdfGenerating[`${heat.heat_id}-tally`] || noSongs}
        onClick={() => downloadTallyFormPdf(heat, {
          onStart: () => setPdfGenerating((p) => ({ ...p, [`${heat.heat_id}-tally`]: true })),
          onDone:  () => setPdfGenerating((p) => ({ ...p, [`${heat.heat_id}-tally`]: false })),
        })}
      >
        {pdfGenerating[`${heat.heat_id}-tally`] ? '⏳ Generating…' : '🧮 Tally Form'}
      </button>
    </div>
  );
}

function HeatNotifyRow({ heat, notifying, onNotifyJudges, onNotifyPerformers, onNotifyMc, onNotifySh, notifiedAt }) {
  const judgesKey    = `${heat.heat_id}-judges`;
  const performersKey = `${heat.heat_id}-performers`;
  const mcKey        = `${heat.heat_id}-mc`;
  const shKey        = `${heat.heat_id}-sh`;
  const judgesTs     = notifiedAt[judgesKey] || heat.judges_notified_at;
  const mcTs         = notifiedAt[mcKey] || heat.mc_notified_at;
  const shTs         = notifiedAt[shKey] || heat.stage_hand_notified_at;
  const busyJudges   = notifying === judgesKey;
  const busyPerf     = notifying === performersKey;
  const busyMc       = notifying === mcKey;
  const busySh       = notifying === shKey;
  const anyBusy      = busyJudges || busyPerf || busyMc || busySh;

  const hasJudges    = (heat.heat_judges || []).length > 0;
  const hasPerformers = (heat.songs || []).length > 0;
  const hasMc        = !!heat.mc_name;
  const hasSh        = !!heat.stage_hand_id;

  const BLUE = '#0ea5e9';
  const GREY = '#9ca3af';

  function fmtTs(ts) {
    if (!ts) return null;
    return new Date(ts).toLocaleString(undefined, {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  }

  return (
    <div style={{ padding: '0.6rem 1rem 1rem', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' }}>
      <button
        className="c-btn c-btn--sm"
        style={{ background: hasJudges ? BLUE : GREY, cursor: hasJudges ? 'pointer' : 'default' }}
        disabled={anyBusy || !hasJudges}
        onClick={() => onNotifyJudges(heat)}
      >
        {busyJudges ? '✉ Sending…' : '✉ Notify Judges'}
      </button>
      {judgesTs && (
        <span style={{ fontSize: '0.78rem', color: '#9ca3af' }}>
          Last notified {fmtTs(judgesTs)}
        </span>
      )}
      <button
        className="c-btn c-btn--sm"
        style={{ background: hasPerformers ? BLUE : GREY, cursor: hasPerformers ? 'pointer' : 'default' }}
        disabled={anyBusy || !hasPerformers}
        onClick={() => onNotifyPerformers(heat)}
      >
        {busyPerf ? '✉ Sending…' : '✉ Notify Performers'}
      </button>
      <button
        className="c-btn c-btn--sm"
        style={{ background: hasMc ? BLUE : GREY, cursor: hasMc ? 'pointer' : 'default' }}
        disabled={anyBusy || !hasMc}
        onClick={() => onNotifyMc(heat)}
      >
        {busyMc ? '✉ Sending…' : '✉ Notify MC'}
      </button>
      {mcTs && (
        <span style={{ fontSize: '0.78rem', color: '#9ca3af' }}>
          MC last notified {fmtTs(mcTs)}
        </span>
      )}
      {/* Stage hand (migration 046). Sits to the right of Notify MC; grey and
          inert until the heat has a stage hand allocated in its Edit form. */}
      <button
        className="c-btn c-btn--sm"
        style={{ background: hasSh ? BLUE : GREY, cursor: hasSh ? 'pointer' : 'default' }}
        disabled={anyBusy || !hasSh}
        onClick={() => onNotifySh(heat)}
        title={hasSh ? `Email ${heat.stage_hand_name} the heat details, running sheet and technical requirements` : 'No stage hand allocated — set one in Edit'}
      >
        {busySh ? '✉ Sending…' : '✉ Notify SH'}
      </button>
      {shTs && (
        <span style={{ fontSize: '0.78rem', color: '#9ca3af' }}>
          SH last notified {fmtTs(shTs)}
        </span>
      )}
    </div>
  );
}

// ─── usePersistentCollapse ───────────────────────────────────────────
// Drop-in replacement for useState(bool) that remembers the flag past unmount.
//
// The `loading` fix stopped a refresh springing collapsed heats open, but leaving
// the Live Heats tab and coming back unmounts the whole panel — and React state
// cannot outlive an unmount, so every collapsed heat opened again. The flags live
// in a module-level Map instead, which survives tab switches and stays scoped to
// the page load: a browser refresh is a deliberate reset and starts everyone open.
//
// Heat cards key on heat id, never on position — renumbering or inserting a heat
// would otherwise hand one heat another's collapse state.
//
// Since 10 Sep 2026 the flags also live in localStorage (one key per panel), so
// a collapsed panel STAYS collapsed across a browser refresh and a new session
// on the same machine. The original module-level Map reset on refresh by
// design, and that was the complaint: every panel Simon had folded away sprang
// open again each time the page reloaded. The Map remains as the in-memory
// copy; localStorage is read once on first mount and written on every change,
// both inside try/catch because the accessor itself can throw in some
// browsers and a failed read must simply mean "default open".
const collapseMemory = new Map();
const COLLAPSE_STORAGE_PREFIX = 'foom:collapse:';

function readStoredCollapse(k) {
  try {
    const raw = window.localStorage.getItem(COLLAPSE_STORAGE_PREFIX + k);
    if (raw === '0') return false;
    if (raw === '1') return true;
  } catch { /* storage unavailable — fall through to the default */ }
  return undefined;
}

function writeStoredCollapse(k, value) {
  try {
    window.localStorage.setItem(COLLAPSE_STORAGE_PREFIX + k, value ? '1' : '0');
  } catch { /* storage unavailable — the in-memory Map still holds it for this page load */ }
}

function usePersistentCollapse(key, defaultOpen = true) {
  const k = String(key);
  const [isOpen, setIsOpenState] = useState(() => {
    if (collapseMemory.has(k)) return collapseMemory.get(k);
    const stored = readStoredCollapse(k);
    const value = stored ?? defaultOpen;
    collapseMemory.set(k, value);
    return value;
  });

  // Takes a boolean or an updater, exactly like a useState setter, so every
  // existing call site works untouched. The writes happen inside the updater
  // so they always see the value React actually committed.
  const setIsOpen = useCallback((next) => {
    setIsOpenState((prev) => {
      const value = typeof next === 'function' ? next(prev) : next;
      collapseMemory.set(k, value);
      writeStoredCollapse(k, value);
      return value;
    });
  }, [k]);

  return [isOpen, setIsOpen];
}

function GrandFinalCard({
  heat, reordering, removingFromGf, onRefunded,
  onNotifyPerformer, notifyingSong,
  openDropdownKey, setOpenDropdownKey,
  onToggleResult, onDeselectAll, savingResultKeys,
  onReorder, onRemove,
  pdfGenerating, setPdfGenerating,
  downloadingJudgeForms, onJudgeForms,
  notifying, onNotifyJudges, onNotifyPerformers, onNotifyMc, onNotifySh, notifiedAt,
  onInsertBreak, onRemoveBreak, breakBusy,
  onEdit, editingHeatId, editProps,
}) {
  const heatJudges = heat.heat_judges || [];
  const [isOpen, setIsOpen] = usePersistentCollapse(`heat:${heat.heat_id}`);
  const isEditing = editingHeatId === heat.heat_id;

  // Same confirmation tally as RegularHeatCard. Meaningful now that the Grand
  // Final songs query returns a per-song `confirmed` flag — before that it was
  // undefined and every row read as unconfirmed.
  const confirmedSongs   = heat.songs.filter((s) => s.confirmed).length;
  const unconfirmedSongs = heat.songs.filter((s) => !s.confirmed).length;

  return (
    <div className="a-heat-card a-heat-card--gf">
      {/* ── Clickable header — click anywhere to collapse/expand ── */}
      <div
        className="a-heat-card__header--gf"
        onClick={() => setIsOpen((v) => !v)}
        style={{ cursor: 'pointer', userSelect: 'none', position: 'relative', flexWrap: 'wrap', rowGap: '0.35rem' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          {/* ▶ chevron on LEFT — same pattern as RegularHeatCard */}
          <span className="a-chevron" style={{ transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
          <span style={{ color: '#d4af37', fontWeight: 700, fontSize: '1.05rem' }}>★ Grand Final</span>
          <span style={{ color: '#6b7280', fontSize: '0.95rem' }}>{heat.heat_name}</span>
        </div>
        <InsertBreakControl
          heat={heat}
          onInsert={onInsertBreak}
          busy={breakBusy === heat.heat_id}
        />
        {/* Flows inline rather than absolutely centred at left:50%. RegularHeatCard
            hit exactly this: a centred MC name sits underneath the judge lozenges
            as soon as the right-hand group grows, and this header now carries the
            same content it does. */}
        {/* Stage hand (migration 046), immediately left of the MC, with the
            same green tick / red cross as the MC (migration 047). is_test
            rows are excluded server-side so an Emails-tab test never shows
            green. */}
        {heat.stage_hand_name && (
          <span style={{ marginLeft: '1rem', fontSize: '0.85rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
            SH: {heat.stage_hand_name}
            <span
              title={heat.stage_hand_confirmed ? 'Stage hand confirmed this heat' : 'Stage hand has not yet confirmed'}
              style={{
                marginLeft: '0.35rem',
                fontWeight: 700,
                color: heat.stage_hand_confirmed ? '#15803d' : '#dc2626',
              }}
            >
              {heat.stage_hand_confirmed ? '✓' : '✗'}
            </span>
          </span>
        )}
        {heat.mc_name && (
          <span style={{
            marginLeft: '1rem', fontSize: '0.85rem', fontWeight: 600, whiteSpace: 'nowrap',
          }}>
            MC: {heat.mc_name}
            {/* MC heat confirmation (migration 044) — same green tick / red
                cross as the judge lozenges. Always one or the other, so a heat
                whose MC has not replied reads as unconfirmed rather than as
                though the feature were missing. is_test rows are excluded
                server-side so an Emails-tab test never shows green. */}
            <span
              title={heat.mc_confirmed ? 'MC confirmed this heat' : 'MC has not yet confirmed'}
              style={{
                marginLeft: '0.35rem',
                fontWeight: 700,
                color: heat.mc_confirmed ? '#15803d' : '#dc2626',
              }}
            >
              {heat.mc_confirmed ? '✓' : '✗'}
            </span>
          </span>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap', marginLeft: 'auto' }}>
          {heatJudges.map((j) => (
            <span key={j.judge_id} className="a-judge-lozenge">
              {j.judge_name}
              {/* Judge heat confirmation (migration 043): green tick once the
                  judge has confirmed, red cross until then. Always one or the
                  other — a heat with no replies should read as unconfirmed,
                  not as though the feature were missing. is_test rows are
                  excluded server-side so an Emails-tab test never shows green. */}
              <span
                title={j.confirmed ? 'Confirmed this heat' : 'Not yet confirmed'}
                style={{
                  marginLeft: '0.35rem',
                  fontWeight: 700,
                  color: j.confirmed ? '#15803d' : '#dc2626',
                }}
              >
                {j.confirmed ? '✓' : '✗'}
              </span>
            </span>
          ))}
          {/* Confirmed / unconfirmed song badges — shown conditionally */}
          {confirmedSongs > 0 && (
            <span style={{
              background: '#fff', borderRadius: '10px',
              padding: '0.1rem 0.5rem', fontSize: '1.1rem', fontWeight: 700,
              color: '#15803d', whiteSpace: 'nowrap', lineHeight: 1.3,
            }}>
              ✓ {confirmedSongs}
            </span>
          )}
          {unconfirmedSongs > 0 && (
            <span style={{
              background: '#fff', borderRadius: '10px',
              padding: '0.1rem 0.5rem', fontSize: '1.1rem', fontWeight: 700,
              color: '#dc2626', whiteSpace: 'nowrap', lineHeight: 1.3,
            }}>
              ✗ {unconfirmedSongs}
            </span>
          )}
          {/* Song count badge */}
          <span className="badge" style={{ background: '#1a1a2e', color: '#fff', borderRadius: '50%', minWidth: '1.6rem', textAlign: 'center' }}>
            {heat.songs.length}
          </span>
          <button
            className="c-btn c-btn--outline c-btn--sm"
            style={{ marginLeft: '0.25rem', fontWeight: 600 }}
            onClick={(e) => { e.stopPropagation(); onEdit(heat); }}
          >
            {isEditing ? 'Cancel' : 'Edit'}
          </button>
        </div>
      </div>

      {isEditing && editProps && (
        <HeatLogisticsEdit key={`edit-${heat.heat_id}`} heatId={heat.heat_id} {...editProps} />
      )}

      {isOpen && (
        <>
          {/* Date / arrival / venue strip — same as RegularHeatCard. The Grand
              Final is a live_heats row like any other and has all three. */}
          {(heat.heat_date || heat.venue_name) && (
            <div style={{ fontSize: '0.78rem', color: '#6b7280', padding: '0.3rem 1rem 0.1rem', lineHeight: 1.6 }}>
              {heat.heat_date && (
                <span>{new Date(heat.heat_date).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}</span>
              )}
              {heat.performer_arrival_time && (
                <span> · Arrival {heat.performer_arrival_time.slice(0, 5)}</span>
              )}
              {heat.venue_name && (
                <span>{(heat.heat_date || heat.performer_arrival_time) ? ' · ' : ''}{heat.venue_name}</span>
              )}
            </div>
          )}

          {heat.songs.length === 0 ? (
            <p className="a-heat-card__empty">No results set yet — songs appear here once a result is assigned in a regular heat.</p>
          ) : (
            <ul className="a-heat-card__list">
              {heat.songs.map((s, idx) => (
                <React.Fragment key={s.id}>
                  <HeatSongRow
                    heatId={heat.heat_id}
                    song={s}
                    idx={idx}
                    totalSongs={heat.songs.length}
                    isGrandFinal
                    reordering={reordering}
                    removing={removingFromGf}
                    unassigning={null}
                    onReorder={onReorder}
                    onUnassign={null}
                    onRemove={onRemove}
                    onRefunded={onRefunded}
                    onNotifyPerformer={onNotifyPerformer}
                    notifyingSong={notifyingSong}
                    openDropdownKey={openDropdownKey}
                    setOpenDropdownKey={setOpenDropdownKey}
                    onToggleResult={onToggleResult}
                    onDeselectAll={onDeselectAll}
                    savingResultKeys={savingResultKeys}
                  />
                  {/* A break sits after a position, so it follows the row at
                      that index whichever song currently occupies it. */}
                  {(heat.breaks || []).includes(idx + 1) && (
                    <HeatBreakRow
                      position={idx + 1}
                      busy={breakBusy === heat.heat_id}
                      onRemove={(pos) => onRemoveBreak(heat.heat_id, pos)}
                    />
                  )}
                </React.Fragment>
              ))}
            </ul>
          )}

          <HeatPdfButtons
            heat={heat}
            pdfGenerating={pdfGenerating}
            setPdfGenerating={setPdfGenerating}
            downloadingJudgeForms={downloadingJudgeForms}
            onJudgeForms={onJudgeForms}
          />
          <HeatNotifyRow
            heat={heat}
            notifying={notifying}
            onNotifyJudges={onNotifyJudges}
            onNotifyPerformers={onNotifyPerformers}
            onNotifyMc={onNotifyMc}
            onNotifySh={onNotifySh}
            notifiedAt={notifiedAt}
          />
        </>
      )}
    </div>
  );
}

function RegularHeatCard({
  heat, reordering, unassigning,
  openDropdownKey, setOpenDropdownKey,
  onToggleResult, onDeselectAll, savingResultKeys,
  onReorder, onUnassign,
  pdfGenerating, setPdfGenerating,
  downloadingJudgeForms, onJudgeForms,
  notifying, onNotifyJudges, onNotifyPerformers, onNotifyMc, onNotifySh, notifiedAt,
  onNotifyPerformer, notifyingSong, onRefunded,
  onInsertBreak, onRemoveBreak, breakBusy,
  onEdit, editingHeatId, editProps,
}) {
  const heatJudges      = heat.heat_judges || [];
  const [isOpen, setIsOpen] = usePersistentCollapse(`heat:${heat.heat_id}`);

  const confirmedSongs   = heat.songs.filter((s) => s.confirmed).length;
  const unconfirmedSongs = heat.songs.filter((s) => !s.confirmed).length;

  return (
    <div className="a-heat-card">
      {/* ── Clickable gold header — click anywhere to collapse/expand ── */}
      <div
        className="a-heat-card__header"
        onClick={() => setIsOpen((v) => !v)}
        style={{ cursor: 'pointer', userSelect: 'none', position: 'relative', flexWrap: 'wrap', rowGap: '0.35rem' }}
      >
        {/* ▶ chevron on LEFT — same pattern as Create / Unassigned section toggles */}
        <span className="a-chevron" style={{ transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)', marginRight: '0.15rem' }}>▶</span>
        <span className="a-heat-card__number">Heat {heat.heat_number}</span>
        <span className="a-heat-card__name">{heat.heat_name}</span>
        <InsertBreakControl
          heat={heat}
          onInsert={onInsertBreak}
          busy={breakBusy === heat.heat_id}
        />
        {/* The MC name used to be absolutely centred at left:50%, which meant it
            sat underneath the judge lozenges as soon as the right-hand group
            grew. It now flows in the header like everything else. */}
        {/* Stage hand (migration 046), immediately left of the MC, with the
            same green tick / red cross as the MC (migration 047). is_test
            rows are excluded server-side so an Emails-tab test never shows
            green. */}
        {heat.stage_hand_name && (
          <span style={{ marginLeft: '1rem', fontSize: '0.85rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
            SH: {heat.stage_hand_name}
            <span
              title={heat.stage_hand_confirmed ? 'Stage hand confirmed this heat' : 'Stage hand has not yet confirmed'}
              style={{
                marginLeft: '0.35rem',
                fontWeight: 700,
                color: heat.stage_hand_confirmed ? '#15803d' : '#dc2626',
              }}
            >
              {heat.stage_hand_confirmed ? '✓' : '✗'}
            </span>
          </span>
        )}
        {heat.mc_name && (
          <span style={{
            marginLeft: '1rem', fontSize: '0.85rem', fontWeight: 600, whiteSpace: 'nowrap',
          }}>
            MC: {heat.mc_name}
            {/* MC heat confirmation (migration 044) — same green tick / red
                cross as the judge lozenges. Always one or the other, so a heat
                whose MC has not replied reads as unconfirmed rather than as
                though the feature were missing. is_test rows are excluded
                server-side so an Emails-tab test never shows green. */}
            <span
              title={heat.mc_confirmed ? 'MC confirmed this heat' : 'MC has not yet confirmed'}
              style={{
                marginLeft: '0.35rem',
                fontWeight: 700,
                color: heat.mc_confirmed ? '#15803d' : '#dc2626',
              }}
            >
              {heat.mc_confirmed ? '✓' : '✗'}
            </span>
          </span>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap', marginLeft: 'auto' }}>
          {heatJudges.map((j) => (
            <span key={j.judge_id} className="a-judge-lozenge">
              {j.judge_name}
              {/* Judge heat confirmation (migration 043): green tick once the
                  judge has confirmed, red cross until then. Always one or the
                  other — a heat with no replies should read as unconfirmed,
                  not as though the feature were missing. is_test rows are
                  excluded server-side so an Emails-tab test never shows green. */}
              <span
                title={j.confirmed ? 'Confirmed this heat' : 'Not yet confirmed'}
                style={{
                  marginLeft: '0.35rem',
                  fontWeight: 700,
                  color: j.confirmed ? '#15803d' : '#dc2626',
                }}
              >
                {j.confirmed ? '✓' : '✗'}
              </span>
            </span>
          ))}
          {/* Confirmed / unconfirmed song badges — shown conditionally */}
          {confirmedSongs > 0 && (
            <span style={{
              background: '#fff', borderRadius: '10px',
              padding: '0.1rem 0.5rem', fontSize: '1.1rem', fontWeight: 700,
              color: '#15803d', whiteSpace: 'nowrap', lineHeight: 1.3,
            }}>
              ✓ {confirmedSongs}
            </span>
          )}
          {unconfirmedSongs > 0 && (
            <span style={{
              background: '#fff', borderRadius: '10px',
              padding: '0.1rem 0.5rem', fontSize: '1.1rem', fontWeight: 700,
              color: '#dc2626', whiteSpace: 'nowrap', lineHeight: 1.3,
            }}>
              ✗ {unconfirmedSongs}
            </span>
          )}
          {/* Song count badge */}
          <span className="badge" style={{ background: '#1a1a2e', color: '#fff', borderRadius: '50%', minWidth: '1.6rem', textAlign: 'center' }}>
            {heat.songs.length}
          </span>
          <button
            className="c-btn c-btn--outline c-btn--sm"
            style={{ marginLeft: '0.25rem', fontWeight: 600 }}
            onClick={(e) => { e.stopPropagation(); onEdit(heat); }}
          >
            {editingHeatId === heat.heat_id ? 'Cancel' : 'Edit'}
          </button>
        </div>
      </div>

      {editingHeatId === heat.heat_id && editProps && (
        <HeatLogisticsEdit key={`edit-${heat.heat_id}`} heatId={heat.heat_id} {...editProps} />
      )}

      {isOpen && (
        <>
          {(heat.heat_date || heat.venue_name) && (
            <div style={{ fontSize: '0.78rem', color: '#6b7280', padding: '0.3rem 1rem 0.1rem', lineHeight: 1.6 }}>
              {heat.heat_date && (
                <span>{new Date(heat.heat_date).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}</span>
              )}
              {heat.performer_arrival_time && (
                <span> · Arrival {heat.performer_arrival_time.slice(0, 5)}</span>
              )}
              {heat.venue_name && (
                <span>{(heat.heat_date || heat.performer_arrival_time) ? ' · ' : ''}{heat.venue_name}</span>
              )}
            </div>
          )}

          {heat.songs.length === 0 ? (
            <p className="a-heat-card__empty">No songs assigned yet.</p>
          ) : (
            <>
              {/* Column header row — "CONFIRMED" aligns over the ✓/✗ icon in each row.
                  The icon sits between the ↓ and Result buttons in the right controls
                  flex group; padding-right approximates that offset from the row edge. */}
              <div style={{
                display: 'flex', justifyContent: 'flex-end',
                padding: '0.3rem 1rem 0',
                fontSize: '0.6rem', fontWeight: 700, color: '#9ca3af',
                letterSpacing: '0.06em', textTransform: 'uppercase',
              }}>
                <span style={{ marginRight: '9.5rem' }}>Confirmed</span>
              </div>
              <ul className="a-heat-card__list">
              {heat.songs.map((s, idx) => (
                <React.Fragment key={s.id}>
                  <HeatSongRow
                    heatId={heat.heat_id}
                    song={s}
                    idx={idx}
                    totalSongs={heat.songs.length}
                    isGrandFinal={false}
                    reordering={reordering}
                    removing={null}
                    unassigning={unassigning}
                    onReorder={onReorder}
                    onUnassign={onUnassign}
                    onRemove={null}
                    openDropdownKey={openDropdownKey}
                    setOpenDropdownKey={setOpenDropdownKey}
                    onToggleResult={onToggleResult}
                    onDeselectAll={onDeselectAll}
                    savingResultKeys={savingResultKeys}
                    onNotifyPerformer={onNotifyPerformer}
                    notifyingSong={notifyingSong}
                    onRefunded={onRefunded}
                  />
                  {/* A break sits after a position, so it follows the row at
                      that index whichever song currently occupies it. */}
                  {(heat.breaks || []).includes(idx + 1) && (
                    <HeatBreakRow
                      position={idx + 1}
                      busy={breakBusy === heat.heat_id}
                      onRemove={(pos) => onRemoveBreak(heat.heat_id, pos)}
                    />
                  )}
                </React.Fragment>
              ))}
            </ul>
            </>
          )}

          <HeatPdfButtons
            heat={heat}
            pdfGenerating={pdfGenerating}
            setPdfGenerating={setPdfGenerating}
            downloadingJudgeForms={downloadingJudgeForms}
            onJudgeForms={onJudgeForms}
          />
          <HeatNotifyRow
            heat={heat}
            notifying={notifying}
            onNotifyJudges={onNotifyJudges}
            onNotifyPerformers={onNotifyPerformers}
            onNotifyMc={onNotifyMc}
            onNotifySh={onNotifySh}
            notifiedAt={notifiedAt}
          />
        </>
      )}
    </div>
  );
}

// ─── LiveHeatsPanel helpers (defined outside to prevent remount on state change) ─
function HeatLogisticsEdit({
  heatId,
  heatEditForm,
  setHeatEditForm,
  savingHeat,
  saveHeatError,
  handleSaveHeat,
  setEditingHeatId,
  setSaveHeatError,
  venues,
  mcs,
  stageHands = [],
}) {
  // Pre-select the venue if the heat's current venue_name exactly matches one in the list
  const matchedVenue = venues.find((v) => v.name === heatEditForm.venue_name);
  const [selectedVenueId, setSelectedVenueId] = useState(
    matchedVenue ? String(matchedVenue.id) : ''
  );

  function handleVenueChange(venueId) {
    setSelectedVenueId(venueId);
    if (venueId) {
      const v = venues.find((v) => String(v.id) === venueId);
      if (v) setHeatEditForm((p) => ({ ...p, venue_name: v.name, venue_address: v.address || '' }));
    }
    // Clearing the dropdown preserves current venue_name/venue_address values
    // so the "not in list" fallback display remains visible
  }

  const hasExistingVenue = Boolean(heatEditForm.venue_name || heatEditForm.venue_address);

  return (
    <tr>
      <td colSpan={6} style={{ background: '#f9fafb', padding: '1rem 1.25rem', borderTop: 'none' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', maxWidth: '640px' }}>
          <div className="c-form-group" style={{ marginBottom: 0 }}>
            <label className="c-label" style={{ fontSize: '0.78rem' }}>Heat Date</label>
            <input
              className="c-input"
              type="date"
              value={heatEditForm.heat_date}
              onChange={(e) => setHeatEditForm((p) => ({ ...p, heat_date: e.target.value }))}
            />
          </div>
          <div className="c-form-group" style={{ marginBottom: 0 }}>
            <label className="c-label" style={{ fontSize: '0.78rem' }}>Performer Arrival Time</label>
            <input
              className="c-input"
              type="time"
              value={heatEditForm.performer_arrival_time}
              onChange={(e) => setHeatEditForm((p) => ({ ...p, performer_arrival_time: e.target.value }))}
            />
          </div>
          {/* Judge Arrival Time (migration 045). MCs are told this same time.
              Left blank it falls back to performer arrival + 30 min, so the
              default behaviour needs no input — this field exists for the heat
              that needs to differ, e.g. a Grand Final briefing. */}
          <div className="c-form-group" style={{ marginBottom: 0 }}>
            <label className="c-label" style={{ fontSize: '0.78rem' }}>Judge / MC Arrival Time</label>
            <input
              className="c-input"
              type="time"
              value={heatEditForm.judge_arrival_time}
              onChange={(e) => setHeatEditForm((p) => ({ ...p, judge_arrival_time: e.target.value }))}
            />
            <p style={{ fontSize: '0.7rem', color: '#6b7280', margin: '0.25rem 0 0' }}>
              Leave blank for 30 minutes after performer arrival.
            </p>
          </div>

          {/* Venue dropdown — spans both columns */}
          <div className="c-form-group" style={{ marginBottom: 0, gridColumn: '1 / -1' }}>
            <label className="c-label" style={{ fontSize: '0.78rem' }}>Venue</label>
            <select
              className="c-select"
              value={selectedVenueId}
              onChange={(e) => handleVenueChange(e.target.value)}
            >
              <option value="">— Select Venue —</option>
              {venues.map((v) => (
                <option key={v.id} value={String(v.id)}>{v.name}</option>
              ))}
            </select>
          </div>

          {/* Selected venue: read-only name + address */}
          {selectedVenueId && (
            <>
              <div className="c-form-group" style={{ marginBottom: 0 }}>
                <label className="c-label" style={{ fontSize: '0.78rem' }}>Venue Name</label>
                <input
                  className="c-input"
                  readOnly
                  value={heatEditForm.venue_name}
                  style={{ background: '#f3f4f6', color: '#6b7280', cursor: 'default' }}
                />
              </div>
              <div className="c-form-group" style={{ marginBottom: 0 }}>
                <label className="c-label" style={{ fontSize: '0.78rem' }}>Venue Address</label>
                <input
                  className="c-input"
                  readOnly
                  value={heatEditForm.venue_address}
                  style={{ background: '#f3f4f6', color: '#6b7280', cursor: 'default' }}
                />
              </div>
            </>
          )}

          {/* No venue selected but heat has existing venue data → show amber notice */}
          {!selectedVenueId && hasExistingVenue && (
            <div style={{
              gridColumn: '1 / -1', padding: '0.4rem 0.65rem',
              background: '#fef3c7', borderRadius: '4px',
              fontSize: '0.82rem', color: '#92400e',
            }}>
              <span style={{ fontWeight: 600 }}>Current venue (not in Venues list):</span>{' '}
              {heatEditForm.venue_name}
              {heatEditForm.venue_address ? `, ${heatEditForm.venue_address}` : ''}
              {' '}— select a venue above to replace it.
            </div>
          )}

          <div className="c-form-group" style={{ marginBottom: 0 }}>
            <label className="c-label" style={{ fontSize: '0.78rem' }}>Max Performers</label>
            <input
              className="c-input"
              type="number"
              min="1"
              value={heatEditForm.max_performers}
              onChange={(e) => setHeatEditForm((p) => ({ ...p, max_performers: e.target.value }))}
              placeholder="e.g. 6"
            />
          </div>
          <div className="c-form-group" style={{ marginBottom: 0 }}>
            <label className="c-label" style={{ fontSize: '0.78rem' }}>MC</label>
            <select
              className="c-select"
              value={heatEditForm.mc_name || ''}
              onChange={(e) => setHeatEditForm((p) => ({ ...p, mc_name: e.target.value }))}
            >
              <option value="">— No MC —</option>
              {mcs.map((m) => (
                <option key={m.id} value={m.full_name}>{m.full_name}</option>
              ))}
            </select>
          </div>
          {/* Stage hand (migration 046). Stored by id, not by name, so renaming
              a stage hand in the roster cannot silently detach them from a heat
              the way it can for an MC. */}
          <div className="c-form-group" style={{ marginBottom: 0 }}>
            <label className="c-label" style={{ fontSize: '0.78rem' }}>Stage hand</label>
            <select
              className="c-select"
              value={heatEditForm.stage_hand_id || ''}
              onChange={(e) => setHeatEditForm((p) => ({ ...p, stage_hand_id: e.target.value }))}
            >
              <option value="">— No stage hand —</option>
              {stageHands.map((s) => (
                <option key={s.id} value={String(s.id)}>{s.full_name}</option>
              ))}
            </select>
          </div>
        </div>
        {saveHeatError && <div className="c-alert" style={{ marginTop: '0.5rem' }}>{saveHeatError}</div>}
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
          <button
            className="c-btn c-btn--primary c-btn--sm"
            onClick={() => handleSaveHeat(heatId)}
            disabled={savingHeat}
          >
            {savingHeat ? 'Saving…' : 'Save'}
          </button>
          <button
            className="c-btn c-btn--outline c-btn--sm"
            onClick={() => { setEditingHeatId(null); setSaveHeatError(''); }}
            disabled={savingHeat}
          >
            Cancel
          </button>
        </div>
      </td>
    </tr>
  );
}

// ─── LiveHeatsPanel ───────────────────────────────────────────────────────────
// Sentinel value for the "Withdrawn" entry in the Assign-to-Heat dropdown.
// Deliberately not a valid heat_id, and deliberately not '' — the empty string
// is the "— select heat —" placeholder.
const WITHDRAWN_OPTION = '__withdrawn__';

function LiveHeatsPanel() {
  const currentYear = new Date().getFullYear();
  const [year, setYear]               = useState(currentYear);
  // The tab follows the ACTIVE COMPETITION's year, not the calendar's. FOOM
  // 2027 is started in September 2026: on the calendar year this tab opened
  // on 2026's heats, and 2027 was not even in the Year list until January —
  // so next year's heats could not be created, or were filed under 2026.
  const [compYear, setCompYear]       = useState(null);
  useEffect(() => {
    let alive = true;
    loadCompetition().then((s) => {
      const y = s.ok && s.competition ? Number(s.competition.year) : null;
      if (alive && y) { setCompYear(y); setYear(y); }
    });
    return () => { alive = false; };
  }, []);
  const yearOptions = [...new Set([compYear, currentYear, currentYear - 1, currentYear - 2].filter(Boolean))].sort((a, b) => b - a);
  const [heats, setHeats]             = useState([]);
  const [unassigned, setUnassigned]   = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');

  // Venues list (fetched once on mount, used in both create and edit forms)
  const [venues, setVenues]           = useState([]);

  // MCs list (fetched once on mount, used in edit forms and the MCs panel)
  const [mcs, setMcs]                 = useState([]);
  const [mcsOpen, setMcsOpen]         = usePersistentCollapse('heats:mcs');
  const [mcEditMode, setMcEditMode]   = useState(null); // null | 'add' | { id, full_name, email, phone, notes }
  const [mcForm, setMcForm]           = useState({ full_name: '', email: '', phone: '', notes: '' });
  const [mcSaving, setMcSaving]       = useState(false);
  const [mcError, setMcError]         = useState('');

  // Stage hands roster (migration 046) — same shape and panel as the MCs
  const [stageHands, setStageHands]   = useState([]);
  const [shOpen, setShOpen]           = usePersistentCollapse('heats:stage-hands');
  const [shEditMode, setShEditMode]   = useState(null); // null | 'add' | { id, full_name, email, phone, notes }
  const [shForm, setShForm]           = useState({ full_name: '', email: '', phone: '', notes: '' });
  const [shSaving, setShSaving]       = useState(false);
  const [shError, setShError]         = useState('');

  // Viewers (10 Sep 2026) — login accounts with role 'observer' that land on
  // /viewer/results and see the Results tab alone. Same panel shape as the
  // MC and Stage hand rosters, but these are real accounts with a password.
  const [viewers, setViewers]         = useState([]);
  const [viewersOpen, setViewersOpen] = usePersistentCollapse('heats:viewers');
  const [viewerEditMode, setViewerEditMode] = useState(null); // null | 'add' | { id, ... }
  const [viewerForm, setViewerForm]   = useState({ full_name: '', email: '', password: '', confirm: '', is_active: true });
  const [viewerSaving, setViewerSaving] = useState(false);
  const [viewerError, setViewerError] = useState('');
  const [viewerLinkBusy, setViewerLinkBusy] = useState(null);  // viewer id being emailed
  const [viewerLinkSent, setViewerLinkSent] = useState({});    // { viewerId: ISO time }
  const [viewerNotice, setViewerNotice] = useState('');  // green line after a grant

  // Create-heat form state
  const [numHeats, setNumHeats]       = useState(3);  // frontend-only: controls dropdown length
  const [heatNumber, setHeatNumber]   = useState('');
  const [createForm, setCreateForm]   = useState({ heat_date: '', performer_arrival_time: '', venue_name: '', venue_address: '', max_performers: '' });
  const [createVenueId, setCreateVenueId] = useState(''); // selected venue for the create form
  const [creating, setCreating]       = useState(false);
  const [createError, setCreateError] = useState('');

  // Bulk assignment for unassigned songs
  const [selectedUnassigned, setSelectedUnassigned] = useState(new Set());
  const [bulkHeatId, setBulkHeatId]                 = useState('');
  const [bulkAssigning, setBulkAssigning]           = useState(false);

  // Renumber heats (close gaps after a deletion)
  const [renumbering, setRenumbering] = useState(false);
  const [renumberMsg, setRenumberMsg] = useState('');

  // Inline-edit state for existing heats
  const [editingHeatId, setEditingHeatId] = useState(null);
  const [heatEditForm, setHeatEditForm]   = useState({});
  const [savingHeat, setSavingHeat]       = useState(false);
  const [saveHeatError, setSaveHeatError] = useState('');

  // PDF generating state: key = `${heatId}-lyrics` | `${heatId}-tech`
  const [pdfGenerating, setPdfGenerating] = useState({});

  // Assign/unassign state
  const [assignSelections, setAssignSelections] = useState({});
  const [assigning, setAssigning]         = useState(null);
  const [unassigning, setUnassigning]     = useState(null);
  // Reorder state: songId -> true while the PATCH is in-flight
  const [reordering, setReordering]       = useState({});

  // Result dropdown state
  const [openDropdownKey, setOpenDropdownKey]     = useState(null);
  const [savingResultKeys, setSavingResultKeys]   = useState(new Set());

  // Grand Final remove state
  const [removingFromGf, setRemovingFromGf]       = useState(null);

  // Notify state
  const [notifying, setNotifying]         = useState(null);
  const [notifiedAt, setNotifiedAt]       = useState({});
  // Single-performer re-notify: song id while its send is in flight, and the
  // set of song ids already sent this session so the dialog can warn that a
  // second send kills the link from the first.
  const [notifyingSong, setNotifyingSong] = useState(null);
  const [songNotified, setSongNotified]   = useState({});

  // Breaks within a heat — heat_id while an add/remove is in flight.
  const [breakBusy, setBreakBusy] = useState(null);
  // Confirmation dialog: null | { type: 'judges'|'performers', heat }
  const [notifyHeatConfirm, setNotifyHeatConfirm] = useState(null);

  // Judge forms PDF state
  const [downloadingJudgeForms, setDownloadingJudgeForms] = useState(null);

  // Section collapse state
  const [createHeatOpen, setCreateHeatOpen]       = usePersistentCollapse('heats:create');
  const [unassignedOpen, setUnassignedOpen]        = usePersistentCollapse('heats:unassigned');
  const [heatSummaryOpen, setHeatSummaryOpen]      = usePersistentCollapse('heats:summary');

  // All judges (for assignment dropdowns in the heats table)
  const [allJudges, setAllJudges] = useState([]);
  const [judgeAdding, setJudgeAdding]   = useState({});
  const [judgeRemoving, setJudgeRemoving] = useState({});
  const [judgeErrors, setJudgeErrors]   = useState({});

  // `loading` gates the ENTIRE heats tree — see the `{!loading && (` further
  // down. Flipping it unmounts every heat card, and a card's collapsed/expanded
  // state is per-card, defaulting to open (see usePersistentCollapse, which now
  // also carries that state across a tab switch).
  // So every action that refreshed afterwards (Notify performer, reorder,
  // refund, insert break…) sprang all the collapsed heats back open — the
  // performer you notified in Heat 2 re-expanded Heat 1.
  //
  // Only the FIRST load has nothing to show, so only the first load may blank
  // the tree. A refresh keeps the cards mounted and swaps their data underneath,
  // which preserves collapse state and stops the whole tab flashing.
  const hasLoaded = useRef(false);

  const loadData = useCallback(async (yr) => {
    if (!hasLoaded.current) setLoading(true);
    setError('');
    try {
      const res = await api.get(`/api/admin/heats?year=${yr}`);
      setHeats(res.data.heats);
      setUnassigned(res.data.unassigned);
      hasLoaded.current = true;
    } catch {
      setError('Failed to load heats data.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Switching year IS a fresh load — the heats on screen belong to the old year
  // and their ids won't survive, so the spinner is right there.
  useEffect(() => { hasLoaded.current = false; loadData(year); }, [year, loadData]);

  /**
   * Close gaps in heat numbering (e.g. 2,3,4,5 -> 1,2,3,4 after Heat 1 is
   * deleted). Runs the server's dry run first and shows exactly what will
   * change, so the preview and the real thing come from the same logic rather
   * than being computed twice. The Grand Final is left alone.
   */
  async function handleRenumber() {
    setRenumbering(true);
    setRenumberMsg('');
    try {
      const preview = await api.post('/api/admin/heats/renumber', {
        dry_run: true, competition_year: year,
      });
      const changes = preview.data.changes || [];

      if (changes.length === 0) {
        setRenumberMsg('Heat numbering is already sequential — nothing to change.');
        return;
      }

      const lines = changes
        .map((c) => `  • ${c.old_name}  →  ${c.new_name}${c.name_kept ? '  (custom name kept)' : ''}`)
        .join('\n');
      const kept = changes.some((c) => c.name_kept)
        ? '\n\nHeats with a custom name keep it; only the number changes.'
        : '';

      if (!window.confirm(
        `Renumber ${changes.length} heat${changes.length === 1 ? '' : 's'}?\n\n${lines}\n\n` +
        `Dates, venues, judges and song assignments are unaffected.${kept}\n\n` +
        `Only do this if heat numbers haven't been sent to contestants — invitations ` +
        `and calendar invites already sent would no longer match.`
      )) return;

      const res = await api.post('/api/admin/heats/renumber', { competition_year: year });
      setRenumberMsg(`Renumbered ${(res.data.changes || []).length} heat(s).`);
      loadData(year);
    } catch (err) {
      setRenumberMsg(err.response?.data?.error || 'Failed to renumber heats.');
    } finally {
      setRenumbering(false);
    }
  }
  useEffect(() => {
    api.get('/api/admin/judges').then((r) => setAllJudges(r.data.judges || [])).catch(() => {});
  }, []);
  useEffect(() => {
    api.get('/api/admin/venues').then((r) => setVenues(r.data.venues || [])).catch(() => {});
  }, []);
  useEffect(() => {
    api.get('/api/admin/mcs').then((r) => setMcs(r.data.mcs || [])).catch(() => {});
  }, []);
  useEffect(() => {
    api.get('/api/admin/stage-hands').then((r) => setStageHands(r.data.stage_hands || [])).catch(() => {});
  }, []);
  useEffect(() => {
    api.get('/api/admin/viewers').then((r) => setViewers(r.data.viewers || [])).catch(() => {});
  }, []);

  function startAddMc() {
    setMcEditMode('add');
    setMcForm({ full_name: '', email: '', phone: '', notes: '' });
    setMcError('');
  }
  function startEditMc(mc) {
    setMcEditMode(mc);
    setMcForm({ full_name: mc.full_name, email: mc.email || '', phone: mc.phone || '', notes: mc.notes || '' });
    setMcError('');
  }
  async function saveMc() {
    if (!mcForm.full_name.trim()) { setMcError('Name is required.'); return; }
    setMcSaving(true);
    setMcError('');
    try {
      if (mcEditMode === 'add') {
        const r = await api.post('/api/admin/mcs', mcForm);
        setMcs((prev) => [...prev, r.data.mc].sort((a, b) => a.full_name.localeCompare(b.full_name)));
      } else {
        const r = await api.put(`/api/admin/mcs/${mcEditMode.id}`, mcForm);
        setMcs((prev) => prev.map((m) => m.id === r.data.mc.id ? r.data.mc : m));
      }
      setMcEditMode(null);
    } catch (err) {
      setMcError(err.response?.data?.error || 'Failed to save.');
    } finally {
      setMcSaving(false);
    }
  }
  async function deleteMc(mc) {
    if (!window.confirm(`Delete MC "${mc.full_name}"?`)) return;
    try {
      await api.delete(`/api/admin/mcs/${mc.id}`);
      setMcs((prev) => prev.filter((m) => m.id !== mc.id));
    } catch (err) {
      setMcError(err.response?.data?.error || 'Failed to delete.');
    }
  }

  // ── Stage hands roster (migration 046) ──
  function startAddSh() {
    setShEditMode('add');
    setShForm({ full_name: '', email: '', phone: '', notes: '' });
    setShError('');
  }
  function startEditSh(sh) {
    setShEditMode(sh);
    setShForm({ full_name: sh.full_name, email: sh.email || '', phone: sh.phone || '', notes: sh.notes || '' });
    setShError('');
  }
  async function saveSh() {
    if (!shForm.full_name.trim()) { setShError('Name is required.'); return; }
    setShSaving(true);
    setShError('');
    try {
      if (shEditMode === 'add') {
        const r = await api.post('/api/admin/stage-hands', shForm);
        setStageHands((prev) => [...prev, r.data.stage_hand].sort((a, b) => a.full_name.localeCompare(b.full_name)));
      } else {
        const r = await api.put(`/api/admin/stage-hands/${shEditMode.id}`, shForm);
        setStageHands((prev) => prev.map((s) => s.id === r.data.stage_hand.id ? r.data.stage_hand : s));
        // The heat headers show the roster name, so a rename must reach them.
        await loadData(year);
      }
      setShEditMode(null);
    } catch (err) {
      setShError(err.response?.data?.error || 'Failed to save.');
    } finally {
      setShSaving(false);
    }
  }
  async function deleteSh(sh) {
    if (!window.confirm(`Delete stage hand "${sh.full_name}"? Any heat they are allocated to will be left without a stage hand.`)) return;
    try {
      await api.delete(`/api/admin/stage-hands/${sh.id}`);
      setStageHands((prev) => prev.filter((s) => s.id !== sh.id));
      await loadData(year);
    } catch (err) {
      setShError(err.response?.data?.error || 'Failed to delete.');
    }
  }

  // ── Viewers ──
  function startAddViewer() {
    setViewerEditMode('add');
    setViewerForm({ full_name: '', email: '', password: '', confirm: '', is_active: true });
    setViewerError('');
  }
  function startEditViewer(v) {
    setViewerEditMode(v);
    setViewerForm({ full_name: v.full_name, email: v.email || '', password: '', confirm: '', is_active: v.is_active !== false });
    setViewerError('');
  }
  async function saveViewer() {
    const name  = viewerForm.full_name.trim();
    const email = viewerForm.email.trim().toLowerCase();
    if (!name)  { setViewerError('Name is required.'); return; }
    if (!email) { setViewerError('Email is required.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setViewerError('Invalid email address.'); return; }
    // A password is optional: left blank, the account fails closed until the
    // viewer sets their own through a login link. Typed, it must be usable.
    if (viewerForm.password && viewerForm.password.length < 8) { setViewerError('Password must be at least 8 characters.'); return; }
    if (viewerForm.password !== viewerForm.confirm) { setViewerError('Passwords do not match.'); return; }
    setViewerSaving(true);
    setViewerError('');
    try {
      if (viewerEditMode === 'add') {
        const r = await api.post('/api/admin/viewers', {
          full_name: name, email,
          ...(viewerForm.password ? { password: viewerForm.password } : {}),
        });
        setViewers((prev) => [...prev, r.data.user].sort((a, b) => a.full_name.localeCompare(b.full_name)));
        // The email already had a FOOM login (a judge, say): the server granted
        // that account the results page instead of creating a second one.
        setViewerNotice(r.data.granted
          ? `${r.data.user.full_name} already had a FOOM login (${r.data.user.role}) — that account now has access to the results page. Press Send results link to tell them.`
          : '');
      } else {
        const r = await api.put(`/api/admin/viewers/${viewerEditMode.id}`, {
          full_name: name, email, is_active: viewerForm.is_active,
          ...(viewerForm.password ? { password: viewerForm.password } : {}),
        });
        setViewers((prev) => prev.map((v) => v.id === r.data.viewer.id ? r.data.viewer : v));
      }
      setViewerEditMode(null);
    } catch (err) {
      setViewerError(err.response?.data?.error || 'Failed to save.');
    } finally {
      setViewerSaving(false);
    }
  }
  async function sendViewerLoginLink(v) {
    setViewerLinkBusy(v.id);
    setViewerError('');
    try {
      const r = await api.post(`/api/admin/viewers/${v.id}/send-login-link`);
      setViewerLinkSent((prev) => ({ ...prev, [v.id]: new Date().toISOString() }));
      // The row now has a live link, so the button reads "Resend" from here on.
      setViewers((prev) => prev.map((x) => x.id === v.id && r.data.kind === 'login_link'
        ? { ...x, link_pending: true, link_expires: r.data.expires }
        : x));
    } catch (err) {
      setViewerError(err.response?.data?.error || 'Failed to send the login link.');
    } finally {
      setViewerLinkBusy(null);
    }
  }
  async function deleteViewer(v) {
    const q = v.granted
      ? `Remove results access from "${v.full_name}"? Their ${v.role} login is unaffected.`
      : `Delete viewer "${v.full_name}"? They will no longer be able to sign in.`;
    if (!window.confirm(q)) return;
    try {
      await api.delete(`/api/admin/viewers/${v.id}`);
      setViewers((prev) => prev.filter((x) => x.id !== v.id));
    } catch (err) {
      setViewerError(err.response?.data?.error || 'Failed to delete.');
    }
  }

  function handleHeatSelect(value) {
    setHeatNumber(value);
    setCreateError('');
    if (!value) {
      setCreateForm({ heat_date: '', performer_arrival_time: '', venue_name: '', venue_address: '', max_performers: '' });
      setCreateVenueId('');
      return;
    }

    const isGf = value === 'grand_final';
    const existing = isGf
      ? heats.find((h) => h.grand_final)
      : heats.find((h) => h.heat_number === parseInt(value, 10) && !h.grand_final);

    if (existing) {
      setCreateForm({
        heat_date:              existing.heat_date              ? existing.heat_date.slice(0, 10)  : '',
        performer_arrival_time: existing.performer_arrival_time ? existing.performer_arrival_time.slice(0, 5) : '',
        venue_name:             existing.venue_name    || '',
        venue_address:          existing.venue_address || '',
        max_performers:         existing.max_performers != null ? String(existing.max_performers) : '',
      });
      // Pre-select venue if existing heat's venue_name exactly matches one in the list
      const match = venues.find((v) => v.name === existing.venue_name);
      setCreateVenueId(match ? String(match.id) : '');
    } else if (!isGf && parseInt(value, 10) > 1) {
      // Populate from the previous heat (n-1)
      const prevNum  = parseInt(value, 10) - 1;
      const prevHeat = heats.find((h) => h.heat_number === prevNum && !h.grand_final);
      if (prevHeat) {
        let nextDate = '';
        if (prevHeat.heat_date) {
          const d = new Date(prevHeat.heat_date);
          d.setDate(d.getDate() + 7);
          nextDate = d.toISOString().slice(0, 10);
        }
        setCreateForm({
          heat_date:              nextDate,
          performer_arrival_time: prevHeat.performer_arrival_time ? prevHeat.performer_arrival_time.slice(0, 5) : '',
          venue_name:             prevHeat.venue_name    || '',
          venue_address:          prevHeat.venue_address || '',
          max_performers:         prevHeat.max_performers != null ? String(prevHeat.max_performers) : '',
        });
        // Pre-select venue from previous heat if it matches
        const match = venues.find((v) => v.name === prevHeat.venue_name);
        setCreateVenueId(match ? String(match.id) : '');
      } else {
        setCreateForm({ heat_date: '', performer_arrival_time: '', venue_name: '', venue_address: '', max_performers: '' });
        setCreateVenueId('');
      }
    } else {
      setCreateForm({ heat_date: '', performer_arrival_time: '', venue_name: '', venue_address: '', max_performers: '' });
      setCreateVenueId('');
    }
  }

  async function handleCreateHeat(e) {
    e.preventDefault();
    const isGf = heatNumber === 'grand_final';
    const num   = isGf ? null : parseInt(heatNumber, 10);

    if (!heatNumber) {
      setCreateError('Please select a heat.');
      return;
    }

    if (!createVenueId) {
      setCreateError('Please select a venue.');
      return;
    }

    // Find existing heat for this selection
    const existing = isGf
      ? heats.find((h) => h.grand_final)
      : heats.find((h) => h.heat_number === num && !h.grand_final);

    // Grand Final that doesn't exist yet is auto-created by the backend
    if (isGf && !existing) {
      setCreateError('The Grand Final is created automatically when a result is assigned in a regular heat.');
      return;
    }

    // Auto-generate a name if creating a new heat
    const autoName = isGf ? 'FOOM Grand Final' : `Heat ${num}`;

    setCreating(true);
    setCreateError('');
    try {
      if (existing) {
        await api.put(`/api/admin/heats/${existing.heat_id}`, {
          heat_date:              createForm.heat_date              || null,
          performer_arrival_time: createForm.performer_arrival_time || null,
          venue_name:             createForm.venue_name?.trim()     || null,
          venue_address:          createForm.venue_address?.trim()  || null,
          max_performers:         createForm.max_performers ? parseInt(createForm.max_performers, 10) : null,
        });
      } else {
        await api.post('/api/admin/heats', {
          heat_number:            num,
          heat_name:              autoName,
          competition_year:       year,
          heat_date:              createForm.heat_date              || null,
          performer_arrival_time: createForm.performer_arrival_time || null,
          venue_name:             createForm.venue_name?.trim()     || null,
          venue_address:          createForm.venue_address?.trim()  || null,
          max_performers:         createForm.max_performers ? parseInt(createForm.max_performers, 10) : null,
        });
      }
      setHeatNumber('');
      setCreateForm({ heat_date: '', performer_arrival_time: '', venue_name: '', venue_address: '', max_performers: '' });
      setCreateVenueId('');
      await loadData(year);
    } catch (err) {
      setCreateError(err.response?.data?.error || 'Failed to save heat.');
    } finally {
      setCreating(false);
    }
  }

  function startEditHeat(h) {
    setEditingHeatId(h.heat_id);
    setSaveHeatError('');
    setHeatEditForm({
      heat_date:             h.heat_date              ? h.heat_date.slice(0, 10)  : '',
      performer_arrival_time: h.performer_arrival_time ? h.performer_arrival_time.slice(0, 5) : '',
      // Blank means "use the +30 default" — the server falls back rather than
      // storing a value, so an empty field is a valid, meaningful state.
      judge_arrival_time:    h.judge_arrival_time     ? h.judge_arrival_time.slice(0, 5) : '',
      venue_name:            h.venue_name             || '',
      venue_address:         h.venue_address          || '',
      max_performers:        h.max_performers != null ? String(h.max_performers) : '',
      mc_name:               h.mc_name               || '',
      stage_hand_id:         h.stage_hand_id != null ? String(h.stage_hand_id) : '',
    });
  }

  async function handleSaveHeat(heatId) {
    setSavingHeat(true);
    setSaveHeatError('');
    try {
      await api.put(`/api/admin/heats/${heatId}`, {
        heat_date:             heatEditForm.heat_date             || null,
        performer_arrival_time: heatEditForm.performer_arrival_time || null,
        judge_arrival_time:    heatEditForm.judge_arrival_time    || null,
        venue_name:            heatEditForm.venue_name?.trim()    || null,
        venue_address:         heatEditForm.venue_address?.trim() || null,
        max_performers:        heatEditForm.max_performers ? parseInt(heatEditForm.max_performers, 10) : null,
        mc_name:               heatEditForm.mc_name?.trim()       || null,
        stage_hand_id:         heatEditForm.stage_hand_id ? parseInt(heatEditForm.stage_hand_id, 10) : null,
      });
      setEditingHeatId(null);
      await loadData(year);
    } catch (err) {
      setSaveHeatError(err.response?.data?.error || 'Failed to save.');
    } finally {
      setSavingHeat(false);
    }
  }

  async function handleDeleteHeat(heat) {
    if (!window.confirm(`Delete "${heat.heat_name}"? Songs in this heat will return to the unassigned pool.`)) return;
    try {
      await api.delete(`/api/admin/heats/${heat.heat_id}`);
      if (editingHeatId === heat.heat_id) setEditingHeatId(null);
      await loadData(year);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete heat.');
    }
  }

  async function handleAssignSong(songId) {
    const heatId = assignSelections[songId];
    if (!heatId) return;
    setAssigning(songId);
    try {
      await api.put(`/api/admin/songs/${songId}/heat`, { heat_id: heatId });
      await loadData(year);
      setAssignSelections((prev) => { const n = { ...prev }; delete n[songId]; return n; });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to assign song.');
    } finally {
      setAssigning(null);
    }
  }

  // Withdraw / reinstate a FOOM Live entry from the Assign-to-Heat dropdown.
  // Applies immediately on selection rather than waiting for Assign: that
  // button places a song in a heat, and withdrawing is the opposite action.
  async function handleWithdrawSong(songId, withdrawn = true) {
    setAssigning(songId);
    try {
      await api.put(`/api/admin/songs/${songId}/withdraw`, { withdrawn });
      await loadData(year);
      // Drop the sentinel so the dropdown returns to "— select heat —" and the
      // saved status (shown as the badge) becomes the single source of truth.
      setAssignSelections((prev) => { const n = { ...prev }; delete n[songId]; return n; });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update song status.');
    } finally {
      setAssigning(null);
    }
  }

  async function handleBulkAssignUnassigned() {
    if (!bulkHeatId || selectedUnassigned.size === 0) return;
    setBulkAssigning(true);
    try {
      for (const songId of selectedUnassigned) {
        await api.put(`/api/admin/songs/${songId}/heat`, { heat_id: bulkHeatId });
      }
      await loadData(year);
      setSelectedUnassigned(new Set());
      setBulkHeatId('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to assign songs.');
    } finally {
      setBulkAssigning(false);
    }
  }

  async function handleUnassignSong(songId) {
    setUnassigning(songId);
    try {
      await api.put(`/api/admin/songs/${songId}/heat`, { heat_id: null });
      await loadData(year);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to unassign song.');
    } finally {
      setUnassigning(null);
    }
  }

  async function handleReorderSong(heatId, songId, direction) {
    setReordering((p) => ({ ...p, [songId]: true }));
    try {
      await api.patch(`/api/admin/heats/${heatId}/songs/reorder`, { songId, direction });
      await loadData(year);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reorder songs.');
    } finally {
      setReordering((p) => ({ ...p, [songId]: false }));
    }
  }

  async function handleToggleResult(heatId, songId, resultType, currentResults) {
    const key = `${heatId}-${songId}-${resultType}`;
    setSavingResultKeys((prev) => new Set([...prev, key]));
    try {
      if (resultType === 'both') {
        const hasPc = currentResults.some((r) => r.result === 'peoples_choice');
        const hasJs = currentResults.some((r) => r.result === 'judge_selection');
        if (!hasPc) await api.post(`/api/admin/heats/${heatId}/songs/${songId}/result`, { result: 'peoples_choice' });
        if (!hasJs) await api.post(`/api/admin/heats/${heatId}/songs/${songId}/result`, { result: 'judge_selection' });
      } else {
        const existing = currentResults.find((r) => r.result === resultType);
        if (existing) {
          await api.delete(`/api/admin/heats/${heatId}/songs/${songId}/result/${existing.id}`);
        } else {
          await api.post(`/api/admin/heats/${heatId}/songs/${songId}/result`, { result: resultType });
        }
      }
      await loadData(year);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update result.');
    } finally {
      setSavingResultKeys((prev) => { const n = new Set(prev); n.delete(key); return n; });
    }
  }

  async function handleDeselectAll(heatId, songId, results) {
    const key = `${heatId}-${songId}-deselect`;
    setSavingResultKeys((prev) => new Set([...prev, key]));
    try {
      await Promise.all(
        results.map((r) => api.delete(`/api/admin/heats/${heatId}/songs/${songId}/result/${r.id}`))
      );
      await loadData(year);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to clear results.');
    } finally {
      setSavingResultKeys((prev) => { const n = new Set(prev); n.delete(key); return n; });
    }
  }

  async function handleGfRemove(gfHeatId, songId) {
    setRemovingFromGf(songId);
    try {
      await api.delete(`/api/admin/heats/${gfHeatId}/songs/${songId}/gf-remove`);
      await loadData(year);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove from Grand Final.');
    } finally {
      setRemovingFromGf(null);
    }
  }

  async function handleAddHeatJudge(heatId, judgeId) {
    setJudgeAdding((p) => ({ ...p, [heatId]: true }));
    setJudgeErrors((p) => ({ ...p, [heatId]: '' }));
    try {
      await api.post(`/api/admin/heats/${heatId}/judges`, { judge_id: Number(judgeId) });
      await loadData(year);
    } catch (e) {
      setJudgeErrors((p) => ({ ...p, [heatId]: e.response?.data?.error || 'Failed to add judge.' }));
    } finally {
      setJudgeAdding((p) => ({ ...p, [heatId]: false }));
    }
  }

  async function handleRemoveHeatJudge(heatId, judgeId) {
    const key = `${heatId}-${judgeId}`;
    setJudgeRemoving((p) => ({ ...p, [key]: true }));
    setJudgeErrors((p) => ({ ...p, [heatId]: '' }));
    try {
      await api.delete(`/api/admin/heats/${heatId}/judges/${judgeId}`);
      await loadData(year);
    } catch (e) {
      setJudgeErrors((p) => ({ ...p, [heatId]: e.response?.data?.error || 'Failed to remove judge.' }));
    } finally {
      setJudgeRemoving((p) => ({ ...p, [key]: false }));
    }
  }

  function handleNotifyJudges(heat)    { setNotifyHeatConfirm({ type: 'judges',    heat }); }
  function handleNotifyPerformers(heat) { setNotifyHeatConfirm({ type: 'performers', heat }); }
  function handleNotifyMc(heat)         { setNotifyHeatConfirm({ type: 'mc',         heat }); }
  function handleNotifySh(heat)         { setNotifyHeatConfirm({ type: 'stage_hand', heat }); }

  async function doNotifyJudges(heat) {
    setNotifyHeatConfirm(null);
    const key = `${heat.heat_id}-judges`;
    setNotifying(key);
    try {
      const pdfBase64 = getRunningSheetPdfBase64(heat);
      await api.post(`/api/admin/heats/${heat.heat_id}/notify-judges`, { pdfBase64 });
      setNotifiedAt((prev) => ({ ...prev, [key]: new Date().toISOString() }));
      await loadData(year);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to notify judges.');
    } finally {
      setNotifying(null);
    }
  }

  async function doNotifyPerformers(heat) {
    setNotifyHeatConfirm(null);
    const key = `${heat.heat_id}-performers`;
    setNotifying(key);
    try {
      const pdfBase64 = getRunningSheetPdfBase64(heat);
      await api.post(`/api/admin/heats/${heat.heat_id}/notify-performers`, { pdfBase64 });
      setNotifiedAt((prev) => ({ ...prev, [key]: new Date().toISOString() }));
      await loadData(year);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to notify performers.');
    } finally {
      setNotifying(null);
    }
  }

  async function handleInsertBreak(heatId, afterPosition) {
    setBreakBusy(heatId);
    setError('');
    try {
      const res = await api.post(`/api/admin/heats/${heatId}/breaks`, { after_position: afterPosition });
      setHeats((prev) => prev.map((h) => (
        h.heat_id === heatId ? { ...h, breaks: res.data.breaks } : h
      )));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to insert the break.');
    } finally {
      setBreakBusy(null);
    }
  }

  async function handleRemoveBreak(heatId, afterPosition) {
    setBreakBusy(heatId);
    setError('');
    try {
      const res = await api.delete(`/api/admin/heats/${heatId}/breaks/${afterPosition}`);
      setHeats((prev) => prev.map((h) => (
        h.heat_id === heatId ? { ...h, breaks: res.data.breaks } : h
      )));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove the break.');
    } finally {
      setBreakBusy(null);
    }
  }

  function handleNotifyPerformer(heatId, song) {
    const heat = heats.find((h) => h.heat_id === heatId);
    if (heat) setNotifyHeatConfirm({ type: 'performer-single', heat, song });
  }

  async function doNotifyPerformer(heat, song) {
    setNotifyHeatConfirm(null);
    setNotifyingSong(song.id);
    try {
      const pdfBase64 = getRunningSheetPdfBase64(heat);
      const res = await api.post(`/api/admin/heats/${heat.heat_id}/notify-performer`, {
        song_id: song.id,
        pdfBase64,
      });
      setSongNotified((prev) => ({ ...prev, [song.id]: res.data.email }));
      await loadData(year);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to notify that performer.');
    } finally {
      setNotifyingSong(null);
    }
  }

  async function doNotifyMc(heat) {
    setNotifyHeatConfirm(null);
    const key = `${heat.heat_id}-mc`;
    setNotifying(key);
    try {
      // The MC template promises an attached running sheet, so send one —
      // this call used to post an empty body and the promise went unmet.
      const pdfBase64 = getRunningSheetPdfBase64(heat);
      await api.post(`/api/admin/heats/${heat.heat_id}/notify-mc`, { pdfBase64 });
      setNotifiedAt((prev) => ({ ...prev, [key]: new Date().toISOString() }));
      await loadData(year);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to notify MC.');
    } finally {
      setNotifying(null);
    }
  }

  async function doNotifySh(heat) {
    setNotifyHeatConfirm(null);
    const key = `${heat.heat_id}-sh`;
    setNotifying(key);
    try {
      // The stage hand works from the technical requirements on the night, so
      // that sheet goes with the running sheet. Both are built here, as the
      // running sheet is for every other notify.
      const pdfBase64     = getRunningSheetPdfBase64(heat);
      const techPdfBase64 = await getTechPdfBase64(heat);
      await api.post(`/api/admin/heats/${heat.heat_id}/notify-stage-hand`, { pdfBase64, techPdfBase64 });
      setNotifiedAt((prev) => ({ ...prev, [key]: new Date().toISOString() }));
      await loadData(year);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to notify the stage hand.');
    } finally {
      setNotifying(null);
    }
  }

  async function handleJudgeFormsPdf(heat) {
    setDownloadingJudgeForms(heat.heat_id);
    try {
      const res = await api.get(`/api/admin/live-heats/${heat.heat_id}/judge-forms-pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a   = document.createElement('a');
      a.href     = url;
      a.download = `FOOM_Judge_Forms_Heat_${heat.heat_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setError('Failed to generate judge forms PDF.');
    } finally {
      setDownloadingJudgeForms(null);
    }
  }

  return (
    <div>
      {error && <div className="c-alert" role="alert" style={{ marginBottom: '1rem' }}>{error}</div>}

      {/* ── Header with year selector ── */}
      <div className="a-section" style={{ marginBottom: '1.5rem' }}>
        <div className="a-section__header">
          <div>
            <div className="a-section__title">Live Heats — FOOM Live</div>
            <div className="a-section__desc">Manage heat assignments for FOOM Live category entries.</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button
              type="button"
              className="c-btn c-btn--outline c-btn--sm"
              onClick={handleRenumber}
              disabled={renumbering}
              title="Close gaps in heat numbering, e.g. 2,3,4 → 1,2,3 after deleting a heat"
            >
              {renumbering ? 'Renumbering…' : 'Renumber heats'}
            </button>
            <label htmlFor="hms-year" style={{ fontWeight: 600, fontSize: '0.9rem' }}>Year:</label>
            <select
              id="hms-year"
              className="c-select"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              style={{ width: 'auto' }}
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
        {renumberMsg && (
          <div className="c-alert" style={{ marginTop: '0.75rem' }} role="status">{renumberMsg}</div>
        )}
      </div>

      {/* ── Create Heat ── */}
      <div className="a-section" style={{ marginBottom: '1.5rem' }}>
        <button
          type="button"
          onClick={() => setCreateHeatOpen((v) => !v)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', padding: 0, marginBottom: createHeatOpen ? '0.75rem' : 0, fontWeight: 700, fontSize: '1rem', color: '#1a1a2e' }}
        >
          <span className="a-chevron" style={{ transform: createHeatOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
          Create new heat
        </button>
        {createHeatOpen && (<><form onSubmit={handleCreateHeat}>
          {(() => {
            const isGf        = heatNumber === 'grand_final';
            const existingHeat = isGf
              ? heats.find((h) => h.grand_final)
              : heats.find((h) => h.heat_number === parseInt(heatNumber, 10) && !h.grand_final);
            const isUpdating  = Boolean(existingHeat);
            return (
              <>
                {/* Header line: "Create new heat" + dropdown + Number of Heats in {year}: [n] */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: heatNumber ? '0.75rem' : 0 }}>
                  <span style={{ fontWeight: 700, fontSize: '1rem', color: '#1a1a2e', whiteSpace: 'nowrap' }}>
                    Create new heat
                  </span>
                  <select
                    id="heat-number"
                    className="c-select"
                    value={heatNumber}
                    onChange={(e) => handleHeatSelect(e.target.value)}
                    style={{ flex: 1 }}
                  >
                    <option value="">— Select heat —</option>
                    {Array.from({ length: numHeats }, (_, i) => i + 1).map((n) => {
                      const exists = heats.some((h) => h.heat_number === n && !h.grand_final);
                      return (
                        <option key={n} value={String(n)}>
                          {`Heat ${n}${exists ? ' (exists — will update)' : ''}`}
                        </option>
                      );
                    })}
                    {(() => {
                      const gfExists = heats.some((h) => h.grand_final);
                      return (
                        <option value="grand_final">
                          {`★ Grand Final${gfExists ? ' (exists — will update)' : ''}`}
                        </option>
                      );
                    })()}
                  </select>
                  <label htmlFor="num-heats" style={{ fontWeight: 600, fontSize: '0.9rem', whiteSpace: 'nowrap' }}>
                    Number of Heats in {year}:
                  </label>
                  <input
                    id="num-heats"
                    className="c-input"
                    type="number"
                    min={1}
                    max={20}
                    value={numHeats}
                    onChange={(e) => setNumHeats(Math.max(1, parseInt(e.target.value, 10) || 1))}
                    style={{ width: '64px', textAlign: 'center' }}
                  />
                  <button type="submit" className="c-btn c-btn--primary" disabled={!heatNumber || creating} style={{ whiteSpace: 'nowrap', background: '#0ea5e9', color: '#ffffff', border: 'none' }}>
                    {creating
                      ? (isUpdating ? 'Updating…' : 'Creating…')
                      : (isUpdating ? '✓ Update Heat' : '+ Create Heat')}
                  </button>
                </div>

                {/* Logistics + submit row — only shown once a heat is selected */}
                {heatNumber && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.75rem', alignItems: 'end' }}>
                    <div className="c-form-group" style={{ marginBottom: 0 }}>
                      <label className="c-label" style={{ fontSize: '0.78rem' }}>Heat Date <span className="c-label-optional">(optional)</span></label>
                      <input
                        className="c-input"
                        type="date"
                        value={createForm.heat_date}
                        onChange={(e) => setCreateForm((p) => ({ ...p, heat_date: e.target.value }))}
                      />
                    </div>
                    <div className="c-form-group" style={{ marginBottom: 0 }}>
                      <label className="c-label" style={{ fontSize: '0.78rem' }}>Arrival Time <span className="c-label-optional">(optional)</span></label>
                      <input
                        className="c-input"
                        type="time"
                        value={createForm.performer_arrival_time}
                        onChange={(e) => setCreateForm((p) => ({ ...p, performer_arrival_time: e.target.value }))}
                      />
                    </div>
                    {/* Venue dropdown — required */}
                    <div className="c-form-group" style={{ marginBottom: 0, gridColumn: '1 / -1' }}>
                      <label className="c-label" style={{ fontSize: '0.78rem' }}>Venue *</label>
                      <select
                        className="c-select"
                        value={createVenueId}
                        onChange={(e) => {
                          const venueId = e.target.value;
                          setCreateVenueId(venueId);
                          if (venueId) {
                            const v = venues.find((v) => String(v.id) === venueId);
                            if (v) setCreateForm((p) => ({ ...p, venue_name: v.name, venue_address: v.address || '' }));
                          } else {
                            setCreateForm((p) => ({ ...p, venue_name: '', venue_address: '' }));
                          }
                        }}
                      >
                        <option value="">— Select Venue —</option>
                        {venues.map((v) => (
                          <option key={v.id} value={String(v.id)}>{v.name}</option>
                        ))}
                      </select>
                    </div>
                    {/* Show selected venue's details as read-only confirmation */}
                    {createVenueId && (
                      <>
                        <div className="c-form-group" style={{ marginBottom: 0 }}>
                          <label className="c-label" style={{ fontSize: '0.78rem' }}>Venue Name</label>
                          <input
                            className="c-input"
                            readOnly
                            value={createForm.venue_name}
                            style={{ background: '#f3f4f6', color: '#6b7280', cursor: 'default' }}
                          />
                        </div>
                        <div className="c-form-group" style={{ marginBottom: 0 }}>
                          <label className="c-label" style={{ fontSize: '0.78rem' }}>Venue Address</label>
                          <input
                            className="c-input"
                            readOnly
                            value={createForm.venue_address}
                            style={{ background: '#f3f4f6', color: '#6b7280', cursor: 'default' }}
                          />
                        </div>
                      </>
                    )}
                    <div className="c-form-group" style={{ marginBottom: 0 }}>
                      <label className="c-label" style={{ fontSize: '0.78rem' }}>Max Performers <span className="c-label-optional">(optional)</span></label>
                      <input
                        className="c-input"
                        type="number"
                        min="1"
                        value={createForm.max_performers}
                        onChange={(e) => setCreateForm((p) => ({ ...p, max_performers: e.target.value }))}
                        placeholder="e.g. 6"
                      />
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </form>
        {createError && <div className="c-alert" style={{ marginTop: '0.5rem' }}>{createError}</div>}

        {/* Existing heats list */}
        {!loading && heats.length > 0 && (
          <div className="a-table-wrap" style={{ marginTop: '1.25rem' }}>
            <table className="a-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Date</th>
                  <th>Songs</th>
                  <th>Max Perfs</th>
                  <th>Judges</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const gf = heats.find((h) => h.grand_final);
                  return gf ? (() => {
                    const hj = gf.heat_judges || [];
                    const available = allJudges.filter(
                      (j) => j.is_active && j.judge_type !== 'non_live_only' && !hj.some((a) => a.judge_id === j.id)
                    );
                    return (
                      <>
                        <tr key={gf.heat_id}>
                          <td><strong style={{ color: '#d4af37' }}>★</strong></td>
                          <td>
                            <div>{gf.heat_name}</div>
                            {gf.venue_name && (
                              <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>{gf.venue_name}</div>
                            )}
                          </td>
                          <td style={{ fontSize: '0.85rem', color: '#374151' }}>
                            {gf.heat_date
                              ? new Date(gf.heat_date).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
                              : <span style={{ color: '#9ca3af' }}>—</span>}
                            {gf.performer_arrival_time && (
                              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                Arrival {gf.performer_arrival_time.slice(0, 5)}
                              </div>
                            )}
                          </td>
                          <td><span className="badge badge--active">{gf.songs.length}</span></td>
                          <td><span className="badge badge--active">{gf.max_performers != null ? gf.max_performers : '—'}</span></td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                              {hj.map((j) => (
                                <span key={j.judge_id} className="a-judge-lozenge" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem' }}>
                                  {j.judge_name}
                                  <button
                                    onClick={() => handleRemoveHeatJudge(gf.heat_id, j.judge_id)}
                                    disabled={judgeRemoving[`${gf.heat_id}-${j.judge_id}`]}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 1px', color: 'inherit', lineHeight: 1, fontSize: '0.9em', opacity: 0.75 }}
                                    aria-label={`Remove ${j.judge_name}`}
                                  >
                                    {judgeRemoving[`${gf.heat_id}-${j.judge_id}`] ? '…' : '×'}
                                  </button>
                                </span>
                              ))}
                              {/* The Grand Final is judged by THREE, unlike a regular heat's two.
                                  The server has always capped every heat at 3, so this shortfall
                                  warning is the only thing that says what the target is — it read
                                  x/2 and went quiet at two judges, which is a Grand Final one judge
                                  short with nothing on screen saying so. */}
                              {hj.length < 3 && (
                                <span style={{ fontSize: '0.72rem', color: '#ef4444', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                  ⚠ {hj.length}/3
                                </span>
                              )}
                              {hj.length < 3 && (
                                <select
                                  value=""
                                  onChange={(e) => { if (e.target.value) handleAddHeatJudge(gf.heat_id, e.target.value); }}
                                  disabled={judgeAdding[gf.heat_id]}
                                  style={{ fontSize: '0.78rem', padding: '2px 4px', border: '1px solid #d1d5db', borderRadius: '4px', background: '#fff', cursor: 'pointer', width: 'auto' }}
                                >
                                  <option value="" disabled>+ Judge</option>
                                  {(() => {
                                    const busy = new Set(heats.flatMap((ht) => (ht.heat_judges || []).map((a) => String(a.judge_id))));
                                    return available.map((j) => {
                                      const conflict = (j.conflicted_categories || []).find((c) => c.category_id === 22);
                                      const inOtherHeat = busy.has(String(j.id));
                                      const optColor = conflict ? '#dc2626' : inOtherHeat ? '#2563eb' : '#1a1a2e';
                                      return (
                                        <option key={j.id} value={j.id} style={{ color: optColor }}>
                                          {conflict ? `⚠ ${j.full_name} (FOOM Live entrant)` : j.full_name}
                                        </option>
                                      );
                                    });
                                  })()}
                                </select>
                              )}
                              {judgeErrors[gf.heat_id] && (
                                <span style={{ fontSize: '0.72rem', color: '#ef4444' }}>{judgeErrors[gf.heat_id]}</span>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="a-row-actions">
                              <button
                                className="c-btn c-btn--outline c-btn--sm"
                                onClick={() => editingHeatId === gf.heat_id ? setEditingHeatId(null) : startEditHeat(gf)}
                              >
                                {editingHeatId === gf.heat_id ? 'Cancel' : 'Edit'}
                              </button>
                            </div>
                          </td>
                        </tr>
                        {editingHeatId === gf.heat_id && (
                          <HeatLogisticsEdit
                            key={`edit-${gf.heat_id}`}
                            heatId={gf.heat_id}
                            heatEditForm={heatEditForm}
                            setHeatEditForm={setHeatEditForm}
                            savingHeat={savingHeat}
                            saveHeatError={saveHeatError}
                            handleSaveHeat={handleSaveHeat}
                            setEditingHeatId={setEditingHeatId}
                            setSaveHeatError={setSaveHeatError}
                            venues={venues}
                            mcs={mcs}
                            stageHands={stageHands}
                          />
                        )}
                      </>
                    );
                  })() : null;
                })()}
                {heats.filter((h) => !h.grand_final).map((h) => {
                  const hj = h.heat_judges || [];
                  const available = allJudges.filter(
                    (j) => j.is_active && j.judge_type !== 'non_live_only' && !hj.some((a) => a.judge_id === j.id)
                  );
                  return (
                  <>
                    <tr key={h.heat_id}>
                      <td><strong>{h.heat_number}</strong></td>
                      <td>
                        <div>{h.heat_name}</div>
                        {h.venue_name && (
                          <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>{h.venue_name}</div>
                        )}
                      </td>
                      <td style={{ fontSize: '0.85rem', color: '#374151' }}>
                        {h.heat_date
                          ? new Date(h.heat_date).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
                          : <span style={{ color: '#9ca3af' }}>—</span>}
                        {h.performer_arrival_time && (
                          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                            Arrival {h.performer_arrival_time.slice(0, 5)}
                          </div>
                        )}
                      </td>
                      <td><span className="badge badge--active">{h.songs.length}</span></td>
                      <td><span className="badge badge--active">{h.max_performers != null ? h.max_performers : '—'}</span></td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                          {hj.map((j) => (
                            <span key={j.judge_id} className="a-judge-lozenge" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem' }}>
                              {j.judge_name}
                              <button
                                onClick={() => handleRemoveHeatJudge(h.heat_id, j.judge_id)}
                                disabled={judgeRemoving[`${h.heat_id}-${j.judge_id}`]}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 1px', color: 'inherit', lineHeight: 1, fontSize: '0.9em', opacity: 0.75 }}
                                aria-label={`Remove ${j.judge_name}`}
                              >
                                {judgeRemoving[`${h.heat_id}-${j.judge_id}`] ? '…' : '×'}
                              </button>
                            </span>
                          ))}
                          {hj.length < 2 && (
                            <span style={{ fontSize: '0.72rem', color: '#ef4444', fontWeight: 600, whiteSpace: 'nowrap' }}>
                              ⚠ {hj.length}/2
                            </span>
                          )}
                          {hj.length < 3 && (
                            <select
                              value=""
                              onChange={(e) => { if (e.target.value) handleAddHeatJudge(h.heat_id, e.target.value); }}
                              disabled={judgeAdding[h.heat_id]}
                              style={{ fontSize: '0.78rem', padding: '2px 4px', border: '1px solid #d1d5db', borderRadius: '4px', background: '#fff', cursor: 'pointer', width: 'auto' }}
                            >
                              <option value="" disabled>+ Judge</option>
                              {(() => {
                                const busy = new Set(heats.flatMap((ht) => (ht.heat_judges || []).map((a) => String(a.judge_id))));
                                return available.map((j) => {
                                  const conflict = (j.conflicted_categories || []).find(
                                    (c) => c.category_id === 22
                                  );
                                  const inOtherHeat = busy.has(String(j.id));
                                  const optColor = conflict ? '#dc2626' : inOtherHeat ? '#2563eb' : '#1a1a2e';
                                  return (
                                    <option key={j.id} value={j.id} style={{ color: optColor }}>
                                      {conflict ? `⚠ ${j.full_name} (FOOM Live entrant)` : j.full_name}
                                    </option>
                                  );
                                });
                              })()}
                            </select>
                          )}
                          {judgeErrors[h.heat_id] && (
                            <span style={{ fontSize: '0.72rem', color: '#ef4444' }}>{judgeErrors[h.heat_id]}</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="a-row-actions">
                          <button
                            className="c-btn c-btn--outline c-btn--sm"
                            onClick={() => editingHeatId === h.heat_id ? setEditingHeatId(null) : startEditHeat(h)}
                          >
                            {editingHeatId === h.heat_id ? 'Cancel' : 'Edit'}
                          </button>
                          <button
                            className="c-btn c-btn--danger c-btn--sm"
                            onClick={() => handleDeleteHeat(h)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                    {editingHeatId === h.heat_id && (
                      <HeatLogisticsEdit
                        key={`edit-${h.heat_id}`}
                        heatId={h.heat_id}
                        heatEditForm={heatEditForm}
                        setHeatEditForm={setHeatEditForm}
                        savingHeat={savingHeat}
                        saveHeatError={saveHeatError}
                        handleSaveHeat={handleSaveHeat}
                        setEditingHeatId={setEditingHeatId}
                        setSaveHeatError={setSaveHeatError}
                        venues={venues}
                        mcs={mcs}
                        stageHands={stageHands}
                      />
                    )}
                  </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {!loading && heats.length === 0 && (
          <p style={{ color: '#6b7280', marginTop: '0.75rem', fontSize: '0.9rem' }}>
            No heats created for {year} yet.
          </p>
        )}
        </>)}
      </div>

      {loading && <p style={{ color: '#6b7280' }}>Loading…</p>}

      {!loading && (
        <>
          {/* ── Unassigned Pool ── */}
          <div className="a-section" style={{ marginBottom: '1.5rem' }}>
            <div className="a-section__header" style={{ cursor: 'pointer' }} onClick={() => setUnassignedOpen((v) => !v)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <span className="a-chevron" style={{ transform: unassignedOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                <div>
                  <div className="a-section__title">Unassigned Songs</div>
                  <div className="a-section__desc">
                    FOOM Live entries that haven't been placed in a heat yet.
                  </div>
                </div>
              </div>
              {/* Withdrawn entries stay in the list but are not awaiting a heat,
                  so they must not inflate the "still to place" count. */}
              <span className="badge badge--pending">
                {unassigned.filter((s) => s.status !== 'withdrawn').length} songs
              </span>
            </div>

            {unassignedOpen && (unassigned.length === 0 ? (
              <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>All FOOM Live songs are assigned to heats.</p>
            ) : heats.length === 0 ? (
              <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Create at least one heat above before assigning songs.</p>
            ) : (
              <>
                <div className="a-table-wrap">
                  <table className="a-table">
                    <thead>
                      <tr>
                        <th style={{ width: 36 }}>
                          <input
                            type="checkbox"
                            checked={selectedUnassigned.size === unassigned.length && unassigned.length > 0}
                            ref={(el) => {
                              if (el) el.indeterminate = selectedUnassigned.size > 0 && selectedUnassigned.size < unassigned.length;
                            }}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedUnassigned(new Set(unassigned.map((s) => s.id)));
                              } else {
                                setSelectedUnassigned(new Set());
                              }
                            }}
                            aria-label="Select all"
                          />
                        </th>
                        <th>Song — Artist</th>
                        <th>Payment</th>
                        <th>Assign to Heat</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {unassigned.map((s) => (
                        <tr key={s.id}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedUnassigned.has(s.id)}
                              onChange={() => {
                                setSelectedUnassigned((prev) => {
                                  const next = new Set(prev);
                                  next.has(s.id) ? next.delete(s.id) : next.add(s.id);
                                  return next;
                                });
                              }}
                              aria-label={`Select ${s.title}`}
                            />
                          </td>
                          <td>
                            <strong>{s.title}</strong>
                            {s.artist && (
                              <div style={{ color: '#6b7280', fontSize: '0.8rem', marginTop: '0.1rem' }}>{s.artist}</div>
                            )}
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                              <span className={`badge badge--${s.payment_status === 'paid' || s.payment_status === 'waived' ? 'active' : 'pending'}`}>
                                {s.payment_status}
                              </span>
                              <RefundControl song={s} onRefunded={() => loadData(year)} />
                              <EmailEntrantControl song={s} />
                            </div>
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              {/* Status indicator, not a control. Rendered from the
                                  SAVED song status, so it survives a refresh — the
                                  dropdown selection is not what keeps it on screen.
                                  A disabled button rather than a span: it reads as
                                  inert to a mouse and to a screen reader, which is
                                  what "not an active button" means. */}
                              {s.status === 'withdrawn' && (
                                <button
                                  type="button"
                                  disabled
                                  className="c-btn c-btn--sm"
                                  style={{
                                    background: '#6b7280', color: '#fff', opacity: 1,
                                    cursor: 'default', whiteSpace: 'nowrap', fontWeight: 700,
                                  }}
                                  title="This entry has been withdrawn. Assign it to a heat to reinstate it."
                                >
                                  Withdrawn
                                </button>
                              )}
                              <select
                                className="a-assign-select"
                                value={assignSelections[s.id] || ''}
                                disabled={assigning === s.id}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  if (v === WITHDRAWN_OPTION) {
                                    handleWithdrawSong(s.id, true);
                                    return;
                                  }
                                  setAssignSelections((prev) => ({ ...prev, [s.id]: v }));
                                }}
                              >
                                <option value="">— select heat —</option>
                                {heats.filter((h) => !h.grand_final).map((h) => (
                                  <option key={h.heat_id} value={h.heat_id}>
                                    Heat {h.heat_number}: {h.heat_name}
                                  </option>
                                ))}
                                {/* Only offered while the entry is live. Reinstating
                                    is done by picking a heat and pressing Assign,
                                    so a second "un-withdraw" option would be a
                                    redundant second way to do the same thing. */}
                                {s.status !== 'withdrawn' && (
                                  <option value={WITHDRAWN_OPTION}>Withdrawn</option>
                                )}
                              </select>
                            </div>
                          </td>
                          <td>
                            <div className="a-row-actions">
                              <button
                                className="c-btn c-btn--primary c-btn--sm"
                                onClick={() => handleAssignSong(s.id)}
                                disabled={!assignSelections[s.id] || assigning === s.id}
                              >
                                {assigning === s.id ? 'Assigning…' : 'Assign'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* ── Bulk assignment bar ── */}
                {selectedUnassigned.size > 0 && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
                    marginTop: '0.75rem', padding: '0.75rem 1rem',
                    background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '6px',
                  }}>
                    <span style={{ fontSize: '0.9rem', color: '#0c4a6e', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {selectedUnassigned.size} song{selectedUnassigned.size !== 1 ? 's' : ''} selected
                    </span>
                    <select
                      className="a-assign-select"
                      value={bulkHeatId}
                      onChange={(e) => setBulkHeatId(e.target.value)}
                    >
                      <option value="">— Select heat —</option>
                      {heats.filter((h) => !h.grand_final).map((h) => (
                        <option key={h.heat_id} value={h.heat_id}>
                          Heat {h.heat_number}: {h.heat_name}
                        </option>
                      ))}
                    </select>
                    <button
                      className="c-btn"
                      style={{ background: '#0ea5e9', color: '#fff' }}
                      onClick={handleBulkAssignUnassigned}
                      disabled={!bulkHeatId || bulkAssigning}
                    >
                      {bulkAssigning ? 'Assigning…' : 'Assign to Heat'}
                    </button>
                    <button
                      className="c-btn c-btn--outline c-btn--sm"
                      onClick={() => setSelectedUnassigned(new Set())}
                      style={{ marginLeft: 'auto' }}
                    >
                      Clear selection
                    </button>
                  </div>
                )}
              </>
            ))}
          </div>

          {/* ── Heat Summary Cards ── */}
          {heats.length > 0 && (
            <div className="a-section">
              <div className="a-section__header" style={{ cursor: 'pointer' }} onClick={() => setHeatSummaryOpen((v) => !v)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <span className="a-chevron" style={{ transform: heatSummaryOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                  <div>
                    <div className="a-section__title">Heat Summary</div>
                    <div className="a-section__desc">Songs grouped by heat with results, notifications and PDFs.</div>
                  </div>
                </div>
              </div>
              {heatSummaryOpen && <div className="a-heat-grid">
                {heats.map((h) => (
                  h.grand_final ? (
                    <GrandFinalCard
                      key={h.heat_id}
                      heat={h}
                      reordering={reordering}
                      removingFromGf={removingFromGf}
                      onRefunded={() => loadData(year)}
                      onNotifyPerformer={handleNotifyPerformer}
                      notifyingSong={notifyingSong}
                      openDropdownKey={openDropdownKey}
                      setOpenDropdownKey={setOpenDropdownKey}
                      onToggleResult={handleToggleResult}
                      onDeselectAll={handleDeselectAll}
                      savingResultKeys={savingResultKeys}
                      onReorder={handleReorderSong}
                      onRemove={handleGfRemove}
                      pdfGenerating={pdfGenerating}
                      setPdfGenerating={setPdfGenerating}
                      downloadingJudgeForms={downloadingJudgeForms}
                      onJudgeForms={handleJudgeFormsPdf}
                      notifying={notifying}
                      onNotifyJudges={handleNotifyJudges}
                      onNotifyPerformers={handleNotifyPerformers}
                      onNotifyMc={handleNotifyMc}
                      onNotifySh={handleNotifySh}
                      notifiedAt={notifiedAt}
                      onInsertBreak={handleInsertBreak}
                      onRemoveBreak={handleRemoveBreak}
                      breakBusy={breakBusy}
                      onEdit={(h) => editingHeatId === h.heat_id ? setEditingHeatId(null) : startEditHeat(h)}
                      editingHeatId={editingHeatId}
                      editProps={{ heatEditForm, setHeatEditForm, savingHeat, saveHeatError, handleSaveHeat, setEditingHeatId, setSaveHeatError, venues, mcs, stageHands }}
                    />
                  ) : (
                    <RegularHeatCard
                      key={h.heat_id}
                      heat={h}
                      reordering={reordering}
                      unassigning={unassigning}
                      openDropdownKey={openDropdownKey}
                      setOpenDropdownKey={setOpenDropdownKey}
                      onToggleResult={handleToggleResult}
                      onDeselectAll={handleDeselectAll}
                      savingResultKeys={savingResultKeys}
                      onReorder={handleReorderSong}
                      onUnassign={handleUnassignSong}
                      pdfGenerating={pdfGenerating}
                      setPdfGenerating={setPdfGenerating}
                      downloadingJudgeForms={downloadingJudgeForms}
                      onJudgeForms={handleJudgeFormsPdf}
                      notifying={notifying}
                      onNotifyJudges={handleNotifyJudges}
                      onNotifyPerformers={handleNotifyPerformers}
                      onNotifyMc={handleNotifyMc}
                      onNotifySh={handleNotifySh}
                      onNotifyPerformer={handleNotifyPerformer}
                      notifyingSong={notifyingSong}
                      onRefunded={() => loadData(year)}
                      onInsertBreak={handleInsertBreak}
                      onRemoveBreak={handleRemoveBreak}
                      breakBusy={breakBusy}
                      notifiedAt={notifiedAt}
                      onEdit={(h) => editingHeatId === h.heat_id ? setEditingHeatId(null) : startEditHeat(h)}
                      editingHeatId={editingHeatId}
                      editProps={{ heatEditForm, setHeatEditForm, savingHeat, saveHeatError, handleSaveHeat, setEditingHeatId, setSaveHeatError, venues, mcs, stageHands }}
                    />
                  )
                ))}
              </div>}
            </div>
          )}
        </>
      )}

      {/* ── Notify Judges confirmation dialog ── */}
      {notifyHeatConfirm?.type === 'judges' && (() => {
        const heat = notifyHeatConfirm.heat;
        // Must match the route's recipient set: heat_judges (the gold-header
        // lozenges) UNION the song-level judges. Listing only the song-level
        // ones meant this dialog named nobody for every FOOM Live heat —
        // category 22 is never assigned to online judging, so s.judges is
        // always empty there — and the silence read as "no names to show"
        // rather than "this will email no one".
        const judgeNames = [...new Map([
          ...(heat.heat_judges || []),
          ...(heat.songs || []).flatMap((s) => s.judges || []),
        ].map((j) => [j.judge_id, j])).values()].map((j) => j.judge_name);
        const heatLabel = heat.grand_final ? 'Grand Final' : `Heat ${heat.heat_number}${heat.heat_name ? ` — ${heat.heat_name}` : ''}`;
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: '#fff', borderRadius: '10px', padding: '1.75rem 2rem', maxWidth: '440px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
              <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1a1a2e', marginBottom: '0.75rem' }}>Notify Judges</div>
              <p style={{ margin: '0 0 1.5rem', lineHeight: 1.5, color: '#374151' }}>
                Send assignment notification emails to the judges assigned to <strong>{heatLabel}</strong>?
                {judgeNames.length > 0 && (
                  <> This will notify: <strong>{judgeNames.join(', ')}</strong>.</>
                )}
              </p>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button className="c-btn c-btn--outline" onClick={() => setNotifyHeatConfirm(null)}>Cancel</button>
                <button className="c-btn" style={{ background: '#0ea5e9' }} onClick={() => doNotifyJudges(heat)}>Send Emails</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── MCs Panel ── */}
      <div className="a-section" style={{ marginTop: '1.5rem' }}>
        <div className="a-section__header" style={{ cursor: 'pointer' }} onClick={() => setMcsOpen((v) => !v)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <span className="a-chevron" style={{ transform: mcsOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
            <div>
              <div className="a-section__title">MCs</div>
              <div className="a-section__desc">Manage the MC roster for FOOM Live heats.</div>
            </div>
          </div>
          <button
            className="c-btn c-btn--sm"
            style={{ background: '#1a1a2e', color: '#fff' }}
            onClick={(e) => { e.stopPropagation(); setMcsOpen(true); startAddMc(); }}
          >+ Add MC</button>
        </div>

        {mcsOpen && (<>
          {mcError && <div className="c-alert" style={{ marginBottom: '0.5rem' }}>{mcError}</div>}

          {/* Add form */}
          {mcEditMode === 'add' && (
            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '1rem', marginBottom: '0.75rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div className="c-form-group" style={{ marginBottom: 0, gridColumn: '1 / -1' }}>
                  <label className="c-label" style={{ fontSize: '0.78rem' }}>Name *</label>
                  <input className="c-input" value={mcForm.full_name} onChange={(e) => setMcForm((p) => ({ ...p, full_name: e.target.value }))} placeholder="Full name" />
                </div>
                <div className="c-form-group" style={{ marginBottom: 0 }}>
                  <label className="c-label" style={{ fontSize: '0.78rem' }}>Email</label>
                  <input className="c-input" type="email" value={mcForm.email} onChange={(e) => setMcForm((p) => ({ ...p, email: e.target.value }))} />
                </div>
                <div className="c-form-group" style={{ marginBottom: 0 }}>
                  <label className="c-label" style={{ fontSize: '0.78rem' }}>Phone</label>
                  <input className="c-input" value={mcForm.phone} onChange={(e) => setMcForm((p) => ({ ...p, phone: e.target.value }))} />
                </div>
                <div className="c-form-group" style={{ marginBottom: 0, gridColumn: '1 / -1' }}>
                  <label className="c-label" style={{ fontSize: '0.78rem' }}>Notes</label>
                  <input className="c-input" value={mcForm.notes} onChange={(e) => setMcForm((p) => ({ ...p, notes: e.target.value }))} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                <button className="c-btn c-btn--primary c-btn--sm" onClick={saveMc} disabled={mcSaving}>{mcSaving ? 'Saving…' : 'Save'}</button>
                <button className="c-btn c-btn--outline c-btn--sm" onClick={() => { setMcEditMode(null); setMcError(''); }} disabled={mcSaving}>Cancel</button>
              </div>
            </div>
          )}

          {mcs.length === 0 && mcEditMode !== 'add' ? (
            <div className="c-empty">
              <p className="c-empty__text">No MCs added yet. Add one above to allocate MCs to heats.</p>
            </div>
          ) : (
            <div className="a-table-wrap">
              <table className="a-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Notes</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {mcs.map((mc) => (
                    mcEditMode?.id === mc.id ? (
                      <tr key={mc.id}>
                        <td colSpan={5} style={{ padding: '0.75rem', background: '#f9fafb' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                            <div className="c-form-group" style={{ marginBottom: 0, gridColumn: '1 / -1' }}>
                              <label className="c-label" style={{ fontSize: '0.78rem' }}>Name *</label>
                              <input className="c-input" value={mcForm.full_name} onChange={(e) => setMcForm((p) => ({ ...p, full_name: e.target.value }))} />
                            </div>
                            <div className="c-form-group" style={{ marginBottom: 0 }}>
                              <label className="c-label" style={{ fontSize: '0.78rem' }}>Email</label>
                              <input className="c-input" type="email" value={mcForm.email} onChange={(e) => setMcForm((p) => ({ ...p, email: e.target.value }))} />
                            </div>
                            <div className="c-form-group" style={{ marginBottom: 0 }}>
                              <label className="c-label" style={{ fontSize: '0.78rem' }}>Phone</label>
                              <input className="c-input" value={mcForm.phone} onChange={(e) => setMcForm((p) => ({ ...p, phone: e.target.value }))} />
                            </div>
                            <div className="c-form-group" style={{ marginBottom: 0, gridColumn: '1 / -1' }}>
                              <label className="c-label" style={{ fontSize: '0.78rem' }}>Notes</label>
                              <input className="c-input" value={mcForm.notes} onChange={(e) => setMcForm((p) => ({ ...p, notes: e.target.value }))} />
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                            <button className="c-btn c-btn--primary c-btn--sm" onClick={saveMc} disabled={mcSaving}>{mcSaving ? 'Saving…' : 'Save'}</button>
                            <button className="c-btn c-btn--outline c-btn--sm" onClick={() => { setMcEditMode(null); setMcError(''); }} disabled={mcSaving}>Cancel</button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <tr key={mc.id}>
                        <td><strong>{mc.full_name}</strong></td>
                        <td style={{ color: '#6b7280', fontSize: '0.85rem' }}>{mc.email || '—'}</td>
                        <td style={{ color: '#6b7280', fontSize: '0.85rem' }}>{mc.phone || '—'}</td>
                        <td style={{ color: '#6b7280', fontSize: '0.85rem' }}>{mc.notes || '—'}</td>
                        <td>
                          <div className="a-row-actions">
                            {/* One-off email, same dialog as Email Entrant. Only
                                offered when the roster row has an address. */}
                            {mc.email && <EmailEntrantControl mc={mc} label="Email" noun="MC" />}
                            <button className="c-btn c-btn--sm c-btn--outline" onClick={() => startEditMc(mc)}>Edit</button>
                            <button className="c-btn c-btn--sm c-btn--danger" onClick={() => deleteMc(mc)}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    )
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>)}
      </div>

      {/* ── Stage hands Panel (migration 046) — same shape as the MCs panel ── */}
      <div className="a-section" style={{ marginTop: '1.5rem' }}>
        <div className="a-section__header" style={{ cursor: 'pointer' }} onClick={() => setShOpen((v) => !v)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <span className="a-chevron" style={{ transform: shOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
            <div>
              <div className="a-section__title">Stage hands</div>
              <div className="a-section__desc">Manage the stage hand roster for FOOM Live heats. Allocate one to a heat in its Edit form.</div>
            </div>
          </div>
          <button
            className="c-btn c-btn--sm"
            style={{ background: '#1a1a2e', color: '#fff' }}
            onClick={(e) => { e.stopPropagation(); setShOpen(true); startAddSh(); }}
          >+ Add Stage hand</button>
        </div>

        {shOpen && (<>
          {shError && <div className="c-alert" style={{ marginBottom: '0.5rem' }}>{shError}</div>}

          {/* Add form */}
          {shEditMode === 'add' && (
            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '1rem', marginBottom: '0.75rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div className="c-form-group" style={{ marginBottom: 0, gridColumn: '1 / -1' }}>
                  <label className="c-label" style={{ fontSize: '0.78rem' }}>Name *</label>
                  <input className="c-input" value={shForm.full_name} onChange={(e) => setShForm((p) => ({ ...p, full_name: e.target.value }))} placeholder="Full name" />
                </div>
                <div className="c-form-group" style={{ marginBottom: 0 }}>
                  <label className="c-label" style={{ fontSize: '0.78rem' }}>Email</label>
                  <input className="c-input" type="email" value={shForm.email} onChange={(e) => setShForm((p) => ({ ...p, email: e.target.value }))} />
                </div>
                <div className="c-form-group" style={{ marginBottom: 0 }}>
                  <label className="c-label" style={{ fontSize: '0.78rem' }}>Phone</label>
                  <input className="c-input" value={shForm.phone} onChange={(e) => setShForm((p) => ({ ...p, phone: e.target.value }))} />
                </div>
                <div className="c-form-group" style={{ marginBottom: 0, gridColumn: '1 / -1' }}>
                  <label className="c-label" style={{ fontSize: '0.78rem' }}>Notes</label>
                  <input className="c-input" value={shForm.notes} onChange={(e) => setShForm((p) => ({ ...p, notes: e.target.value }))} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                <button className="c-btn c-btn--primary c-btn--sm" onClick={saveSh} disabled={shSaving}>{shSaving ? 'Saving…' : 'Save'}</button>
                <button className="c-btn c-btn--outline c-btn--sm" onClick={() => { setShEditMode(null); setShError(''); }} disabled={shSaving}>Cancel</button>
              </div>
            </div>
          )}

          {stageHands.length === 0 && shEditMode !== 'add' ? (
            <div className="c-empty">
              <p className="c-empty__text">No stage hands added yet. Add one above to allocate stage hands to heats.</p>
            </div>
          ) : (
            <div className="a-table-wrap">
              <table className="a-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Notes</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {stageHands.map((sh) => (
                    shEditMode?.id === sh.id ? (
                      <tr key={sh.id}>
                        <td colSpan={5} style={{ padding: '0.75rem', background: '#f9fafb' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                            <div className="c-form-group" style={{ marginBottom: 0, gridColumn: '1 / -1' }}>
                              <label className="c-label" style={{ fontSize: '0.78rem' }}>Name *</label>
                              <input className="c-input" value={shForm.full_name} onChange={(e) => setShForm((p) => ({ ...p, full_name: e.target.value }))} />
                            </div>
                            <div className="c-form-group" style={{ marginBottom: 0 }}>
                              <label className="c-label" style={{ fontSize: '0.78rem' }}>Email</label>
                              <input className="c-input" type="email" value={shForm.email} onChange={(e) => setShForm((p) => ({ ...p, email: e.target.value }))} />
                            </div>
                            <div className="c-form-group" style={{ marginBottom: 0 }}>
                              <label className="c-label" style={{ fontSize: '0.78rem' }}>Phone</label>
                              <input className="c-input" value={shForm.phone} onChange={(e) => setShForm((p) => ({ ...p, phone: e.target.value }))} />
                            </div>
                            <div className="c-form-group" style={{ marginBottom: 0, gridColumn: '1 / -1' }}>
                              <label className="c-label" style={{ fontSize: '0.78rem' }}>Notes</label>
                              <input className="c-input" value={shForm.notes} onChange={(e) => setShForm((p) => ({ ...p, notes: e.target.value }))} />
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                            <button className="c-btn c-btn--primary c-btn--sm" onClick={saveSh} disabled={shSaving}>{shSaving ? 'Saving…' : 'Save'}</button>
                            <button className="c-btn c-btn--outline c-btn--sm" onClick={() => { setShEditMode(null); setShError(''); }} disabled={shSaving}>Cancel</button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <tr key={sh.id}>
                        <td><strong>{sh.full_name}</strong></td>
                        <td style={{ color: '#6b7280', fontSize: '0.85rem' }}>{sh.email || '—'}</td>
                        <td style={{ color: '#6b7280', fontSize: '0.85rem' }}>{sh.phone || '—'}</td>
                        <td style={{ color: '#6b7280', fontSize: '0.85rem' }}>{sh.notes || '—'}</td>
                        <td>
                          <div className="a-row-actions">
                            {sh.email && <EmailEntrantControl stageHand={sh} label="Email" noun="stage hand" />}
                            <button className="c-btn c-btn--sm c-btn--outline" onClick={() => startEditSh(sh)}>Edit</button>
                            <button className="c-btn c-btn--sm c-btn--danger" onClick={() => deleteSh(sh)}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    )
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>)}
      </div>

      {/* ── Viewers Panel (10 Sep 2026) — login accounts that see the Results tab only ── */}
      {(() => {
        const viewerFields = (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div className="c-form-group" style={{ marginBottom: 0 }}>
              <label className="c-label" style={{ fontSize: '0.78rem' }}>Name *</label>
              <input className="c-input" value={viewerForm.full_name} onChange={(e) => setViewerForm((p) => ({ ...p, full_name: e.target.value }))} placeholder="Full name" />
            </div>
            <div className="c-form-group" style={{ marginBottom: 0 }}>
              <label className="c-label" style={{ fontSize: '0.78rem' }}>Email * (their sign-in name)</label>
              <input className="c-input" type="email" value={viewerForm.email} onChange={(e) => setViewerForm((p) => ({ ...p, email: e.target.value }))} />
            </div>
            <div className="c-form-group" style={{ marginBottom: 0 }}>
              <label className="c-label" style={{ fontSize: '0.78rem' }}>{viewerEditMode === 'add' ? 'Password (optional — or send them a login link after saving)' : 'New password (leave blank to keep)'}</label>
              <input className="c-input" type="password" autoComplete="new-password" placeholder="Min. 8 characters" value={viewerForm.password} onChange={(e) => setViewerForm((p) => ({ ...p, password: e.target.value }))} />
            </div>
            <div className="c-form-group" style={{ marginBottom: 0 }}>
              <label className="c-label" style={{ fontSize: '0.78rem' }}>Confirm password</label>
              <input className="c-input" type="password" autoComplete="new-password" placeholder="Repeat password" value={viewerForm.confirm} onChange={(e) => setViewerForm((p) => ({ ...p, confirm: e.target.value }))} />
            </div>
            {viewerEditMode !== 'add' && (
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', gridColumn: '1 / -1' }}>
                <input type="checkbox" checked={viewerForm.is_active} onChange={(e) => setViewerForm((p) => ({ ...p, is_active: e.target.checked }))} />
                Active — unticked, they cannot sign in
              </label>
            )}
          </div>
        );
        const viewerButtons = (
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
            <button className="c-btn c-btn--primary c-btn--sm" onClick={saveViewer} disabled={viewerSaving}>{viewerSaving ? 'Saving…' : 'Save'}</button>
            <button className="c-btn c-btn--outline c-btn--sm" onClick={() => { setViewerEditMode(null); setViewerError(''); }} disabled={viewerSaving}>Cancel</button>
          </div>
        );
        const fmtLogin = (ts) => ts
          ? new Date(ts).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
          : 'Never';
        return (
          <div className="a-section" style={{ marginTop: '1.5rem' }}>
            <div className="a-section__header" style={{ cursor: 'pointer' }} onClick={() => setViewersOpen((v) => !v)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <span className="a-chevron" style={{ transform: viewersOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                <div>
                  <div className="a-section__title">Viewers</div>
                  <div className="a-section__desc">
                    Viewers sign in with their email and password and see the Results page only — placements, scores, and the songs to play or download. No tabs, nothing editable.
                    Add them, then press <strong>Send login link</strong> to email them a link to choose their own password.
                    Someone who already has a FOOM login (a judge or entrant) can be added by the same email: their existing account is given the results page, and a <strong>FOOM results</strong> button appears on their dashboard.
                  </div>
                </div>
              </div>
              <button
                className="c-btn c-btn--sm"
                style={{ background: '#1a1a2e', color: '#fff' }}
                onClick={(e) => { e.stopPropagation(); setViewersOpen(true); startAddViewer(); }}
              >+ Add Viewer</button>
            </div>

            {viewersOpen && (<>
              {viewerError && <div className="c-alert" style={{ marginBottom: '0.5rem' }}>{viewerError}</div>}
              {viewerNotice && (
                <div className="c-alert c-alert--success" style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                  <span>{viewerNotice}</span>
                  <button type="button" className="c-btn c-btn--sm c-btn--outline" onClick={() => setViewerNotice('')}>OK</button>
                </div>
              )}

              {viewerEditMode === 'add' && (
                <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '1rem', marginBottom: '0.75rem' }}>
                  {viewerFields}
                  {viewerButtons}
                </div>
              )}

              {viewers.length === 0 && viewerEditMode !== 'add' ? (
                <div className="c-empty">
                  <p className="c-empty__text">No viewers yet. Add one above to give someone read-only access to the results.</p>
                </div>
              ) : (
                <div className="a-table-wrap">
                  <table className="a-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Status</th>
                        <th>Last sign-in</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewers.map((v) => (
                        viewerEditMode?.id === v.id ? (
                          <tr key={v.id}>
                            <td colSpan={5} style={{ padding: '0.75rem', background: '#f9fafb' }}>
                              {viewerFields}
                              {viewerButtons}
                            </td>
                          </tr>
                        ) : (
                          <tr key={v.id}>
                            <td>
                              <strong style={!v.is_active ? { color: '#9ca3af' } : undefined}>{v.full_name}</strong>
                              {v.granted && (
                                <span className="badge" style={{ marginLeft: '0.4rem', background: '#e0e7ff', color: '#3730a3' }}
                                      title="Signs in with their own FOOM account; given the results page from here">
                                  also {v.is_judge ? 'judge' : v.is_contestant ? 'entrant' : v.role}
                                </span>
                              )}
                            </td>
                            <td style={{ color: '#6b7280', fontSize: '0.85rem' }}>{v.email}</td>
                            <td>
                              <span className={`badge ${v.is_active ? 'badge--active' : 'badge--withdrawn'}`}>
                                {v.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td style={{ color: '#6b7280', fontSize: '0.85rem' }}>{fmtLogin(v.last_login_at)}</td>
                            <td>
                              <div className="a-row-actions">
                                {/* Emails a 7-day set-your-password link. Blue, like
                                    the Notify buttons; grey while the account is
                                    inactive, since the server refuses those. */}
                                <button
                                  className="c-btn c-btn--sm"
                                  style={{ background: v.is_active ? '#0ea5e9' : '#9ca3af', cursor: v.is_active ? 'pointer' : 'default' }}
                                  disabled={viewerLinkBusy === v.id || !v.is_active}
                                  onClick={() => sendViewerLoginLink(v)}
                                  title={!v.is_active
                                    ? 'Inactive — tick Active in Edit first'
                                    : v.granted
                                      ? `Email ${v.email} a link to the results page — they sign in with their usual password`
                                      : (v.link_pending
                                        ? `Email ${v.email} a fresh link — the one they hold stops working`
                                        : `Email ${v.email} a link to set their password (valid 7 days)`)}
                                >
                                  {viewerLinkBusy === v.id ? '✉ Sending…'
                                    : v.granted ? '✉ Send results link'
                                    : v.link_pending ? '✉ Resend login link' : '✉ Send login link'}
                                </button>
                                {viewerLinkSent[v.id] ? (
                                  <span style={{ fontSize: '0.75rem', color: '#16a34a', alignSelf: 'center', whiteSpace: 'nowrap' }}>
                                    Sent {new Date(viewerLinkSent[v.id]).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                ) : v.link_pending && v.link_expires ? (
                                  <span style={{ fontSize: '0.75rem', color: '#6b7280', alignSelf: 'center', whiteSpace: 'nowrap' }}
                                        title="A login link has been emailed and not yet used">
                                    Link expires {new Date(v.link_expires).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
                                  </span>
                                ) : null}
                                <EmailEntrantControl user={{ user_id: v.id, full_name: v.full_name }} label="Email" noun="viewer" />
                                {!v.granted && <button className="c-btn c-btn--sm c-btn--outline" onClick={() => startEditViewer(v)}>Edit</button>}
                                <button className="c-btn c-btn--sm c-btn--danger" onClick={() => deleteViewer(v)}>{v.granted ? 'Remove access' : 'Delete'}</button>
                              </div>
                            </td>
                          </tr>
                        )
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>)}
          </div>
        );
      })()}

      {/* ── Notify Performers confirmation dialog ── */}
      {notifyHeatConfirm?.type === 'performers' && (() => {
        const heat = notifyHeatConfirm.heat;
        const heatLabel = heat.grand_final ? 'Grand Final' : `Heat ${heat.heat_number}${heat.heat_name ? ` — ${heat.heat_name}` : ''}`;
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: '#fff', borderRadius: '10px', padding: '1.75rem 2rem', maxWidth: '440px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
              <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1a1a2e', marginBottom: '0.75rem' }}>Notify Performers</div>
              <p style={{ margin: '0 0 1.5rem', lineHeight: 1.5, color: '#374151' }}>
                Send notification emails to the performers in <strong>{heatLabel}</strong> with their heat details and arrival time?
              </p>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button className="c-btn c-btn--outline" onClick={() => setNotifyHeatConfirm(null)}>Cancel</button>
                <button className="c-btn" style={{ background: '#0ea5e9' }} onClick={() => doNotifyPerformers(heat)}>Send Emails</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Notify single performer confirmation dialog ── */}
      {notifyHeatConfirm?.type === 'performer-single' && (() => {
        const { heat, song } = notifyHeatConfirm;
        const heatLabel = `Heat ${heat.heat_number}${heat.heat_name ? ` — ${heat.heat_name}` : ''}`;
        const alreadySent = songNotified[song.id];
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: '#fff', borderRadius: '10px', padding: '1.75rem 2rem', maxWidth: '470px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
              <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1a1a2e', marginBottom: '0.75rem' }}>Notify performer</div>
              <p style={{ margin: '0 0 1rem', lineHeight: 1.5, color: '#374151' }}>
                Email the performer of <strong>{song.title}</strong> their current details for <strong>{heatLabel}</strong>, with the running sheet attached?
              </p>
              <p style={{ margin: '0 0 1.5rem', fontSize: '0.85rem', lineHeight: 1.5, color: '#6b7280' }}>
                This issues them a new confirmation link and clears any confirmation they have already given for this heat, so they will need to confirm again.
                {alreadySent && (
                  <>
                    {' '}<strong style={{ color: '#b45309' }}>
                      You already notified them about this song ({alreadySent}) — sending again will stop that earlier link working.
                    </strong>
                  </>
                )}
              </p>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button className="c-btn c-btn--outline" onClick={() => setNotifyHeatConfirm(null)}>Cancel</button>
                <button className="c-btn" style={{ background: '#0ea5e9' }} onClick={() => doNotifyPerformer(heat, song)}>Send Email</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Notify MC confirmation dialog ── */}
      {notifyHeatConfirm?.type === 'mc' && (() => {
        const heat = notifyHeatConfirm.heat;
        const heatLabel = heat.grand_final ? 'Grand Final' : `Heat ${heat.heat_number}${heat.heat_name ? ` — ${heat.heat_name}` : ''}`;
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: '#fff', borderRadius: '10px', padding: '1.75rem 2rem', maxWidth: '440px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
              <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1a1a2e', marginBottom: '0.75rem' }}>Notify MC</div>
              <p style={{ margin: '0 0 1.5rem', lineHeight: 1.5, color: '#374151' }}>
                Send a notification email to <strong>{heat.mc_name}</strong> for <strong>{heatLabel}</strong> with venue details and arrival time?
              </p>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button className="c-btn c-btn--outline" onClick={() => setNotifyHeatConfirm(null)}>Cancel</button>
                <button className="c-btn" style={{ background: '#0ea5e9' }} onClick={() => doNotifyMc(heat)}>Send Email</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Notify Stage hand confirmation dialog (migration 046) ── */}
      {notifyHeatConfirm?.type === 'stage_hand' && (() => {
        const heat = notifyHeatConfirm.heat;
        const heatLabel = heat.grand_final ? 'Grand Final' : `Heat ${heat.heat_number}${heat.heat_name ? ` — ${heat.heat_name}` : ''}`;
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: '#fff', borderRadius: '10px', padding: '1.75rem 2rem', maxWidth: '440px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
              <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1a1a2e', marginBottom: '0.75rem' }}>Notify Stage hand</div>
              <p style={{ margin: '0 0 1.5rem', lineHeight: 1.5, color: '#374151' }}>
                Send a notification email to <strong>{heat.stage_hand_name}</strong> for <strong>{heatLabel}</strong> with venue details and arrival time, with the running sheet and technical requirements sheet attached?
              </p>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button className="c-btn c-btn--outline" onClick={() => setNotifyHeatConfirm(null)}>Cancel</button>
                <button className="c-btn" style={{ background: '#0ea5e9' }} onClick={() => doNotifySh(heat)}>Send Email</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ─── Audio URL helper ─────────────────────────────────────────────────────────
// Stored URLs are absolute (http://localhost:PORT/...) — extract just the pathname
// so they resolve correctly from any host in the browser.
function audioPath(url) {
  if (!url) return null;
  try { return new URL(url).pathname; } catch { return url; }
}

// ─── AudioControls ───────────────────────────────────────────────────────────
// The play button and its download button now live in
// components/AudioControls.js, shared with the judge dashboard and the legacy
// results page. playerKey defaults to songId; pass a distinct one when the
// same song is rendered twice on one page (Most celebrated and the results
// table below), or both <audio> elements share an id and toggleAudio drives
// the wrong one.

// ─── ResultsPanel ────────────────────────────────────────────────────────────
// Exported: the viewer page (/viewer/results) renders this same panel on its
// own, with no tab bar. Its data comes from GET /api/admin/results, which
// observer.js answers read-only for the viewer role, and the audio play and
// download links are the static /uploads paths, so nothing here needs an
// admin-only route.
export function ResultsPanel() {
  const [songs,     setSongs]     = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [expanded,  setExpanded]  = useState({});  // { songId: bool }
  const [playingId, setPlayingId] = useState(null); // songId of currently playing audio
  const [categoryTab, setCategoryTab] = useState(''); // '' = all categories
  const [placementSort, setPlacementSort] = useState(null); // null | 'asc' | 'desc'
  const [celebratedOpen, setCelebratedOpen] = usePersistentCollapse('results:celebrated');
  const [celebExpanded, setCelebExpanded] = useState({}); // { songId: bool }
  const [pdfBusy, setPdfBusy] = useState(false);
  // Year printed on the Most celebrated PDF: the competition's, not the calendar's.
  const [pdfYear, setPdfYear] = useState(new Date().getFullYear());
  useEffect(() => {
    let alive = true;
    loadCompetition().then((s) => { if (alive && s.ok && s.competition) setPdfYear(Number(s.competition.year)); });
    return () => { alive = false; };
  }, []);
  const [resultsOpen,    setResultsOpen]    = usePersistentCollapse('results:table');
  const [liveAwardsOpen, setLiveAwardsOpen] = usePersistentCollapse('results:live-awards');
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [refreshing,  setRefreshing]  = useState(false);

  /**
   * Judging is still in progress, so results and placements arrive while this
   * page is open. Reloads on an interval, on regaining focus, and on demand.
   *
   * `initial` gates the full-page spinner. A background refresh must NOT set
   * `loading` -- that unmounts the panels and would collapse expanded rows,
   * reset the category tab and lose the sort, mid-read. Same trap as the Heats
   * tab (see CLAUDE.md); here it only ever replaces the songs array, so state
   * keyed on song id survives.
   */
  const loadResults = useCallback(async ({ initial = false } = {}) => {
    if (initial) setLoading(true); else setRefreshing(true);
    try {
      const res = await api.get('/api/admin/results');
      setSongs(res.data.songs);
      setRefreshedAt(new Date());
      setError('');
    } catch {
      // Leave the last good data on screen rather than blanking the panel;
      // a failed poll should not destroy what the organiser is reading.
      if (initial) setError('Failed to load results.');
    } finally {
      if (initial) setLoading(false); else setRefreshing(false);
    }
  }, []);

  useEffect(() => { loadResults({ initial: true }); }, [loadResults]);

  // Poll while the tab is visible, and refresh immediately on return to it.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') loadResults();
    }, 60000);
    const onVisible = () => { if (document.visibilityState === 'visible') loadResults(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [loadResults]);

  function toggleAudio(songId, shouldPlay) {
    if (playingId && playingId !== songId) {
      const prev = document.getElementById(`audio-${playingId}`);
      if (prev) { prev.pause(); prev.currentTime = 0; }
    }
    const audio = document.getElementById(`audio-${songId}`);
    if (!audio) return;
    if (shouldPlay) { audio.play(); setPlayingId(songId); }
    else            { audio.pause(); audio.currentTime = 0; setPlayingId(null); }
  }

  if (loading) return <div className="c-loading"><div className="c-spinner" /></div>;
  if (error)   return <div className="c-alert">{error}</div>;
  if (!songs || songs.length === 0) {
    return (
      <div className="a-section">
        <div className="a-section__header">
          <div>
            <div className="a-section__title">Results</div>
            <div className="a-section__desc">Scores and rankings for all songs.</div>
          </div>
        </div>
        <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>No evaluated songs yet.</p>
      </div>
    );
  }

  const fmt = (v) => (v != null ? Number(v).toFixed(3) : '—');

  // Category tabs, matching Judges & Assignments.
  const uniqueCategories = [...new Set(songs.map((s) => s.category_name).filter(Boolean))].sort();
  const filteredSongs = categoryTab
    ? songs.filter((s) => s.category_name === categoryTab)
    : songs;

  /**
   * "Most celebrated": every song that has been given a placement, grouped by
   * FOOM category. A song counts if the organiser awarded one or if any judge
   * did. Within a category they are ordered best placement first, then by how
   * many judges agreed, then by score.
   *
   * Every category is listed even when it has no placements yet, so the four
   * sub-panels stay in a stable order rather than appearing as awards land.
   */
  // The live category is split in two for display: awards won at a regular heat
  // and awards won at the Grand Final. Both are the same song category (FOOM
  // Live) -- the split is by which heat gave the award, which is why it lives
  // here rather than in song_categories.
  const LIVE_HEATS = 'FOOM Live Heats';
  const LIVE_FINAL = 'FOOM Live Final';

  const isLiveCategory = (cat) => /^foom live$/i.test((cat || '').trim());
  const wonAtFinal     = (s) => (s.live_results || []).some((r) => r.grand_final);
  const wonAtHeat      = (s) => (s.live_results || []).some((r) => !r.grand_final);

  const placedIn = (cat) => songs
    .filter((s) => s.category_name === cat)
    .filter((s) => s.admin_placement_name || placementsFor(s).length > 0);

  const byPlacement = (a, b) => {
    const ra = placementRank(a);
    const rb = placementRank(b);
    const cmp = (ra.tier - rb.tier) || (ra.order - rb.order) || (rb.n - ra.n);
    if (cmp !== 0) return cmp;
    return (Number(b.avg_weighted_score) || 0) - (Number(a.avg_weighted_score) || 0);
  };

  const celebratedByCategory = uniqueCategories.flatMap((cat) => {
    if (!isLiveCategory(cat)) {
      return [{ category: cat, songs: placedIn(cat).sort(byPlacement) }];
    }
    const live = placedIn(cat);
    // Final first: it is the culmination of the competition, so it leads even
    // while empty.
    return [
      { category: LIVE_FINAL, songs: live.filter(wonAtFinal).sort(byPlacement) },
      // A song with only an organiser award and no heat result sits under Heats,
      // since the Final has not been run when those are entered.
      { category: LIVE_HEATS, songs: live.filter((s) => wonAtHeat(s) || !wonAtFinal(s)).sort(byPlacement) },
    ];
  });

  const celebratedTotal = celebratedByCategory.reduce((n, c) => n + c.songs.length, 0);

  /**
   * Live awards regrouped by the heat that gave them: Heat 1 … Heat 4, then the
   * Grand Final. Same rows as Most celebrated (live_results on each song); only
   * the grouping differs. Heats are ordered by the number in their name with
   * the Grand Final last, since live_results carries no heat date or number.
   */
  const liveAwardsByHeat = (() => {
    const heats = new Map();
    for (const song of songs) {
      for (const r of song.live_results || []) {
        if (!r?.heat_name) continue;
        const h = heats.get(r.heat_name)
          || { heat_name: r.heat_name, grand_final: Boolean(r.grand_final), bySong: new Map() };
        const entry = h.bySong.get(song.id) || { song, awards: [] };
        if (!entry.awards.some((a) => a.name === r.category_name)) {
          entry.awards.push({ name: r.category_name, order: r.display_order ?? 998 });
        }
        h.bySong.set(song.id, entry);
        heats.set(r.heat_name, h);
      }
    }
    const num = (name) => { const m = /(\d+)/.exec(name); return m ? Number(m[1]) : 0; };
    return [...heats.values()]
      .sort((a, b) =>
        (Number(a.grand_final) - Number(b.grand_final))
        || (num(a.heat_name) - num(b.heat_name))
        || a.heat_name.localeCompare(b.heat_name))
      .map((h) => ({
        heat_name: h.heat_name,
        grand_final: h.grand_final,
        songs: [...h.bySong.values()]
          .map((e) => ({ ...e, awards: [...e.awards].sort((a, b) => a.order - b.order) }))
          .sort((a, b) => (a.awards[0].order - b.awards[0].order) || a.song.title.localeCompare(b.song.title)),
      }));
  })();
  const liveAwardTotal = liveAwardsByHeat.reduce((n, h) => n + h.songs.reduce((m, e) => m + e.awards.length, 0), 0);

  // Default order is the server's (weighted average, best first). Sorting by
  // Placement re-orders a copy; songs with no placement always sit last, since
  // floating "no award" to the top is never what you want from this column.
  const visibleSongs = !placementSort
    ? filteredSongs
    : [...filteredSongs].sort((a, b) => {
        const ra = placementRank(a);
        const rb = placementRank(b);
        if (!ra && !rb) return 0;
        if (!ra) return 1;
        if (!rb) return -1;
        const cmp = (ra.tier - rb.tier) || (ra.order - rb.order) || (rb.n - ra.n);
        return placementSort === 'asc' ? cmp : -cmp;
      });

  /**
   * Placements awarded to a song, for the collapsed row. Read from the API's
   * designation_summary, which lists one entry per awarding judge and is
   * filtered server-side to submitted evaluations. The same placement can come
   * from several judges, so entries are collapsed and counted.
   * Sorted best-first by the placement category's display_order.
   */
  function placementsFor(song) {
    const counts = new Map();

    const add = (name, order, live) => {
      if (!name) return;
      const cur = counts.get(name) || { name, n: 0, order: order ?? 999, live: false, online: false };
      cur.n += 1;
      if (live) cur.live = true; else cur.online = true;
      counts.set(name, cur);
    };

    // Online judging: one entry per awarding judge.
    for (const d of song.designation_summary || []) add(d?.category_name, d?.display_order, false);

    // Live heats: FOOM Live songs are never judged online, so their awards come
    // from heat_song_results, mapped onto the same placement categories.
    for (const r of song.live_results || []) add(r?.category_name, r?.display_order, true);

    return [...counts.values()].sort((a, b) => a.order - b.order);
  }

  /** Tooltip text for a placement badge, naming where the award came from. */
  function placementTitle(pl) {
    if (pl.live && !pl.online) return `Awarded at a live heat${pl.n > 1 ? ` (${pl.n} heats)` : ''}`;
    if (pl.live && pl.online)  return `Awarded by judges and at a live heat (${pl.n} total)`;
    return `Awarded by ${pl.n} judge${pl.n === 1 ? '' : 's'}`;
  }

  /**
   * Rank for sorting the Placement column. Lower is better.
   * The organiser's award outranks any judge award; among judge awards the
   * category's display_order decides (Winner before Highly commended), and a
   * placement given by more judges outranks the same one given by fewer.
   * Songs with no placement sort last in either direction.
   */
  function placementRank(song) {
    if (song.admin_placement_name) {
      return { tier: 0, order: song.admin_placement_order ?? 999, n: 0 };
    }
    const best = placementsFor(song)[0];
    if (!best) return null;
    return { tier: 1, order: best.order, n: best.n };
  }

  return (
    <>
    {/* ── Most celebrated ── every song with a placement, grouped by category */}
    <div className="a-section" style={{ marginBottom: '1.5rem' }}>
      <div
        className="a-section__header"
        style={{ cursor: 'pointer' }}
        onClick={() => setCelebratedOpen((v) => !v)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <span className="a-chevron" style={{ transform: celebratedOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
          <div>
            <div className="a-section__title">Most celebrated</div>
            <div className="a-section__desc">
              {celebratedTotal} song{celebratedTotal !== 1 ? 's' : ''} awarded a placement, by category.
              {' '}Updates as judging comes in.
            </div>
          </div>
        </div>

        {/* stopPropagation: this sits inside the header, which toggles the panel */}
        <div
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          onClick={(e) => e.stopPropagation()}
        >
          {refreshedAt && (
            <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
              {refreshing ? 'Updating…' : `Updated ${refreshedAt.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`}
            </span>
          )}
          <button
            type="button"
            className="c-btn c-btn--outline c-btn--sm"
            onClick={() => downloadCelebratedPdf(celebratedByCategory, {
              placementsFor,
              year: pdfYear,
              onStart: () => setPdfBusy(true),
              onDone:  () => setPdfBusy(false),
            })}
            disabled={pdfBusy}
            title="Download this panel as a PDF"
          >
            {pdfBusy ? 'Preparing…' : 'PDF'}
          </button>
          <button
            type="button"
            className="c-btn c-btn--outline c-btn--sm"
            onClick={() => loadResults()}
            disabled={refreshing}
            title="Reload results now"
          >
            Refresh
          </button>
        </div>
      </div>

      {celebratedOpen && (
        celebratedTotal === 0 ? (
          <p style={{ color: '#6b7280', fontSize: '0.9rem', margin: '0.75rem 0 0' }}>
            No placements awarded yet.
          </p>
        ) : (
          <div className="a-celebrated">
            {celebratedByCategory.map(({ category, songs: winners }) => (
              <div key={category} className="a-celebrated__cat">
                <div className="a-celebrated__cat-title">
                  {category}
                  <span className="a-celebrated__count">{winners.length}</span>
                </div>

                {winners.length === 0 ? (
                  <p className="a-celebrated__empty">
                    {category === LIVE_FINAL
                      ? 'Awaiting the Grand Final — results will appear here once they are entered.'
                      : 'No placements yet.'}
                  </p>
                ) : (
                  <ul className="a-celebrated__list">
                    {winners.map((song) => {
                      // Comments only exist for online judging. FOOM Live songs
                      // are never judged in-app, so for those the expander shows
                      // which heat gave the award instead of an empty panel.
                      const comments = (song.evaluations || [])
                        .filter((ev) => ev.is_submitted && ev.comments && ev.comments.trim());
                      const liveRows = song.live_results || [];
                      const canExpand = comments.length > 0 || liveRows.length > 0;
                      const isOpen = Boolean(celebExpanded[song.id]);

                      return (
                        <li key={song.id} className="a-celebrated__item">
                          <div className="a-celebrated__row">
                            <AudioControls
                              songId={song.id}
                              playerKey={`c${song.id}`}
                              audioUrl={song.audio_url ? audioPath(song.audio_url) : null}
                              playingId={playingId}
                              onToggle={toggleAudio}
                            />

                            <span className="a-celebrated__title">{song.title}</span>
                            <span className="a-celebrated__by">{song.contestant_name}</span>

                            <span className="a-celebrated__badges">
                              {song.admin_placement_name && (
                                <span className="badge badge--winner" title="Awarded by the organiser">
                                  {song.admin_placement_name}
                                </span>
                              )}
                              {placementsFor(song).map((pl) => (
                                <span
                                  key={pl.name}
                                  className="a-celebrated__judge-badge"
                                  title={placementTitle(pl)}
                                >
                                  {pl.name}{pl.n > 1 ? ` ×${pl.n}` : ''}
                                </span>
                              ))}
                            </span>

                            {canExpand ? (
                              <button
                                type="button"
                                className="a-celebrated__expand"
                                onClick={() => setCelebExpanded((prev) => ({ ...prev, [song.id]: !prev[song.id] }))}
                                title={isOpen ? 'Hide judge comments' : 'Show judge comments'}
                                aria-expanded={isOpen}
                              >
                                {isOpen ? '▲' : '▼'}
                              </button>
                            ) : (
                              <span className="a-celebrated__expand a-celebrated__expand--none" />
                            )}
                          </div>

                          {isOpen && (
                            <div className="a-celebrated__detail">
                              {comments.map((ev) => (
                                <div key={ev.evaluation_id} className="a-celebrated__comment">
                                  <div className="a-celebrated__judge">
                                    {ev.judge_name}
                                    {ev.placement_category_name && (
                                      <span className="a-celebrated__judge-badge" style={{ marginLeft: '0.4rem' }}>
                                        {ev.placement_category_name}
                                      </span>
                                    )}
                                  </div>
                                  <div className="a-celebrated__comment-text">{ev.comments}</div>
                                </div>
                              ))}

                              {liveRows.map((r, i2) => (
                                <div key={`live-${i2}`} className="a-celebrated__comment">
                                  <div className="a-celebrated__judge">
                                    {r.heat_name}
                                    <span className="a-celebrated__judge-badge" style={{ marginLeft: '0.4rem' }}>
                                      {r.category_name}
                                    </span>
                                  </div>
                                  <div className="a-celebrated__comment-text">
                                    Awarded at the live heat — live entries are judged on paper, so there are no written comments.
                                  </div>
                                </div>
                              ))}

                              {comments.length === 0 && liveRows.length === 0 && (
                                <div className="a-celebrated__comment-text">No comments recorded.</div>
                              )}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )
      )}
    </div>

    {/* ── Live heat awards ── each heat and the Grand Final, with the songs it
        awarded. Same data as Most celebrated (live_results on each song), but
        grouped by the heat that gave the award rather than by category, so a
        reader can see what happened on each night. Added 15 Sep 2026 for the
        viewer results page; the admin Results tab shares the panel. */}
    <div className="a-section">
      <div
        className="a-section__header"
        style={{ cursor: 'pointer' }}
        onClick={() => setLiveAwardsOpen((v) => !v)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <span className="a-chevron" style={{ transform: liveAwardsOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
          <div>
            <div className="a-section__title">Live heat awards</div>
            <div className="a-section__desc">
              {liveAwardTotal} award{liveAwardTotal !== 1 ? 's' : ''} across {liveAwardsByHeat.length} live event{liveAwardsByHeat.length !== 1 ? 's' : ''} — Judges' Choice and People's Choice, heat by heat.
            </div>
          </div>
        </div>
      </div>

      {liveAwardsOpen && (
        liveAwardsByHeat.length === 0 ? (
          <p style={{ color: '#6b7280', fontSize: '0.9rem', margin: '0.75rem 0 0' }}>
            No live heat awards recorded yet.
          </p>
        ) : (
          <div className="a-celebrated">
            {liveAwardsByHeat.map((heat, hi) => (
              <div key={heat.heat_name} className="a-celebrated__cat">
                <div className="a-celebrated__cat-title">
                  {heat.heat_name}
                  <span className="a-celebrated__count">{heat.songs.length}</span>
                </div>
                <ul className="a-celebrated__list">
                  {heat.songs.map(({ song, awards }) => (
                    <li key={song.id} className="a-celebrated__item">
                      <div className="a-celebrated__row">
                        <AudioControls
                          songId={song.id}
                          playerKey={`h${hi}-${song.id}`}
                          audioUrl={song.audio_url ? audioPath(song.audio_url) : null}
                          playingId={playingId}
                          onToggle={toggleAudio}
                        />
                        <span className="a-celebrated__title">{song.title}</span>
                        <span className="a-celebrated__by">{song.contestant_name}</span>
                        <span className="a-celebrated__badges">
                          {awards.map((a) => (
                            <span
                              key={a.name}
                              className="a-celebrated__judge-badge"
                              title={`${a.name} — awarded at ${heat.heat_name}`}
                            >
                              {a.name}
                            </span>
                          ))}
                        </span>
                        <span className="a-celebrated__expand a-celebrated__expand--none" />
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )
      )}
    </div>

    {/* ── Results ── the full scored table */}
    <div className="a-section">
      <div
        className="a-section__header"
        style={{ cursor: 'pointer' }}
        onClick={() => setResultsOpen((v) => !v)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <span className="a-chevron" style={{ transform: resultsOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
          <div>
            <div className="a-section__title">Results</div>
            <div className="a-section__desc">
              {songs.length} song{songs.length !== 1 ? 's' : ''} — ranked by weighted average score. Click a row to expand judge details.
            </div>
          </div>
        </div>
      </div>

      {resultsOpen && (<>

      {/* Category tabs — same control as Judges & Assignments */}
      {uniqueCategories.length > 1 && (
        <div className="a-cat-tabs">
          <button
            className={`a-cat-tab${categoryTab === '' ? ' a-cat-tab--active' : ''}`}
            onClick={() => setCategoryTab('')}
          >
            All ({songs.length})
          </button>
          {uniqueCategories.map((cat) => (
            <button
              key={cat}
              className={`a-cat-tab${categoryTab === cat ? ' a-cat-tab--active' : ''}`}
              onClick={() => setCategoryTab(cat)}
            >
              {cat} ({songs.filter((s) => s.category_name === cat).length})
            </button>
          ))}
        </div>
      )}

      <div className="a-table-wrap">
        <table className="a-table">
          <thead>
            <tr>
              <th style={{ width: '2rem' }}>#</th>
              <th>Title</th>
              <th>Category</th>
              <th>Contestant</th>
              <th>Avg Score</th>
              <th>Evaluations</th>
              <th>
                <button
                  type="button"
                  className="a-sort-btn"
                  onClick={() => setPlacementSort((d) => (d === 'asc' ? 'desc' : d === 'desc' ? null : 'asc'))}
                  title="Sort by placement — click again to reverse, a third time to clear"
                >
                  Placement{placementSort === 'asc' ? ' ▲' : placementSort === 'desc' ? ' ▼' : ''}
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleSongs.map((song, idx) => (
              <React.Fragment key={song.id}>
                <tr
                  style={{ cursor: song.evaluations && song.evaluations.length > 0 ? 'pointer' : 'default' }}
                  onClick={() => {
                    if (song.evaluations && song.evaluations.length > 0)
                      setExpanded((p) => ({ ...p, [song.id]: !p[song.id] }));
                  }}
                >
                  <td style={{ color: '#6b7280', fontWeight: 600 }}>{idx + 1}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <AudioControls
                        songId={song.id}
                        audioUrl={song.audio_url ? audioPath(song.audio_url) : null}
                        playingId={playingId}
                        onToggle={toggleAudio}
                      />
                      <div>
                        <strong>{song.title}</strong>
                        {song.artist && (
                          <span style={{ display: 'block', color: '#6b7280', fontSize: '0.8rem' }}>{song.artist}</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>{song.category_name}</td>
                  <td>{song.contestant_name}</td>
                  <td style={{ fontWeight: 700, color: song.avg_weighted_score != null ? '#1a1a2e' : '#9ca3af' }}>
                    {fmt(song.avg_weighted_score)}
                  </td>
                  <td>
                    <span style={{ color: Number(song.submitted_eval_count) === Number(song.total_assignments) && Number(song.total_assignments) > 0 ? '#16a34a' : '#6b7280' }}>
                      {song.submitted_eval_count}/{song.total_assignments}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flexWrap: 'wrap' }}>
                      {/* Admin-awarded placement — the official one, so it leads. */}
                      {song.admin_placement_name && (
                        <span
                          className="badge badge--winner"
                          title="Awarded by the organiser"
                        >
                          {song.admin_placement_name}
                        </span>
                      )}

                      {/* Judge-awarded placements, previously only visible once
                          the row was expanded. Count shown when more than one
                          judge gave the same award. */}
                      {placementsFor(song).map((pl) => (
                        <span
                          key={pl.name}
                          style={{
                            background: '#e0f2fe', color: '#0369a1',
                            borderRadius: '4px', padding: '1px 6px',
                            fontSize: '0.72rem', whiteSpace: 'nowrap',
                          }}
                          title={placementTitle(pl)}
                        >
                          {pl.name}{pl.n > 1 ? ` ×${pl.n}` : ''}
                        </span>
                      ))}

                      {!song.admin_placement_name && placementsFor(song).length === 0 && (
                        <span style={{ color: '#9ca3af' }}>—</span>
                      )}

                      {song.evaluations && song.evaluations.length > 0 && (
                        <span style={{ marginLeft: '0.15rem', color: '#9ca3af', fontSize: '0.75rem' }}>
                          {expanded[song.id] ? '▲' : '▼'}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
                {expanded[song.id] && song.evaluations && (
                  <tr key={`${song.id}-exp`}>
                    <td colSpan={7} style={{ padding: 0, background: '#f9fafb' }}>
                      <div style={{ padding: '1rem 1.5rem' }}>
                        <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.75rem' }}>
                          Judge Evaluations
                        </div>
                        {song.evaluations.map((ev) => (
                          <div key={ev.evaluation_id} className="a-eval-row">
                            <div className="a-eval-row__judge">
                              {ev.judge_name}
                              <div style={{ display: 'flex', gap: '0.3rem', marginTop: '0.2rem', flexWrap: 'wrap' }}>
                                {ev.placement_category_name && (
                                  <span style={{ background: '#e0f2fe', color: '#0369a1', borderRadius: '4px', padding: '1px 6px', fontSize: '0.72rem' }}>
                                    {ev.placement_category_name}
                                  </span>
                                )}
                                {!ev.is_submitted && <span className="badge badge--pending">Draft</span>}
                              </div>
                            </div>
                            <div className="a-eval-row__scores">
                              {ev.scores && ev.scores.length > 0 ? (
                                ev.scores.map((sc) => (
                                  <span key={sc.criterion_id} className="a-score-chip">
                                    <span className="a-score-chip__name">{sc.criterion_name}</span>
                                    <span className="a-score-chip__value">{sc.score}</span>
                                  </span>
                                ))
                              ) : (
                                <span style={{ color: '#9ca3af', fontSize: '0.82rem' }}>No scores recorded</span>
                              )}
                            </div>
                            {ev.comments && (
                              <p className="a-eval-row__comments">"{ev.comments}"</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      </>)}
    </div>
    </>
  );
}

// ─── AdminEmailPanel ─────────────────────────────────────────────────────────
// Three sections:
//   1. Admin Accounts           — read-only table. Viewer accounts (role='observer',
//                                 shown as "Viewer") used to be added here too; they
//                                 moved to the Viewers panel on the Live Heats tab
//                                 (under Stage hands) on 10 Sep 2026, where they can
//                                 also be edited, deleted and emailed.
//   2. Sender Emails            — FROM address registry (GET/POST/PUT/DELETE /api/admin/sender-emails)
//   3. Email CC List            — foom_managers (GET/POST/DELETE /api/admin/foom-managers)
function AdminEmailPanel() {
  const [loadError, setLoadError] = useState('');

  // Section 1 — admin users (the same endpoint also returns viewers; filtered below)
  const [adminUsers,     setAdminUsers]     = useState(null);

  // Section 2 — sender emails
  const [senderEmails,   setSenderEmails]   = useState(null);
  const [newEmail,       setNewEmail]       = useState('');
  const [newLabel,       setNewLabel]       = useState('');
  const [senderAdding,   setSenderAdding]   = useState(false);
  const [senderAddErr,   setSenderAddErr]   = useState('');
  const [activating,     setActivating]     = useState(null);
  const [senderDeleting, setSenderDeleting] = useState(null);
  const [senderDelErr,   setSenderDelErr]   = useState('');

  // Section 3 — CC list (foom_managers)
  const [managers,   setManagers]   = useState(null);
  const [newCcEmail, setNewCcEmail] = useState('');
  const [newCcName,  setNewCcName]  = useState('');
  const [ccAdding,   setCcAdding]   = useState(false);
  const [ccAddErr,   setCcAddErr]   = useState('');
  const [ccDeleting, setCcDeleting] = useState(null);
  const [ccDelErr,   setCcDelErr]   = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/api/admin/admin-users'),
      api.get('/api/admin/sender-emails'),
      api.get('/api/admin/foom-managers'),
    ])
      .then(([usersRes, senderRes, managersRes]) => {
        setAdminUsers(usersRes.data.users || []);
        setSenderEmails(senderRes.data.sender_emails || []);
        setManagers(managersRes.data.managers || []);
      })
      .catch(() => setLoadError('Failed to load admin email settings.'));
  }, []);

  // ── Section 2 handlers ────────────────────────────────────────────

  async function handleAddSender(e) {
    e.preventDefault();
    setSenderAddErr('');
    const trimEmail = newEmail.trim().toLowerCase();
    const trimLabel = newLabel.trim();
    if (!trimEmail) { setSenderAddErr('Email is required.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimEmail)) {
      setSenderAddErr('Invalid email address.');
      return;
    }
    setSenderAdding(true);
    try {
      const res = await api.post('/api/admin/sender-emails', { email: trimEmail, label: trimLabel || null });
      setSenderEmails((prev) => [...prev, { ...res.data.sender_email, is_active: false }]);
      setNewEmail('');
      setNewLabel('');
    } catch (err) {
      setSenderAddErr(err.response?.data?.error || 'Failed to add sender email.');
    } finally {
      setSenderAdding(false);
    }
  }

  async function handleActivateSender(id) {
    setActivating(id);
    setSenderDelErr('');
    try {
      const res = await api.put(`/api/admin/sender-emails/${id}/activate`);
      const newActive = res.data.active_from_email;
      setSenderEmails((prev) => prev.map((s) => ({ ...s, is_active: s.email === newActive })));
    } catch (err) {
      setSenderDelErr(err.response?.data?.error || 'Failed to activate sender email.');
    } finally {
      setActivating(null);
    }
  }

  async function handleDeleteSender(id) {
    setSenderDeleting(id);
    setSenderDelErr('');
    try {
      await api.delete(`/api/admin/sender-emails/${id}`);
      setSenderEmails((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      setSenderDelErr(err.response?.data?.error || 'Failed to delete sender email.');
    } finally {
      setSenderDeleting(null);
    }
  }

  // ── Section 3 handlers ────────────────────────────────────────────

  async function handleAddCc(e) {
    e.preventDefault();
    setCcAddErr('');
    const trimEmail = newCcEmail.trim().toLowerCase();
    const trimName  = newCcName.trim();
    if (!trimName)  { setCcAddErr('Name is required.'); return; }
    if (!trimEmail) { setCcAddErr('Email is required.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimEmail)) {
      setCcAddErr('Invalid email address.');
      return;
    }
    setCcAdding(true);
    try {
      const res = await api.post('/api/admin/foom-managers', { full_name: trimName, email: trimEmail });
      setManagers((prev) => [...prev, res.data.manager]);
      setNewCcEmail('');
      setNewCcName('');
    } catch (err) {
      setCcAddErr(err.response?.data?.error || 'Failed to add CC address.');
    } finally {
      setCcAdding(false);
    }
  }

  async function handleDeleteCc(id) {
    setCcDeleting(id);
    setCcDelErr('');
    try {
      await api.delete(`/api/admin/foom-managers/${id}`);
      setManagers((prev) => prev.filter((m) => m.id !== id));
    } catch (err) {
      setCcDelErr(err.response?.data?.error || 'Failed to remove CC address.');
    } finally {
      setCcDeleting(null);
    }
  }

  // ── Render ────────────────────────────────────────────────────────

  if (loadError) return <div className="c-alert" role="alert">{loadError}</div>;
  const loading = adminUsers === null || senderEmails === null || managers === null;

  return (
    <div>

      {/* ══ SECTION 1a — Admin Accounts ════════════════════════════════════════ */}
      <div className="a-section">
        <div className="a-section__header">
          <div>
            <div className="a-section__title">Admin Accounts</div>
            <div className="a-section__desc">
              Admin accounts are managed via the SophtPlanet platform dashboard.
            </div>
          </div>
        </div>

        {loading ? (
          <div className="c-loading" style={{ padding: '2rem' }}>
            <div className="c-spinner" aria-label="Loading…" />
          </div>
        ) : (() => {
          const admins = adminUsers.filter((u) => u.role === 'admin');
          return admins.length === 0 ? (
            <div className="c-empty" style={{ margin: '1rem 0' }}>No admin accounts.</div>
          ) : (
            <div className="a-table-wrap">
              <table className="a-table">
                <thead>
                  <tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {admins.map((u) => (
                    <tr key={u.id}>
                      <td>
                        <strong style={!u.is_active ? { color: '#9ca3af' } : undefined}>
                          {u.full_name}
                        </strong>
                      </td>
                      <td style={{ color: u.is_active ? '#6b7280' : '#9ca3af', fontSize: '0.85rem' }}>
                        {u.email}
                      </td>
                      <td><span className="badge badge--submitted">Admin</span></td>
                      <td>
                        <span className={`badge ${u.is_active ? 'badge--active' : 'badge--inactive'}`}>
                          {u.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })()}
      </div>

      {/* Viewer accounts are managed on the Live Heats tab (Viewers panel, under
          Stage hands) since 10 Sep 2026 — see the note at the top of this panel. */}

      {/* ══ SECTION 2 — Sender Emails ══════════════════════════════════════════ */}
      <div className="a-section">
        <div className="a-section__header">
          <div>
            <div className="a-section__title">Sender Emails</div>
            <div className="a-section__desc">
              The active sender email is used as the FROM address for all outgoing competition emails.
            </div>
          </div>
        </div>

        {loading ? (
          <div className="c-loading" style={{ padding: '2rem' }}>
            <div className="c-spinner" aria-label="Loading…" />
          </div>
        ) : (
          <>
            {senderEmails.length === 0 ? (
              <div className="c-empty" style={{ margin: '1rem 0' }}>No sender emails registered.</div>
            ) : (
              <div className="a-table-wrap" style={{ marginBottom: '1.25rem' }}>
                <table className="a-table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Label</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {senderEmails.map((s) => (
                      <tr key={s.id}>
                        <td><strong>{s.email}</strong></td>
                        <td style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                          {s.label || <span style={{ color: '#9ca3af' }}>—</span>}
                        </td>
                        <td>
                          {s.is_active ? (
                            <span className="badge badge--active">ACTIVE</span>
                          ) : (
                            <button
                              className="c-btn c-btn--sm c-btn--outline"
                              onClick={() => handleActivateSender(s.id)}
                              disabled={activating === s.id}
                            >
                              {activating === s.id ? 'Activating…' : 'Set as Active'}
                            </button>
                          )}
                        </td>
                        <td>
                          <div className="a-row-actions">
                            <button
                              className="c-btn c-btn--danger c-btn--sm"
                              onClick={() => handleDeleteSender(s.id)}
                              disabled={s.is_active || senderDeleting === s.id}
                              title={s.is_active ? 'Cannot delete the active sender email' : 'Delete'}
                            >
                              {senderDeleting === s.id ? 'Deleting…' : 'Delete'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {senderDelErr && (
              <div className="c-alert" role="alert" style={{ marginBottom: '1rem' }}>
                {senderDelErr}
              </div>
            )}

            {/* Add sender form */}
            <form onSubmit={handleAddSender} style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                <input
                  className={`c-input${senderAddErr ? ' is-error' : ''}`}
                  style={{ width: '240px' }}
                  type="email"
                  placeholder="email@domain.com"
                  value={newEmail}
                  onChange={(e) => { setNewEmail(e.target.value); setSenderAddErr(''); }}
                  disabled={senderAdding}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                <input
                  className="c-input"
                  style={{ width: '220px' }}
                  type="text"
                  placeholder="Label (e.g. SCALA Competition)"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  disabled={senderAdding}
                />
              </div>
              <button className="c-btn c-btn--sm" type="submit" disabled={senderAdding}>
                {senderAdding ? 'Adding…' : 'Add Sender Email'}
              </button>
            </form>
            {senderAddErr && (
              <p style={{ color: '#dc2626', fontSize: '0.78rem', margin: '0.3rem 0 0' }}>
                {senderAddErr}
              </p>
            )}

            <p style={{ fontSize: '0.78rem', color: '#6b7280', fontStyle: 'italic', margin: '0.75rem 0 0' }}>
              Each sender address must be verified in SMTP2GO before use.
              Currently verified: <strong>scala.org.au</strong> (sender domain)
            </p>
          </>
        )}
      </div>

      {/* ══ SECTION 3 — Email CC List ═══════════════════════════════════════════ */}
      <div className="a-section">
        <div className="a-section__header">
          <div>
            <div className="a-section__title">Email CC List</div>
            <div className="a-section__desc">
              These addresses are CC'd on ALL outgoing emails from the app — submission
              confirmations, judge notifications, heat emails, and all other system emails.
            </div>
          </div>
        </div>

        {loading ? (
          <div className="c-loading" style={{ padding: '2rem' }}>
            <div className="c-spinner" aria-label="Loading…" />
          </div>
        ) : (
          <>
            {managers.length === 0 ? (
              <div className="c-empty" style={{ margin: '1rem 0' }}>No CC addresses configured.</div>
            ) : (
              <div className="a-table-wrap" style={{ marginBottom: '1.25rem' }}>
                <table className="a-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Added</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {managers.map((m) => (
                      <tr key={m.id}>
                        <td><strong>{m.full_name}</strong></td>
                        <td style={{ color: '#6b7280', fontSize: '0.85rem' }}>{m.email}</td>
                        <td style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                          {new Date(m.created_at).toLocaleDateString('en-AU')}
                        </td>
                        <td>
                          <div className="a-row-actions">
                            <button
                              className="c-btn c-btn--danger c-btn--sm"
                              onClick={() => handleDeleteCc(m.id)}
                              disabled={ccDeleting === m.id}
                            >
                              {ccDeleting === m.id ? 'Removing…' : 'Remove'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {ccDelErr && (
              <div className="c-alert" role="alert" style={{ marginBottom: '1rem' }}>
                {ccDelErr}
              </div>
            )}

            {/* Add CC form */}
            <form onSubmit={handleAddCc} style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div>
                <input
                  className="c-input"
                  style={{ width: '180px' }}
                  type="text"
                  placeholder="Full name"
                  value={newCcName}
                  onChange={(e) => { setNewCcName(e.target.value); setCcAddErr(''); }}
                  disabled={ccAdding}
                />
              </div>
              <div>
                <input
                  className={`c-input${ccAddErr ? ' is-error' : ''}`}
                  style={{ width: '240px' }}
                  type="email"
                  placeholder="email@domain.com"
                  value={newCcEmail}
                  onChange={(e) => { setNewCcEmail(e.target.value); setCcAddErr(''); }}
                  disabled={ccAdding}
                />
              </div>
              <button className="c-btn c-btn--sm" type="submit" disabled={ccAdding}>
                {ccAdding ? 'Adding…' : 'Add CC Address'}
              </button>
            </form>
            {ccAddErr && (
              <p style={{ color: '#dc2626', fontSize: '0.78rem', margin: '0.3rem 0 0' }}>
                {ccAddErr}
              </p>
            )}

            <div
              className="c-alert c-alert--warning"
              style={{ marginTop: '1rem', fontSize: '0.82rem', display: 'flex', alignItems: 'flex-start', gap: '0.4rem' }}
            >
              <span>⚠</span>
              <span>Addresses added here receive a copy of <strong>every</strong> email sent by the system.</span>
            </div>
          </>
        )}
      </div>

    </div>
  );
}

// ─── AdminDashboard ───────────────────────────────────────────────────────────
const TABS = [
  { id: 'overview',        label: 'Overview' },
  { id: 'settings',        label: 'Settings' },
  { id: 'catcriteria',     label: 'Categories & Criteria' },
  { id: 'judges',          label: 'Judges & Assignments' },
  { id: 'liveheats',       label: 'Live Heats' },
  { id: 'results',         label: 'Results' },
  { id: 'users',           label: 'Users' },
  // "Admin Email" and "Emails" sat next to each other and told you nothing about
  // which was which. They are different things: one holds the addresses the
  // system sends FROM and copies TO, the other holds the messages themselves.
  // Named for their contents, and "Email" now matches the equivalent tab in the
  // Performer and MeMember apps. The ids are unchanged - they are referenced
  // elsewhere in this file and renaming them buys nothing.
  { id: 'adminEmail',      label: 'Senders & CC' },
  { id: 'emails',          label: 'Email' },
  { id: 'helptext',        label: 'Help Text' },
];

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('overview');

  return (
    <div className="c-page">
      <AdminHeader />

      <main className="c-main">
        <div className="a-topbar">
          <div className="a-tabs" role="tablist">
            {TABS.map((t) => (
              <button
                key={t.id}
                role="tab"
                aria-selected={activeTab === t.id}
                className={`a-tab${activeTab === t.id ? ' is-active' : ''}`}
                onClick={() => setActiveTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>

        </div>

        <div className="a-panel" key={activeTab}>
          {activeTab === 'overview'        && <OverviewPanel />}
          {activeTab === 'settings'        && <SettingsPanel />}
          {activeTab === 'catcriteria'     && <><CategoriesPanel /><CriteriaPanel /><InstrumentsPanel /><VenuesPanel /></>}
          {activeTab === 'judges'          && <JudgesPanel />}
          {activeTab === 'liveheats'       && <LiveHeatsPanel />}
          {activeTab === 'results'         && <ResultsPanel />}
          {activeTab === 'users'           && <UsersPanel />}
          {activeTab === 'adminEmail'      && <AdminEmailPanel />}
          {activeTab === 'emails'          && <EmailsPanel />}
          {activeTab === 'helptext'        && <ManageHelpText />}
        </div>
      </main>
    </div>
  );
}
